/**
 * Cases — OMS-Helpdesk-Tickets (Kanban- und Listenansicht).
 *
 * Features:
 * - Kanban-Board mit Drag & Drop zwischen Stages
 * - Listenansicht mit Sortierung und Filterung
 * - Case-Detail mit Nachrichten, Stage-/Priorität-/Assignee-Änderung
 * - AI-Zusammenfassung pro Case
 * - Mini-Dashboard mit offenen Cases
 *
 * @page /cases
 * @roles admin, manager
 * @i18n cases, common
 * @endpoints GET /api/cases, GET /api/cases/:id, GET /api/cases/stages, GET /api/cases/users, PUT /api/cases/:id/stage, PUT /api/cases/:id/priority, PUT /api/cases/:id/assign, POST /api/cases/:id/message, POST /api/cases/:id/summarize
 */
import { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Building, User, Users, Pause, Mail, Send, MessageSquare, Info, Trash2, Paperclip, FileText, X, Tag, FolderOpen, ChevronDown, ChevronLeft, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Code, Link2, Eye, AlertCircle, AlertTriangle, Clock, RefreshCw, Pin, Shield, ExternalLink, Phone, CheckSquare, Link as LinkIcon, Plus, PlusCircle, GitBranch, History, UserCheck, PencilLine } from 'lucide-react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TiptapUnderline from '@tiptap/extension-underline';
import TiptapImage from '@tiptap/extension-image';
import DOMPurify from 'dompurify';
import useCasesRefreshInterval from '../hooks/useCasesRefreshInterval';
import useTabParam from '../hooks/useTabParam';
import RichTextEditor from '../components/RichTextEditor';
import UniversalChatter from '../components/UniversalChatter';
import ConfirmDialog from '../components/ConfirmDialog';
import CaseZeiterfassungPanel from '../components/CaseZeiterfassungPanel';
import FlowApprovalBanner from '../components/FlowApprovalBanner';
import SearchableSelect from '../components/SearchableSelect';
import StatusAfterSendModal from '../components/cases/StatusAfterSendModal';
import ContactAutocomplete from '../components/ContactAutocomplete';
import DoneCategoryRequiredModal from '../components/cases/DoneCategoryRequiredModal';
import AISearchModal from '../components/cases/AISearchModal';
import ConvertCaseToClaimModal from '../components/cases/ConvertCaseToClaimModal';
import LinkedEntityList from '../components/LinkedEntityList';
import LinkEntityModal from '../components/LinkEntityModal';
import { formatCaseId, buildChipMatcher, isFilterImmuneChip, parseTicketNumberQuery } from '../utils/case-display';
import MailOriginalModal from '../components/MailOriginalModal';
import { extractCleanContent } from '../utils/mailRender';

const API = import.meta.env.VITE_API_URL || '';
const MODULE_COLOR = '#338edc';

function api(path, opts = {}) {
 const hasBody = opts.body != null;
 const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
 return fetch(`${API}${path}`, {
 ...opts,
 credentials: 'include',
 headers: { ...headers, ...opts.headers },
 body: hasBody ? JSON.stringify(opts.body) : undefined,
 }).then(async (r) => {
 const data = await r.json();
 if (!r.ok) throw new Error(data.error || data.message || `HTTP ${r.status}`);
 return data;
 });
}

function apiUpload(path, formData) {
 return fetch(`${API}${path}`, {
 method: 'POST',
 credentials: 'include',
 body: formData,
 }).then(async (r) => {
 const data = await r.json();
 if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
 return data;
 });
}

const PURIFY_CONFIG = { ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup', 'a', 'ul', 'ol', 'li', 'code', 'pre', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span', 'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img'], ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'class', 'title', 'style', 'data-cid'] };

// ─── Stage translation ───
const STAGE_KEY = {
 'response required': 'cases.stageResponseRequired',
 'waiting for support': 'cases.stageWaitingSupport',
 'in progress': 'cases.stageInProgress',
 'solution proposed': 'cases.stageSolutionProposed',
 'done': 'cases.stageDone',
 'closed': 'cases.stageDone',
};
function stageLabel(n, t) { return n ? (STAGE_KEY[n.toLowerCase()] ? t(STAGE_KEY[n.toLowerCase()]) : n) : ''; }

const STAGE_KEY_SHORT = {
 'response required': 'cases.stageShortResponse',
 'waiting for support': 'cases.stageShortWaiting',
 'in progress': 'cases.stageShortInProgress',
 'solution proposed': 'cases.stageShortSolution',
 'done': 'cases.stageShortDone',
 'closed': 'cases.stageShortDone',
};
function stageLabelShort(n, t) { return n ? (STAGE_KEY_SHORT[n.toLowerCase()] ? t(STAGE_KEY_SHORT[n.toLowerCase()]) : n) : ''; }

// ─── Helpers ───
function relTime(dateStr, t) {
 if (!dateStr) return '';
 const dt = dateStr.endsWith('Z') || dateStr.includes('+') ? new Date(dateStr) : new Date(dateStr + 'Z');
 const ms = Date.now() - dt.getTime();
 const m = Math.floor(ms / 60000);
 if (m < 1) return t('cases.justNow');
 if (m < 60) return t('cases.minutesAgo', { n: m });
 const h = Math.floor(m / 60);
 const remainMin = m % 60;
 if (h < 24) {
 if (remainMin > 0) return t('cases.hoursMinAgo', { h, m: remainMin });
 return t('cases.hoursAgo', { n: h });
 }
 const d = Math.floor(h / 24);
 if (d < 7) return d === 1 ? t('cases.dayAgo') : t('cases.daysAgo', { n: d });
 if (d < 30) {
 const w = Math.floor(d / 7);
 const rd = d % 7;
 if (rd > 0) return t('cases.weeksDaysAgo', { w, d: rd });
 return w === 1 ? t('cases.weekAgo') : t('cases.weeksAgo', { n: w });
 }
 const mo = Math.floor(d / 30);
 const rd2 = d % 30;
 if (mo < 12) {
 if (rd2 > 0) return t('cases.monthsDaysAgo', { mo, d: rd2 });
 return mo === 1 ? t('cases.monthAgo') : t('cases.monthsAgo', { n: mo });
 }
 return t('cases.yearsAgo', { n: Math.floor(mo / 12) });
}
function fmtDateTime(s) { return s ? new Date(s).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''; }

// ─── "Warte auf Kunde" stage detection ───
function isWarteAufKundeStage(stageName) {
 if (!stageName) return false;
 const lower = stageName.toLowerCase();
 return lower.includes('solution proposed') || lower.includes('loesung vorgeschlagen')
 || lower.includes('lösung vorgeschlagen') || lower.includes('warte auf kunde')
 || lower.includes('waiting for customer') || lower.includes('customer feedback');
}
function fmtDate(s) { return s ? new Date(s).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' }) : '–'; }
function fmtDayLabel(s) { return s ? new Date(s).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' }) : ''; }
function fmtTime(s) { return s ? new Date(s).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''; }
function hrsSince(s) { return s ? (Date.now() - new Date(s).getTime()) / 3600000 : Infinity; }

const TAG_COLORS = ['#6b7280','#ef4444','#f59e0b','#10b981','#338edc','#4D9CE0','#ec4899','#06b6d4','#f97316','#84cc16','#14b8a6'];
function tagCol(i) { return TAG_COLORS[i % TAG_COLORS.length]; }
function initials(n) { if (!n) return '?'; const p = n.split(/\s+/).filter(Boolean); return p.length >= 2 ? (p[0][0] + p[p.length-1][0]).toUpperCase() : n.substring(0,2).toUpperCase(); }

// ─── Kunden-Avatar ───
const AVATAR_COLORS = [
 { bg: 'bg-primary-100 dark:bg-primary-900/40', text: 'text-primary-600 dark:text-primary-400' },
 { bg: 'bg-cyan-100 dark:bg-cyan-900/40', text: 'text-cyan-600 dark:text-cyan-400' },
 { bg: 'bg-indigo-100 dark:bg-indigo-900/40', text: 'text-indigo-600 dark:text-indigo-400' },
 { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-600 dark:text-amber-400' },
 { bg: 'bg-rose-100 dark:bg-rose-900/40', text: 'text-rose-600 dark:text-rose-400' },
 { bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-600 dark:text-emerald-400' },
 { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-600 dark:text-blue-400' },
 { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-600 dark:text-orange-400' },
];
function avatarColor(name) {
 if (!name) return AVATAR_COLORS[0];
 let h = 0;
 for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
 return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
function KundenAvatar({ name }) {
 const c = avatarColor(name);
 return (
 <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm shrink-0 ${c.bg} ${c.text}`}>
 {initials(name)}
 </div>
 );
}

// ─── SLA Helpers ───
function slaTimeReadable(minutes) {
 if (minutes == null) return '';
 const abs = Math.round(Math.abs(minutes));
 if (abs >= 1440) {
 const d = Math.floor(abs / 1440);
 const h = Math.floor((abs % 1440) / 60);
 return h > 0 ? `${d} Tag${d > 1 ? 'e' : ''} ${h}h` : `${d} Tag${d > 1 ? 'e' : ''}`;
 }
 if (abs >= 60) {
 const h = Math.floor(abs / 60);
 const m = abs % 60;
 return m > 0 ? `${h}h ${m}min` : `${h}h`;
 }
 return `${abs}min`;
}

// SLA urgency class for remaining time text — uses backend escalation_level if available
function slaUrgency(verbleibendMin, slaStunden, escalationLevel) {
 if (escalationLevel === 'overdue') return 'overdue';
 if (escalationLevel === 'critical') return 'critical';
 if (escalationLevel === 'warning') return 'warning';
 if (verbleibendMin == null || !slaStunden) return '';
 if (verbleibendMin < 0) return 'overdue';
 const totalMin = slaStunden * 60;
 const pctRemaining = totalMin > 0 ? (verbleibendMin / totalMin) * 100 : 100;
 if (pctRemaining < 25) return 'critical';
 if (pctRemaining < 50) return 'warning';
 return 'ok';
}

// Escalation level label + dot color
function escalationDot(level) {
 if (level === 'overdue') return { color: 'text-red-500', dot: 'bg-red-500', pulse: true };
 if (level === 'critical') return { color: 'text-red-500', dot: 'bg-red-500', pulse: false };
 if (level === 'warning') return { color: 'text-amber-500', dot: 'bg-amber-500', pulse: false };
 return { color: 'text-green-500', dot: 'bg-green-500', pulse: false };
}

// Calculate remaining minutes from a deadline timestamp
function deadlineToMinutes(deadline) {
 if (!deadline) return null;
 const dl = new Date(deadline).getTime();
 return Math.round((dl - Date.now()) / 60000);
}

function slaBadgeCls(farbe) {
 if (farbe === 'orange') return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
 if (farbe === 'violet') return 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400';
 if (farbe === 'red') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
 if (farbe === 'green') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
 if (farbe === 'blue') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
 return 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] dark:bg-[var(--bg-inset,#F8F9FC)] dark:text-[var(--text-tertiary,#6B7280)]';
}

function slaProgressColor(prozent, pausiert) {
 if (pausiert) return 'bg-[var(--text-muted,#9CA3AF)] dark:bg-[var(--text-secondary,#4B5563)]';
 if (prozent >= 100) return 'bg-red-500';
 if (prozent > 50) return 'bg-amber-500';
 return 'bg-green-500';
}

function SlaKlasseBadge({ task }) {
 if (!task.sla_klasse) return null;
 return (
 <span className={`px-2 py-0.5 rounded text-[10px] font-semibold leading-none ${slaBadgeCls(task.sla_klasse_farbe)}`}>
 {task.sla_klasse_icon} {task.sla_klasse_label}
 </span>
 );
}

function SlaProgressBar({ task }) {
 if (!task.sla_stunden) return null;
 const pct = Math.min(task.sla_prozent || 0, 200);
 const isOverdue = pct >= 100;
 const width = isOverdue ? 100 : pct;
 return (
 <div className="w-full h-1.5 rounded-full bg-surface-inset overflow-hidden">
 <div className={`h-full rounded-full transition-all ${slaProgressColor(pct, task.sla_pausiert)}`}
 style={{ width: `${width}%` }} />
 </div>
 );
}

function SlaTimerLine({ task, t }) {
 if (!task.sla_stunden && !task.sla_next_response_deadline) return null;
 const isPaused = task.sla_pausiert || !!task.sla_paused_at;
 if (isPaused) {
 return (
 <div className="flex items-center gap-1 text-[10px] text-[var(--text-tertiary)]">
 <Pause className="w-3 h-3" />
 <span>{t('sla.paused')}</span>
 </div>
 );
 }
 const esc = task.sla_escalation_level || 'none';
 const dot = escalationDot(esc);
 const nextMin = deadlineToMinutes(task.sla_next_response_deadline);
 const verbl = nextMin != null ? nextMin : task.sla_verbleibend_min;
 const isOverdue = verbl != null && verbl < 0;
 const urgency = slaUrgency(verbl, task.sla_stunden, esc);
 const timeText = slaTimeReadable(verbl);
 const colorCls = urgency === 'overdue' || urgency === 'critical' ? 'text-red-400 font-bold'
 : urgency === 'warning' ? 'text-amber-500 font-semibold'
 : 'text-[var(--text-secondary)]';
 return (
 <div className="space-y-0.5">
 <div className={`flex items-center gap-1 text-[10px] ${colorCls}`}>
 <span className="relative flex h-2 w-2 shrink-0">
 {dot.pulse && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />}
 <span className={`relative inline-flex rounded-full h-2 w-2 ${dot.dot}`} />
 </span>
 {isOverdue ? `${timeText} ${t('sla.overdueShort')}` : `${timeText} ${t('sla.remainingShort')}`}
 </div>
 <SlaProgressBar task={task} />
 </div>
 );
}

// ─── #1062 SSoT: Effective SLA for display ───
// Manuel-Priorität: 1) First-Response gerissen ROT  2) First-Response läuft  3) Resolution/Antwort  4) OK
// Returns: { kind, isOverdue, isPaused, label, colorCls, dotCls, pulse, pillCls, verblMin }
function getEffectiveSla(task, t) {
  if (!task) return null;
  const hasAnyTimer = task.sla_stunden || task.sla_first_response_deadline || task.sla_next_response_deadline || task.sla_deadline;
  if (!hasAnyTimer) return null;

  const verhalten = task.sla_verhalten || 'laeuft';
  const isStopped = verhalten === 'gestoppt';
  const isPaused = verhalten === 'pausiert' || task.sla_pausiert || !!task.sla_paused_at;

  const frMet = task.sla_first_response_met;
  const frDeadline = task.sla_first_response_deadline;
  const frMin = deadlineToMinutes(frDeadline);
  const frCompleted = task.sla_first_response_at != null || frMet === true;
  const frOverdue = !frCompleted && (frMet === false || (frDeadline != null && frMin != null && frMin < 0));

  // Stopped (closed) — show on-time/late summary
  if (isStopped) {
    const onTime = frMet === true || (frMet == null && !task.sla_overdue);
    return {
      kind: 'stopped',
      isOverdue: !onTime,
      isPaused: false,
      verblMin: null,
      label: onTime ? t('sla.onTime') : t('sla.late'),
      colorCls: onTime ? 'text-emerald-500 font-semibold' : 'text-red-500 font-semibold',
      dotCls: onTime ? 'bg-emerald-500' : 'bg-red-500',
      pulse: false,
      pillCls: onTime
        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/20'
        : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/20',
    };
  }

  // Pausiert (Timer frozen) — gewinnt vor First-Response-Overdue, damit pausierte Cases nicht ROT schreien
  // Ausnahme: frMet === false (definitiver Breach via UI gemeldet) bleibt rot
  if (isPaused && frMet !== false) {
    return {
      kind: 'paused',
      isOverdue: false,
      isPaused: true,
      verblMin: null,
      label: t('sla.paused'),
      colorCls: 'text-[var(--text-tertiary)]',
      dotCls: 'bg-amber-500',
      pulse: false,
      pillCls: 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/20',
    };
  }

  // Priority 1: First-Response GERISSEN — ROT prominent
  if (frOverdue) {
    return {
      kind: 'first_response_overdue',
      isOverdue: true,
      isPaused: false,
      verblMin: frMin,
      label: t('sla.firstResponseOverdue', { time: slaTimeReadable(Math.abs(frMin)) }),
      colorCls: 'text-red-500 font-bold',
      dotCls: 'bg-red-500',
      pulse: true,
      pillCls: 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/20',
    };
  }

  // Priority 2: First-Response noch offen + läuft (grün/gelb je Urgency)
  if (!frCompleted && frDeadline != null && frMin != null && frMin >= 0) {
    const urg = slaUrgency(frMin, task.sla_stunden, task.sla_escalation_level);
    const colorCls = urg === 'critical' ? 'text-red-500 font-bold'
      : urg === 'warning' ? 'text-amber-500 font-semibold'
      : 'text-emerald-500';
    const pillCls = urg === 'critical'
      ? 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/20'
      : urg === 'warning'
      ? 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/20'
      : 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/20';
    const dotCls = urg === 'critical' ? 'bg-red-500' : urg === 'warning' ? 'bg-amber-500' : 'bg-emerald-500';
    return {
      kind: 'first_response_running',
      isOverdue: false,
      isPaused: false,
      verblMin: frMin,
      label: t('sla.firstResponseRemaining', { time: slaTimeReadable(frMin) }),
      colorCls, dotCls, pulse: false, pillCls,
    };
  }

  // Priority 3: First-Response erfüllt → Resolution/Next-Response aktiv
  const respDeadline = task.sla_next_response_deadline;
  const nextMin = deadlineToMinutes(respDeadline);
  const verbl = nextMin != null ? nextMin : task.sla_verbleibend_min;

  if (verbl != null) {
    const isOver = verbl < 0;
    if (isOver) {
      return {
        kind: 'response_overdue',
        isOverdue: true,
        isPaused: false,
        verblMin: verbl,
        label: t('sla.responseOverdue', { time: slaTimeReadable(Math.abs(verbl)) }),
        colorCls: 'text-red-500 font-bold',
        dotCls: 'bg-red-500',
        pulse: true,
        pillCls: 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/20',
      };
    }
    const urg = slaUrgency(verbl, task.sla_stunden, task.sla_escalation_level);
    const colorCls = urg === 'critical' ? 'text-red-500 font-bold'
      : urg === 'warning' ? 'text-amber-500 font-semibold'
      : 'text-emerald-500';
    const pillCls = urg === 'critical'
      ? 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/20'
      : urg === 'warning'
      ? 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/20'
      : 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/20';
    const dotCls = urg === 'critical' ? 'bg-red-500' : urg === 'warning' ? 'bg-amber-500' : 'bg-emerald-500';
    return {
      kind: 'response_running',
      isOverdue: false,
      isPaused: false,
      verblMin: verbl,
      label: t('sla.responseRemaining', { time: slaTimeReadable(verbl) }),
      colorCls, dotCls, pulse: false, pillCls,
    };
  }

  // Priority 4: Alles erfüllt
  return {
    kind: 'all_met',
    isOverdue: false,
    isPaused: false,
    verblMin: null,
    label: t('sla.ok'),
    colorCls: 'text-emerald-500',
    dotCls: 'bg-emerald-500',
    pulse: false,
    pillCls: 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/20',
  };
}

function stripHtml(html) {
 if (!html) return '';
 return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
 .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/\s+/g, ' ').trim();
}

// #1247 — REFRESH_OPTS_BASE entfernt; Intervall jetzt in /cases/einstellungen (useCasesRefreshInterval-Hook).

// ─── Priority Stars ───
// Odoo priorities: '0'=normal (no stars), '1'=important (1 star), '2'=urgent (2 stars)
// Hotel-star logic: all stars from left up to the selected one are filled
function Stars({ value, onChange, sz = 'sm' }) {
 const cls = sz === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5';
 const v = Number(value) || 0;
 return (
 <div className="flex gap-0.5" onClick={e => e.stopPropagation()}>
 {[1,2].map(s => {
 const filled = v >= s;
 return (
 <button key={s} onClick={e => { e.stopPropagation(); onChange?.(v === s ? String(s - 1) : String(s)); }}
 className={`${cls} transition-colors ${filled ? 'text-yellow-400' : 'text-content-tertiary hover:text-yellow-300'}`}>
 <svg viewBox="0 0 20 20" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5">
 <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
 </svg>
 </button>
 );
 })}
 </div>
 );
}

function Toast({ message, type, onClose }) {
 useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
 if (!message) return null;
 return <div className={`fixed bottom-6 right-6 z-[100] px-4 py-3 rounded-card shadow-elevated text-sm font-medium ${type==='error'?'bg-red-500/90 text-white':'bg-green-500/90 text-white'}`}>{message}</div>;
}

function ColumnSkeleton() {
 return (
 <div className="flex-shrink-0 w-[300px] flex flex-col rounded-card border border-line bg-surface-card animate-pulse">
 <div className="px-4 py-3 border-b border-line"><div className="h-3 w-24 bg-surface-inset rounded" /></div>
 <div className="p-3 space-y-2">{[1,2,3].map(i => (<div key={i} className="bg-surface-inset rounded-card p-3 space-y-1.5"><div className="h-3 w-3/4 bg-surface-hover rounded" /><div className="h-2.5 w-1/2 bg-surface-hover rounded" /></div>))}</div>
 </div>
 );
}

// #1064: List-Form Skeleton während Loading (matched ListView shape)
function ListSkeleton() {
 return (
 <div className="bg-surface-card border border-line-card rounded-card overflow-hidden animate-pulse">
 <div className="border-b border-line bg-surface-card px-4 py-2.5 flex gap-4">
 <div className="h-3 w-24 bg-surface-inset rounded" />
 <div className="h-3 w-16 bg-surface-inset rounded" />
 <div className="h-3 w-20 bg-surface-inset rounded" />
 <div className="h-3 w-28 bg-surface-inset rounded" />
 <div className="h-3 w-20 bg-surface-inset rounded ml-auto" />
 </div>
 {[1,2,3,4,5,6,7,8].map(i => (
 <div key={i} className="border-b border-line/50 px-4 py-2.5 flex gap-4 items-center">
 <div className="h-3 w-3/12 bg-surface-inset rounded" />
 <div className="h-3 w-1/12 bg-surface-inset rounded" />
 <div className="h-3 w-2/12 bg-surface-inset rounded" />
 <div className="h-3 w-2/12 bg-surface-inset rounded" />
 <div className="h-3 w-1/12 bg-surface-inset rounded ml-auto" />
 </div>
 ))}
 </div>
 );
}

// ─── Stage color dot (Kanban) ───
function stageDotColor(stageName) {
 const n = (stageName || '').toLowerCase();
 if (n.includes('response required') || n.includes('antwort')) return '#ef4444';
 if (n.includes('waiting for support') || n.includes('warte auf support')) return '#f59e0b';
 if (n.includes('in progress') || n.includes('bearbeitung')) return '#3b82f6';
 if (n.includes('solution proposed') || n.includes('vorgeschlagen')) return '#338edc';
 if (n.includes('done') || n.includes('erledigt') || n.includes('closed')) return '#22c55e';
 return '#6b7280';
}
function StageDot({ stageKey, stages, responseRequiredStageId, doneStageId, t }) {
 const { getStageLabel } = useLanguage();
 const stage = stages.find(s => s.key === stageKey);
 return (
 <span className="inline-flex items-center gap-1.5">
 <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: stageDotColor(stage?.name) }} />
 <span className="text-xs font-medium text-content-secondary">{getStageLabel(stage?.name)}</span>
 </span>
 );
}

// ─── Stage tab dot color ───
function stageTabDotCls(stageName) {
 const n = (stageName || '').toLowerCase();
 if (n.includes('response required') || n.includes('antwort')) return 'bg-red-400';
 if (n.includes('warte auf support') || n.includes('waiting for support')) return 'bg-amber-400';
 if (n.includes('in progress') || n.includes('bearbeitung')) return 'bg-blue-400';
 if (n.includes('solution proposed') || n.includes('vorgeschlagen')) return 'bg-purple-400';
 if (n.includes('done') || n.includes('erledigt') || n.includes('closed')) return 'bg-green-400';
 return 'bg-[var(--text-muted,#9CA3AF)]';
}

// ─── Stage badge (List view) ───
function stageBadgeCls(stageName) {
 const n = (stageName || '').toLowerCase();
 if (n.includes('response required') || n.includes('antwort'))
 return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
 if (n.includes('warte auf support') || n.includes('waiting for support'))
 return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
 if (n.includes('in progress') || n.includes('bearbeitung'))
 return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
 if (n.includes('solution proposed') || n.includes('vorgeschlagen'))
 return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
 if (n.includes('done') || n.includes('erledigt') || n.includes('closed'))
 return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
 return 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-primary,#1A1A2E)] dark:bg-[var(--bg-inset,#F8F9FC)] dark:text-[var(--text-tertiary,#6B7280)]';
}
function StageBadge({ stageKey, stages, t }) {
 const { getStageLabel } = useLanguage();
 const stage = stages.find(s => s.key === stageKey);
 return (
 <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${stageBadgeCls(stage?.name)}`}>
 {getStageLabel(stage?.name)}
 </span>
 );
}

// #1132 — AI-Suggestion-Badge in Cases-Liste (case-stage-validator skill output)
// Sichtbar nur bei confidence >= 0.85 und category in {acknowledgment, new_question}.
// Hover-Tooltip: reasoning + Confidence% + relative analyzed_at.
function AiSuggestionBadge({ suggestion, t }) {
 if (!suggestion) return null;
 const conf = Number(suggestion.confidence) || 0;
 if (conf < 0.85) return null;
 const cat = suggestion.category;
 if (cat !== 'acknowledgment' && cat !== 'new_question') return null;

 const isGreen = cat === 'acknowledgment';
 const label = isGreen ? t('cases.aiBadge.canClose') : t('cases.aiBadge.needsReply');
 const icon = isGreen ? '✓' : '⚠';
 const cls = isGreen
   ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/50'
   : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800/50';
 const tooltip = [
   suggestion.reasoning || '',
   `${t('cases.aiBadge.tooltipConfidence')}: ${Math.round(conf * 100)}%`,
   `${t('cases.aiBadge.tooltipAnalyzed')}: ${relTime(suggestion.analyzed_at, t)}`,
 ].filter(Boolean).join('\n');

 return (
   <span
     title={tooltip}
     className={`flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${cls}`}
     onClick={e => e.stopPropagation()}
   >
     <span aria-hidden="true">{icon}</span>
     <span className="hidden md:inline">{label}</span>
   </span>
 );
}

// ════════════════════════════════════════════════════════════════
// TIPTAP EDITOR — Rich-text compose bar
// ════════════════════════════════════════════════════════════════
// #1676 BR-58 P2 — Inline-Bild-Gates: erlaubte Formate + Max-Größe (10 MB) für
// Paste/Drop in den Antwort-Editor. Abgelehnte Bilder → Klartext-Fehler statt stumm.
const INLINE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const INLINE_IMAGE_MAX_BYTES = 10 * 1024 * 1024;

// #1678 — allowInlineImages: Inline-Bild-Feature (Paste/Drag → <img>) ist am CASE-TYP
// gegated (nur E-Mail-Cases). Bei false: keine Image-Extension (img nicht im Schema),
// gepastete/gezogene Bilder gehen als NORMALE Anhänge in die Chips statt in den Fließtext
// — kein relatives img Richtung OMS/Odoo (#1677-Befund 2).
function TiptapEditor({ onSubmit, isInternal, placeholder, t, editorRefCb, allowInlineImages, onInlineImageUpload, onInlineImageError, onNonImageFiles, onDropConsumed }) {
 // #1131 Idee 4: Toolbar on-focus — fade-in bei Fokus, hidden wenn leer+unfokussiert
 const [isFocused, setIsFocused] = useState(false);
 const [hasContent, setHasContent] = useState(false);
 // #1676 — handlePaste/handleDrop-Closures entstehen VOR dem editor-Objekt → Ref
 const editorRefLocal = useRef(null);
 // #1679 — per Drag-Griff gezogene Höhe; null = Auto-Verhalten wie bisher (min 80 / max 400)
 const [dragHeight, setDragHeight] = useState(null);
 const contentWrapRef = useRef(null);

 // #1679 — vertikaler Resize per Maus: Höhe clampen (nicht kleiner als bisheriges Minimum,
 // nicht über ~70% Viewport). Doppelklick auf den Griff = zurück auf Auto.
 // #1680 — Richtung invertiert: Griff HOCHziehen = größer. Der Griff sitzt direkt über dem
 // Intern/Senden-Balken nahe am Viewport-Boden — nach unten sind nur wenige Pixel Maus-Weg,
 // dadurch war Vergrößern faktisch gekappt. Nach oben ist der volle Viewport frei.
 const startResizeDrag = (e) => {
 e.preventDefault();
 const startY = e.clientY;
 const startH = contentWrapRef.current ? contentWrapRef.current.getBoundingClientRect().height : 80;
 const maxH = Math.max(200, Math.round(window.innerHeight * 0.7));
 const onMove = (ev) => setDragHeight(Math.min(maxH, Math.max(80, Math.round(startH + (startY - ev.clientY)))));
 const onUp = () => {
 window.removeEventListener('mousemove', onMove);
 window.removeEventListener('mouseup', onUp);
 };
 window.addEventListener('mousemove', onMove);
 window.addEventListener('mouseup', onUp);
 };

 // #1682 — logische Anzeigegröße des Bildes bestimmen: HiDPI-Screenshots (Retina) haben
 // die doppelte intrinsische Pixelbreite → ohne width-Attribut erscheinen sie in Bubble
 // und beim Empfänger aufgeblasen. naturalWidth / devicePixelRatio = Originalgröße wie gesehen.
 const readLogicalWidth = (file) => new Promise((resolve) => {
 const url = URL.createObjectURL(file);
 const im = new window.Image();
 im.onload = () => {
 const dpr = window.devicePixelRatio || 1;
 URL.revokeObjectURL(url);
 resolve(Math.round(im.naturalWidth / dpr) || null);
 };
 im.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
 im.src = url;
 });

 // #1676 — gemeinsamer Handler für Paste + Drop: Bild-Dateien validieren, hochladen,
 // als <img src="/api/cases/attachments/<id>/inline"> in den Fließtext einfügen.
 const insertImageFiles = (editorInstance, files) => {
 for (const file of files) {
 if (!INLINE_IMAGE_TYPES.includes(file.type)) {
 onInlineImageError?.(t('cases.inlineImageBadType').replace('{name}', file.name || 'Bild'));
 continue;
 }
 if (file.size > INLINE_IMAGE_MAX_BYTES) {
 onInlineImageError?.(t('cases.inlineImageTooLarge').replace('{name}', file.name || 'Bild'));
 continue;
 }
 Promise.all([onInlineImageUpload?.(file), readLogicalWidth(file)]).then(([att, logicalWidth]) => {
 if (att?.id) {
 // #1682 — width-Attribut = Originalgröße (logische Pixel), Downscale-Schutz macht das CSS/Mail-Style
 editorInstance.chain().focus().insertContent({
 type: 'image',
 attrs: { src: `/api/cases/attachments/${att.id}/inline`, alt: att.original_name || file.name || '', width: logicalWidth },
 }).run();
 }
 }).catch(e => onInlineImageError?.(e?.message || t('common.error')));
 }
 };

 const editor = useEditor({
 extensions: [
 StarterKit.configure({ heading: false, codeBlock: false }),
 TiptapLink.configure({ openOnClick: false }),
 Placeholder.configure({ placeholder }),
 TiptapUnderline,
 // #1676 — Bilder im Fließtext (Paste/Drag), inline:true = im Absatzfluss
 // #1678 — nur bei E-Mail-Cases; ohne Extension existiert der img-Node nicht im Schema
 // #1682 — width-Attribut im Schema, damit die Originalgröße (logische Pixel) im HTML landet
 ...(allowInlineImages ? [TiptapImage.extend({
 addAttributes() {
 return { ...this.parent?.(), width: { default: null } };
 },
 }).configure({ inline: true, allowBase64: false })] : []),
 ],
 editorProps: {
 attributes: {
 // #1131 Idee 3: 3 Zeilen (~80px) Start, max 400px (vorher 120/300)
 class: `prose prose-sm max-w-none min-h-[80px] max-h-[400px] overflow-y-auto px-4 py-3 text-sm focus:outline-none text-content [&_img]:max-w-[320px] [&_img]:rounded-[8px] [&_img]:border [&_img]:border-line`,
 },
 handleKeyDown(view, event) {
 if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
 onSubmit?.();
 return true;
 }
 return false;
 },
 // #1676 — Clipboard-Screenshot/Bild-Paste abfangen (verschwand vorher stumm)
 handlePaste: (view, event) => {
 const files = Array.from(event.clipboardData?.files || []).filter(f => f.type.startsWith('image/'));
 if (!files.length) return false;
 event.preventDefault();
 // #1678 — Nicht-E-Mail-Case: Bild NICHT inline übernehmen, sondern als normaler Anhang
 if (!allowInlineImages) { onNonImageFiles?.(files); return true; }
 insertImageFiles(editorRefLocal.current, files);
 return true;
 },
 // #1676 — Drag&Drop von Bildern IN den Editor → inline; Nicht-Bilder → normaler Anhang-Upload
 handleDrop: (view, event) => {
 const all = Array.from(event.dataTransfer?.files || []);
 if (!all.length) return false;
 event.preventDefault();
 event.stopPropagation();
 onDropConsumed?.();
 // #1678 — Nicht-E-Mail-Case: ALLE Dateien (auch Bilder) als normale Anhänge
 const images = allowInlineImages ? all.filter(f => f.type.startsWith('image/')) : [];
 const others = allowInlineImages ? all.filter(f => !f.type.startsWith('image/')) : all;
 if (images.length) insertImageFiles(editorRefLocal.current, images);
 if (others.length) onNonImageFiles?.(others);
 return true;
 },
 },
 onFocus: () => setIsFocused(true),
 onBlur: () => setIsFocused(false),
 onUpdate: ({ editor }) => setHasContent(!editor.isEmpty),
 });

 // Expose editor to parent via callback
 useEffect(() => {
 editorRefLocal.current = editor; // #1676
 if (editor && editorRefCb) editorRefCb(editor);
 }, [editor, editorRefCb]);

 if (!editor) return null;

 const ToolBtn = ({ active, onClick, children, title }) => (
 <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title}
 className={`p-1 rounded transition-colors ${active ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400' : 'text-content-tertiary hover:text-content hover:bg-surface-hover'}`}>
 {children}
 </button>
 );

 const showToolbar = isFocused || hasContent;

 return (
 <div className={`border rounded-card transition-colors focus-within:ring-2 focus-within:ring-[#338edc]/10 ${isInternal ? 'bg-amber-500/5 border-amber-500/20 focus-within:border-amber-500/40' : 'bg-white dark:bg-surface-inset border-[var(--border-default,#E5E7EB)] dark:border-line focus-within:border-[#338edc]'}`}
 onKeyDown={e => e.stopPropagation()}>
 <div className={`flex items-center gap-0.5 px-2 overflow-hidden transition-all duration-150 ease-out ${showToolbar ? 'max-h-10 py-1 opacity-100 border-b border-line/50' : 'max-h-0 py-0 opacity-0 border-b-0'}`}>
 <ToolBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title={t('cases.bold')}><Bold className="w-3.5 h-3.5" /></ToolBtn>
 <ToolBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title={t('cases.italic')}><Italic className="w-3.5 h-3.5" /></ToolBtn>
 <ToolBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title={t('cases.underline')}><UnderlineIcon className="w-3.5 h-3.5" /></ToolBtn>
 <span className="w-px h-4 bg-line mx-1" />
 <ToolBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title={t('cases.bulletList')}><List className="w-3.5 h-3.5" /></ToolBtn>
 <ToolBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title={t('cases.orderedList')}><ListOrdered className="w-3.5 h-3.5" /></ToolBtn>
 <span className="w-px h-4 bg-line mx-1" />
 <ToolBtn active={editor.isActive('link')} onClick={() => {
 if (editor.isActive('link')) { editor.chain().focus().unsetLink().run(); return; }
 const url = window.prompt('URL');
 if (url) editor.chain().focus().setLink({ href: url }).run();
 }} title={t('cases.link')}><Link2 className="w-3.5 h-3.5" /></ToolBtn>
 <ToolBtn active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} title={t('cases.code')}><Code className="w-3.5 h-3.5" /></ToolBtn>
 </div>
 {/* #1679 — gezogene Höhe: Wrapper bekommt fixe Höhe, ProseMirror füllt + scrollt (max-h-Kappung aufgehoben) */}
 <div ref={contentWrapRef}
 className={dragHeight ? 'overflow-hidden [&_.ProseMirror]:h-full [&_.ProseMirror]:max-h-none' : ''}
 style={dragHeight ? { height: `${dragHeight}px` } : undefined}>
 <EditorContent editor={editor} className={dragHeight ? 'h-full' : ''} />
 </div>
 <div onMouseDown={startResizeDrag} onDoubleClick={() => setDragHeight(null)}
 title={t('cases.dragToResize')}
 className="flex items-center justify-center h-2.5 cursor-ns-resize select-none group/resize">
 <span className="w-8 h-1 rounded-full bg-[var(--border-default,#E5E7EB)] group-hover/resize:bg-[var(--text-muted,#9CA3AF)] transition-colors" />
 </div>
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// TAG PILLS + ADD DROPDOWN
// ════════════════════════════════════════════════════════════════

const CASE_TAG_COLORS = {
 gray: 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] dark:bg-[var(--bg-inset,#F8F9FC)] dark:text-[var(--text-tertiary,#6B7280)]',
 violet: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
 orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
 red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
 green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
 blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

function TagPill({ tag, onRemove }) {
 const cls = CASE_TAG_COLORS[tag.color] || CASE_TAG_COLORS.gray;
 return (
 <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>
 {tag.name}
 {onRemove && (
 <button onClick={e => { e.stopPropagation(); onRemove(); }} className="hover:opacity-70">
 <X className="w-2.5 h-2.5" />
 </button>
 )}
 </span>
 );
}

function TagAddDropdown({ allTags, assignedIds, onAdd, onCreate, t }) {
 const [open, setOpen] = useState(false);
 const [search, setSearch] = useState('');
 const ref = useRef(null);
 const inputRef = useRef(null);

 useEffect(() => {
 if (!open) return;
 const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
 document.addEventListener('mousedown', h);
 return () => document.removeEventListener('mousedown', h);
 }, [open]);

 useEffect(() => {
 if (open && inputRef.current) inputRef.current.focus();
 }, [open]);

 const available = allTags.filter(tg => !assignedIds.includes(tg.id));
 const filtered = search.trim()
 ? available.filter(tg => tg.name.toLowerCase().includes(search.toLowerCase()))
 : available;
 const exactMatch = search.trim() && allTags.some(tg => tg.name.toLowerCase() === search.trim().toLowerCase());

 return (
 <div className="relative inline-block" ref={ref}>
 <button onClick={() => setOpen(!open)}
 className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-dashed border-line text-content-tertiary hover:text-content hover:border-line-hover transition-colors">
 <Tag className="w-2.5 h-2.5" /> {t('cases.addTag')}
 </button>
 {open && (
 <div className="absolute z-50 mt-1 left-0 bg-surface-elevated border border-line rounded-lg shadow-sm min-w-[180px] max-w-[240px]">
 <div className="p-1.5">
 <input ref={inputRef} type="text" value={search} onChange={e => setSearch(e.target.value)}
 placeholder={t('cases.searchTags')}
 className="w-full px-2.5 py-1.5 text-xs bg-surface-inset border border-line rounded-lg text-content focus:outline-none focus:border-primary-500/50"
 onKeyDown={e => {
 if (e.key === 'Enter' && search.trim() && !exactMatch) {
 onCreate?.(search.trim());
 setSearch('');
 setOpen(false);
 }
 }} />
 </div>
 <div className="max-h-[160px] overflow-y-auto py-1">
 {filtered.map(tg => (
 <button key={tg.id} onClick={() => { onAdd(tg.id); setSearch(''); setOpen(false); }}
 className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-content hover:bg-surface-hover">
 <span className={`w-2 h-2 rounded-full flex-shrink-0 ${CASE_TAG_COLORS[tg.color]?.split(' ')[0] || 'bg-[var(--text-muted,#9CA3AF)]'}`} />
 {tg.name}
 </button>
 ))}
 {search.trim() && !exactMatch && (
 <button onClick={() => { onCreate?.(search.trim()); setSearch(''); setOpen(false); }}
 className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-primary-500 hover:bg-surface-hover font-medium">
 <Tag className="w-3 h-3" />
 {t('cases.createTag', { name: search.trim() })}
 </button>
 )}
 {filtered.length === 0 && !search.trim() && (
 <div className="px-3 py-2 text-xs text-content-tertiary">{t('cases.noTagsAvailable')}</div>
 )}
 </div>
 </div>
 )}
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// TEMPLATE DROPDOWN
// ════════════════════════════════════════════════════════════════
function TemplateDropdown({ caseId, onInsert, t }) {
 const [open, setOpen] = useState(false);
 const [templates, setTemplates] = useState([]);
 const [loaded, setLoaded] = useState(false);

 const loadTemplates = async () => {
 if (loaded) return;
 try {
 const res = await api('/api/cases/reply-templates');
 setTemplates(res.templates || []);
 } catch {}
 setLoaded(true);
 };

 const handleSelect = async (tpl) => {
 setOpen(false);
 try {
 const res = await api(`/api/cases/reply-templates/${tpl.id}/render?case_id=${caseId}`);
 if (res.html) onInsert(res.html);
 } catch { onInsert(tpl.content_html); }
 };

 if (!templates.length && loaded) return null;

 return (
 <div className="relative inline-block">
 <button onClick={() => { loadTemplates(); setOpen(!open); }}
 className="flex-shrink-0 p-2 rounded-lg text-content-tertiary hover:text-content hover:bg-surface-hover transition-colors"
 title={t('cases.insertTemplate')}>
 <FileText className="w-4 h-4" />
 </button>
 {open && templates.length > 0 && (
 <div className="absolute z-50 bottom-full mb-1 left-0 bg-surface-elevated border border-line rounded-lg shadow-sm py-1 min-w-[200px] max-h-[200px] overflow-y-auto">
 {templates.map(tpl => (
 <button key={tpl.id} onClick={() => handleSelect(tpl)}
 className="flex flex-col w-full px-3 py-2 text-left hover:bg-surface-hover">
 <span className="text-xs font-medium text-content">{tpl.title}</span>
 {tpl.category && <span className="text-[10px] text-content-tertiary">{tpl.category}</span>}
 </button>
 ))}
 </div>
 )}
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// AVATAR STACK — #1060 Live-Presence-Indikator (overlapping avatars)
// ════════════════════════════════════════════════════════════════
function AvatarStack({ users, t, currentUserId, max = 5, compact = false }) {
 if (!users || !users.length) return null;
 // #1078b: eigenen User nicht anzeigen — nur andere Zuseher sind relevant
 const others = currentUserId != null
   ? users.filter(u => Number(u.user_id) !== Number(currentUserId))
   : users;
 if (!others.length) return null;
 const visible = others.slice(0, max);
 const overflow = others.length - visible.length;
 const initials = (name) => {
 if (!name) return '?';
 const parts = String(name).trim().split(/\s+/);
 if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
 return parts[0].slice(0, 2).toUpperCase();
 };
 // Stable color per user_id (hash-based)
 const colorFor = (uid) => {
 const palette = ['#338edc', '#22D3EE', '#6366F1', '#F59E0B', '#10B981', '#EF4444', '#A855F7', '#EC4899'];
 const n = Math.abs(Number(uid) || 0);
 return palette[n % palette.length];
 };
 const tooltip = (u) => {
 const ago = Math.max(0, Math.floor((Date.now() - new Date(u.last_active).getTime()) / 1000));
 const agoStr = ago < 5 ? t('cases.presence.now') : `${ago}s`;
 return `${u.name}${u.user_id === currentUserId ? ' (' + t('cases.presence.you') + ')' : ''} — ${agoStr}`;
 };
 return (
 <div className="flex items-center gap-1" title={t('cases.presence.tooltip')}>
 <span className="relative flex h-2 w-2 mr-0.5">
 <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#10B981' }}></span>
 <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: '#10B981' }}></span>
 </span>
 <div className="flex -space-x-2">
 {visible.map((u) => (
 <div
 key={u.user_id}
 title={tooltip(u)}
 className="relative inline-flex items-center justify-center w-7 h-7 rounded-full ring-2 ring-white dark:ring-[var(--bg-card,#fff)] text-[10px] font-semibold text-white overflow-hidden"
 style={{ background: u.avatar_url ? 'transparent' : colorFor(u.user_id) }}
 >
 {u.avatar_url ? (
 <>
 <img
 src={u.avatar_url}
 alt={u.name}
 className="w-full h-full object-cover"
 onError={(e) => {
 e.currentTarget.style.display = 'none';
 if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
 }}
 />
 <span style={{ display: 'none', background: colorFor(u.user_id) }} className="absolute inset-0 items-center justify-center text-white">
 {initials(u.name)}
 </span>
 </>
 ) : (
 initials(u.name)
 )}
 </div>
 ))}
 {overflow > 0 && (
 <div
 title={others.slice(max).map(u => u.name).join(', ')}
 className="inline-flex items-center justify-center w-7 h-7 rounded-full ring-2 ring-white dark:ring-[var(--bg-card,#fff)] text-[10px] font-semibold"
 style={{ background: 'var(--bg-inset,#F3F4F6)', color: 'var(--text-secondary,#4B5563)' }}
 >
 +{overflow}
 </div>
 )}
 </div>
 {!compact && (
 <span className="text-[11px] ml-1.5" style={{ color: 'var(--text-tertiary)' }}>
 {others.length === 1 ? t('cases.presence.viewing1') : t('cases.presence.viewingN').replace('{n}', others.length)}
 </span>
 )}
 </div>
 );
}

// #1500 — Problemstellungs-Body isoliert + memoisiert: re-rendert NUR bei Inhalts-Änderung
// (nicht bei Tippen/Presence/Audit/SLA-Churn des Modals) → der dangerouslySetInnerHTML-Knoten
// wird nicht reconciled, die Browser-Textselektion bleibt stehen. user-select:text explizit.
const CaseDescriptionBody = memo(function CaseDescriptionBody({ html }) {
 return (
 <div className="select-text bg-[var(--bg-inset,#F8F9FC)] dark:bg-[rgba(255,255,255,0.03)] border border-[var(--border-table,#F3F4F6)] dark:border-[var(--border-table,#F3F4F6)] rounded-lg p-4 text-[13px] text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)] leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0 [&_a]:text-blue-500 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_img]:max-w-full [&_img]:h-auto [&_table]:w-full [&_table]:text-xs [&_td]:p-1 [&_th]:p-1 [&_th]:text-left [&_th]:font-semibold"
 style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
 dangerouslySetInnerHTML={{ __html: html }} />
 );
});

// #1837 — Nachrichten-Body isoliert + memoisiert; gleiche Begründung wie #1500, nur eine
// Ebene tiefer. React 19 entscheidet über `dangerouslySetInnerHTML` allein an der
// OBJEKT-Identität (react-dom `updateProperties`: `nextProp === lastProp`), nie am
// HTML-Text — das JSX-Literal `{__html: …}` ist bei jedem Render neu, also läuft
// `domElement.innerHTML = …` auch dann, wenn sich am Text nichts geändert hat. Der
// Teilbaum wird verworfen und neu gebaut: die Browser-Selektion verliert ihren
// Bezugspunkt und jedes <img> im Mail-Body wird erneut geladen. memo hält den Knoten
// aus der Reconciliation heraus, solange der HTML-Text derselbe ist. Deshalb kommt hier
// NUR Vergleichbares an (Strings), keine bei jedem Render neu gebauten Objekte.
const MessageBody = memo(function MessageBody({ html, className, borderLeft, borderRight }) {
  return (
    <div data-case-msg-body className={className}
      style={{ borderLeft, borderRight, userSelect: 'text', WebkitUserSelect: 'text' }}
      dangerouslySetInnerHTML={{ __html: html }} />
  );
});

// #1506 — CC-Empfänger der ersten eingehenden Mail als Pill im Case-Kopf.
// SSoT = m.cc (gleiche Quelle wie die Thread-Anzeige Cases.jsx). Bei 1 Adresse direkt,
// bei mehreren "CC: N Empfänger" mit Tooltip + aufklappbarer Adressliste. Kein CC → null.
function CcHeaderPill({ cc, t }) {
 const [open, setOpen] = useState(false);
 const ref = useRef(null);
 useEffect(() => {
 if (!open) return;
 const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
 document.addEventListener('mousedown', h);
 return () => document.removeEventListener('mousedown', h);
 }, [open]);
 if (!Array.isArray(cc) || cc.length === 0) return null;
 const single = cc.length === 1;
 const label = single
 ? `CC: ${cc[0]}`
 : `CC: ${t('cases.header.ccCount', { count: cc.length, defaultValue: `${cc.length} Empfänger` })}`;
 return (
 <span className="relative" ref={ref}>
 <button type="button" title={cc.join(', ')}
 onClick={() => { if (!single) setOpen(o => !o); }}
 className={`inline-flex items-center gap-1.5 text-xs text-[var(--text-secondary,#4B5563)] bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] px-2.5 py-1.5 rounded-lg dark:bg-[var(--bg-inset,#F8F9FC)] dark:border-[var(--border-default,#E5E7EB)] dark:text-[var(--text-tertiary,#6B7280)] ${single ? 'cursor-default' : 'cursor-pointer hover:border-line-hover'}`}>
 <Users size={13} className="text-[var(--text-tertiary,#6B7280)]" />
 <span className="truncate max-w-[240px]">{label}</span>
 </button>
 {open && !single && (
 <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] max-w-[340px] bg-surface-elevated border border-line rounded-lg shadow-sm p-2">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-content-tertiary mb-1 px-1">CC</div>
 {cc.map((a, i) => (
 <div key={i} className="text-xs text-content px-1 py-0.5 truncate" title={a}>{a}</div>
 ))}
 </div>
 )}
 </span>
 );
}

// #1754 — An-Empfänger (To) der ersten eingehenden Mail als Pill im Case-Kopf,
// spiegelbildlich zum CC-Pill (#1506). Quelle = m.to (= to_addresses mit Fallback,
// SSoT wie die Bubble #1752). NUR ab 2 Empfängern: bei genau 1 An (Normalfall nur help@)
// KEIN Pill, sonst klebt an jedem Case ein "An: 1".
function ToHeaderPill({ to, t }) {
 const [open, setOpen] = useState(false);
 const ref = useRef(null);
 useEffect(() => {
 if (!open) return;
 const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
 document.addEventListener('mousedown', h);
 return () => document.removeEventListener('mousedown', h);
 }, [open]);
 if (!Array.isArray(to) || to.length < 2) return null;
 const label = `${t('cases.to')}: ${t('cases.header.toCount', { count: to.length, defaultValue: `${to.length} Empfänger` })}`;
 return (
 <span className="relative" ref={ref}>
 <button type="button" title={to.join(', ')}
 onClick={() => setOpen(o => !o)}
 className="inline-flex items-center gap-1.5 text-xs text-[var(--text-secondary,#4B5563)] bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] px-2.5 py-1.5 rounded-lg dark:bg-[var(--bg-inset,#F8F9FC)] dark:border-[var(--border-default,#E5E7EB)] dark:text-[var(--text-tertiary,#6B7280)] cursor-pointer hover:border-line-hover">
 <Users size={13} className="text-[var(--text-tertiary,#6B7280)]" />
 <span className="truncate max-w-[240px]">{label}</span>
 </button>
 {open && (
 <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] max-w-[340px] bg-surface-elevated border border-line rounded-lg shadow-sm p-2">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-content-tertiary mb-1 px-1">{t('cases.to')}</div>
 {to.map((a, i) => (
 <div key={i} className="text-xs text-content px-1 py-0.5 truncate" title={a}>{a}</div>
 ))}
 </div>
 )}
 </span>
 );
}

// ════════════════════════════════════════════════════════════════
// DETAIL MODAL – Single-page helpdesk layout (no tabs)
// ════════════════════════════════════════════════════════════════
function DetailModal({ open, onClose, taskId, srcHint, stages, omsUsers, onRefresh, onDraftChange, allTags, allCategories, onTagsChange, watchedIds, toggleWatch, t, fullPage }) {
 const detailNavigate = useNavigate();
 const { getStageLabel, getStageLabelShort } = useLanguage();
 const { user: currentUser } = useAuth();
 const [task, setTask] = useState(null);
 const [messages, setMessages] = useState([]);
 const [loading, setLoading] = useState(false);
 const [loadError, setLoadError] = useState(false);
 // #1060 Live-Presence + Audit-Stream state
 const [presenceUsers, setPresenceUsers] = useState([]);
 const [auditTick, setAuditTick] = useState(0); // increments on incoming audit, triggers reload
 const [newMsg, setNewMsg] = useState('');
 const [sending, setSending] = useState(false);
 const [isInternal, setIsInternal] = useState(false);
 // #1187 — Status-Pflicht-Pop-up nach erfolgreichem externen Reply
 const [statusModalOpen, setStatusModalOpen] = useState(false);
 // #1155 — Pflicht-Kategorie beim Abschluss (Status → done ohne gesetzte Kategorie)
 const [doneCategoryRequiredOpen, setDoneCategoryRequiredOpen] = useState(false);
 // #1240 — Case→Klärung Konvertierungs-Modal
 const [convertModalOpen, setConvertModalOpen] = useState(false);
 // #1853 — Deep-Link der Daily Cases Analyse (?aktion=schliessen): die Analyse SCHLÄGT VOR,
 // sie hat nichts geändert. Der Abschluss läuft über setStage('done') und damit durch den
 // #1155-Kategorie-Zwang — der Ein-Klick-Weg darf keine Pflichtprüfung umgehen.
 const [analyseVorschlag, setAnalyseVorschlag] = useState(
 () => new URLSearchParams(window.location.search).get('aktion') === 'schliessen'
 );
 const [toast, setToast] = useState(null);
 const [confirmDeleteCase, setConfirmDeleteCase] = useState(false);
 const [confirmRemoveCustomer, setConfirmRemoveCustomer] = useState(false);
 const chatEndRef = useRef(null);
 const taRef = useRef(null);
 const scrollRef = useRef(null);
 const editorRef = useRef(null);
 const fileInputRef = useRef(null);
 // #1751 — Anhänge liegen bis zum Senden nur als PENDING File-Objekte im Composer;
 // Upload passiert erst in sendMsg → Anhang bindet immer an die gesendete Nachricht.
 const [pendingFiles, setPendingFiles] = useState([]);
 // Retry-Schutz: name|size → bereits hochgeladene attachment-id (kein Doppel-Upload,
 // wenn der Message-POST nach erfolgreichem Upload fehlschlägt)
 const uploadedPendingRef = useRef({});
 // #1676 — Inline-Bilder (Paste/Drag in den Editor): separat von den Anhang-Chips;
 // beim Senden werden nur die im Body referenzierten IDs mitgeschickt.
 const [inlineImages, setInlineImages] = useState([]);
 // #1058 Punkt 2: CC/BCC für Reply (komma-getrennte Strings; werden beim Send geparst)
 const [ccInput, setCcInput] = useState('');
 const [bccInput, setBccInput] = useState('');
 const [showBcc, setShowBcc] = useState(false);
 // #1131 Idee 2: CC als Link — Default hidden
 const [showCc, setShowCc] = useState(false);
 // #1156 — Antwort-Entwürfe Auto-Save
 const [tiptapEditor, setTiptapEditor] = useState(null);
 const [draftRestoreInfo, setDraftRestoreInfo] = useState(null); // {minutesAgo} | null
 const draftSaveTimerRef = useRef(null);
 const lastSavedDraftRef = useRef(''); // signature der letzten gespeicherten Draft (body + flags)
 const draftFetchedForRef = useRef(0); // taskId für die wir bereits ge-fetched haben
 const draftSuppressUntilRef = useRef(0); // ms-timestamp — Save unterdrücken (z.B. direkt nach Send)
 // #1131 Idee 1: Signatur kollabiert (Default closed, click toggles preview)
 const [showSignaturePreview, setShowSignaturePreview] = useState(false);
 // #1058 Punkt 10 Follow-up: Signatur-Preview (read-only) unter Editor
 const [userSig, setUserSig] = useState(null);
 const [lightboxUrl, setLightboxUrl] = useState(null);
 // #1058 Punkt 6: Lightbox kann auch PDFs anzeigen (mime hint)
 const [lightboxKind, setLightboxKind] = useState('image'); // 'image' | 'pdf'
 const openLightbox = useCallback((url, kind = 'image') => { setLightboxKind(kind); setLightboxUrl(url); }, []);
 const [showAllNotizen, setShowAllNotizen] = useState(false);
 // #1026 — Toggle "Vollständige E-Mail anzeigen" pro Konversations-Bubble
 const [showFullMap, setShowFullMap] = useState({});
 // #1241 — Original-HTML Modal (kompakt-Rendering Toggle)
 const [mailOriginalModal, setMailOriginalModal] = useState({ open: false, html: '', subject: '' });
 const [relatedCases, setRelatedCases] = useState([]);
 const [customerInfo, setCustomerInfo] = useState(null);
 // #1114 — Resolved Reply-Empfänger + editierbares An:-Feld
 const [resolvedRecipient, setResolvedRecipient] = useState({ address: null, source: null, name: null });
 const [toInput, setToInput] = useState('');
 const [chatterEnabled, setChatterEnabled] = useState(false);
 // #1248 — Tab via URL ?tab= persistierbar (Deep-Link von Cases-Liste-Row Link2-Pill)
 const [activeContentTab, setActiveContentTab] = useTabParam('konversation'); // 'konversation' | 'intern' | 'claims'
 // #1246 — Smart-Buttons + bidirektionale Verlinkung
 const [linkedClaims, setLinkedClaims] = useState([]);
 const [linkedClaimsLoading, setLinkedClaimsLoading] = useState(false);
 const [linkClaimModalOpen, setLinkClaimModalOpen] = useState(false);
 const [unlinkingClaim, setUnlinkingClaim] = useState(null);
 const [linkedTasks, setLinkedTasks] = useState([]);
 const [creatingTask, setCreatingTask] = useState(false);
 const [taskModalOpen, setTaskModalOpen] = useState(false);
 const [taskForm, setTaskForm] = useState({ title: '', assigned_to: '', priority: 'normal', due_date: '', description: '' });
 const [taskUsers, setTaskUsers] = useState([]);
 const [taskSubmitting, setTaskSubmitting] = useState(false);
 const [taskSlideOverId, setTaskSlideOverId] = useState(null);

 // Chatter-Config laden (ob fuer Cases aktiv)
 useEffect(() => {
 api('/api/chatter/config')
 .then(d => {
 const v = d?.config?.chatter_enabled_case;
 setChatterEnabled(v === true || v === 'true');
 })
 .catch(() => {});
 }, []);

 // #1058 Punkt 10 Follow-up: User-Signatur laden für Preview-Block
 useEffect(() => {
 api('/api/users/me/signatur')
 .then(d => setUserSig(d || null))
 .catch(() => setUserSig(null));
 }, []);

 const internalIds = useMemo(() => new Set(omsUsers.map(u => u.id)), [omsUsers]);

 // #1058 Punkt 9: Prev/Next-Navigation auf Basis der zuletzt gesehenen Liste
 const [navList, setNavList] = useState([]);
 useEffect(() => {
 try {
 const raw = sessionStorage.getItem('weship_cases_nav_list');
 const parsed = raw ? JSON.parse(raw) : null;
 setNavList(Array.isArray(parsed) ? parsed : []);
 } catch { setNavList([]); }
 }, [taskId]);
 // #1499 — nav-list-Einträge sind jetzt { id, source } (alte sessionStorage-Einträge
 // können noch nackte IDs sein → tolerant lesen). hrefFor hängt ?src=oms an.
 const navIdOf = (e) => (e && typeof e === 'object') ? e.id : e;
 const navHrefFor = (e) => {
 const id = navIdOf(e);
 const src = (e && typeof e === 'object') ? e.source : undefined;
 return `/cases/${id}${src === 'oms' ? '?src=oms' : ''}`;
 };
 const navIndex = useMemo(() => {
 if (!navList.length || !taskId) return -1;
 const tid = isNaN(Number(taskId)) ? taskId : Number(taskId);
 return navList.findIndex(e => { const id = navIdOf(e); return id === tid || String(id) === String(tid); });
 }, [navList, taskId]);
 const prevEntry = navIndex > 0 ? navList[navIndex - 1] : null;
 const nextEntry = navIndex >= 0 && navIndex < navList.length - 1 ? navList[navIndex + 1] : null;
 const goPrev = useCallback(() => { if (prevEntry != null) detailNavigate(navHrefFor(prevEntry)); }, [prevEntry, detailNavigate]);
 const goNext = useCallback(() => { if (nextEntry != null) detailNavigate(navHrefFor(nextEntry)); }, [nextEntry, detailNavigate]);

 // When fullPage: lock main's scroll and reset scroll position
 useEffect(() => {
 if (!fullPage) return;
 const main = document.querySelector('main');
 if (!main) return;
 main.scrollTop = 0;
 main.style.overflow = 'hidden';
 return () => { main.style.overflow = ''; };
 }, [fullPage]);

 const retryRef = useRef(0);
 const load = useCallback(async (silent = false) => {
 if (!taskId) return;
 if (!silent) setLoading(true);
 setLoadError(false);
 try {
 // #1499 — ?src=oms mitgeben, damit bei kollidierender ID die OMS-Quelle geladen wird
 const d = await api(`/api/cases/${taskId}${srcHint === 'oms' ? '?src=oms' : ''}`);
 // #1293 — OMS-Erkennung normalisieren: Backend setzt nur `source`, kein `is_oms`.
 // Eine Quelle der Wahrheit (`source === 'oms'`), damit alle `task.is_oms`-Konsumenten
 // (FlowAI-Panel, Claims, Audit-Log) bei OMS-Cases korrekt greifen.
 setTask(d.task ? { ...d.task, is_oms: d.task.source === 'oms' } : d.task);
 setMessages(d.messages || []);
 setRelatedCases(d.related_cases || []);
 setCustomerInfo(d.customer_info || null);
 // #1114 — Resolved recipient für Reply-Editor + Sidebar
 const resolved = d.resolved_recipient || { address: null, source: null, name: null };
 setResolvedRecipient(resolved);
 setToInput(resolved.address || '');
 if (!d.task) setLoadError(true);
 retryRef.current = 0;
 // Linked tasks
 if (d.task?.id) {
 fetch(`${API}/api/user-tasks/entity/case/${d.task.id}`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : { tasks: [] })
 .then(j => setLinkedTasks(j.tasks || []))
 .catch(() => {});
 }
 } catch (e) {
 // Auto-retry up to 2 times with backoff (800ms, 1600ms)
 if (!silent && retryRef.current < 2) {
 retryRef.current++;
 const delay = retryRef.current * 800;
 setTimeout(() => load(), delay);
 return; // keep loading=true, don't show error yet
 }
 retryRef.current = 0;
 setToast({ message: e.message, type: 'error' }); setLoadError(true);
 }
 finally { if (retryRef.current === 0) setLoading(false); }
 }, [taskId, srcHint]);

 useEffect(() => { if (open && taskId) { retryRef.current = 0; setLoading(true); setTask(null); setMessages([]); setLoadError(false); setIsInternal(false); setStatusModalOpen(false); setDraftRestoreInfo(null); lastSavedDraftRef.current = ''; draftFetchedForRef.current = 0; load(); } }, [open, taskId, load]);

 // #1156 — Antwort-Entwürfe: Helper
 const draftIsEmpty = useCallback((body) => {
 if (!body) return true;
 const stripped = String(body).replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
 if (!stripped) return true;
 if (/^(\s|<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>|<br\s*\/?>)*$/i.test(String(body))) return true;
 return false;
 }, []);

 const getCurrentDraftBody = useCallback(() => {
 if (task?.source === 'email') return tiptapEditor?.getHTML?.() || '';
 return newMsg || '';
 }, [task?.source, tiptapEditor, newMsg]);

 const buildDraftSignature = useCallback((body) => {
 return JSON.stringify({ b: body, i: !!isInternal, c: ccInput || '', bc: bccInput || '' });
 }, [isInternal, ccInput, bccInput]);

 const saveDraftNow = useCallback(async () => {
 if (!taskId || !open) return;
 if (Date.now() < draftSuppressUntilRef.current) return;
 const body = getCurrentDraftBody();
 const sig = buildDraftSignature(body);
 if (sig === lastSavedDraftRef.current) return;
 lastSavedDraftRef.current = sig;
 const isOms = task?.source === 'oms';
 try {
 const res = await api(`/api/cases/${taskId}/draft${isOms ? '?oms=1' : ''}`, {
 method: 'PUT',
 body: { body, is_internal: !!isInternal, cc_emails: ccInput || '', bcc_emails: bccInput || '' },
 });
 // #1171 — Parent-Liste sofort über Draft-Stand informieren (kein Stale-Icon nach Zurück-Nav)
 if (typeof onDraftChange === 'function') {
 try { onDraftChange(taskId, !!res?.saved); } catch (_) { /* failure-soft */ }
 }
 } catch (_) { /* failure-soft */ }
 }, [taskId, open, task?.source, getCurrentDraftBody, buildDraftSignature, isInternal, ccInput, bccInput, onDraftChange]);

 const scheduleDraftSave = useCallback(() => {
 if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
 draftSaveTimerRef.current = setTimeout(() => { saveDraftNow(); }, 2000);
 }, [saveDraftNow]);

 const flushDraftSave = useCallback(() => {
 if (draftSaveTimerRef.current) { clearTimeout(draftSaveTimerRef.current); draftSaveTimerRef.current = null; }
 saveDraftNow();
 }, [saveDraftNow]);

 // #1156 — GET draft nach Task-Load (einmal pro taskId)
 // #1170 — Tiptap-Race: bei Email-Source MUSS editor ready sein, bevor wir fetchen+injecten,
 // sonst läuft setContent() auf null und der wiederhergestellte Body landet nie im Feld.
 useEffect(() => {
 if (!task?.id && !taskId) return;
 if (!open || !taskId) return;
 if (draftFetchedForRef.current === taskId) return;
 if (!task) return; // erst nach load() — wir brauchen task.source
 if (task.source === 'email' && !tiptapEditor) return; // #1170 — warten bis Tiptap mounted ist
 draftFetchedForRef.current = taskId;
 const isOms = task.source === 'oms';
 api(`/api/cases/${taskId}/draft${isOms ? '?oms=1' : ''}`)
 .then(d => {
 const dr = d?.draft;
 if (!dr || !dr.body || draftIsEmpty(dr.body)) return;
 // Lokaler Editor-Inhalt prüfen — wenn User schon getippt hat, NICHT überschreiben
 const currentBody = getCurrentDraftBody();
 if (!draftIsEmpty(currentBody)) return;
 if (task.source === 'email') {
 // #1170 — emitUpdate=false: Inject darf nicht sofort wieder Auto-Save triggern
 if (tiptapEditor) tiptapEditor.commands.setContent(dr.body, false);
 } else {
 setNewMsg(dr.body.replace(/<[^>]+>/g, ''));
 }
 if (dr.is_internal) setIsInternal(true);
 if (dr.cc_emails) { setCcInput(dr.cc_emails); setShowCc(true); }
 if (dr.bcc_emails) { setBccInput(dr.bcc_emails); setShowBcc(true); }
 const updatedAt = dr.updated_at ? new Date(dr.updated_at).getTime() : Date.now();
 const minutesAgo = Math.max(0, Math.round((Date.now() - updatedAt) / 60000));
 setDraftRestoreInfo({ minutesAgo });
 lastSavedDraftRef.current = buildDraftSignature(dr.body);
 setTimeout(() => setDraftRestoreInfo(null), 10000); // Pill nach 10s ausblenden
 })
 .catch(() => {});
 }, [open, taskId, task, tiptapEditor, draftIsEmpty, getCurrentDraftBody, buildDraftSignature]);

 // #1156 — Debounced Save bei State-Änderungen (newMsg / isInternal / cc / bcc)
 useEffect(() => {
 if (!open || !taskId || !task) return;
 if (draftFetchedForRef.current !== taskId) return; // erst nach GET initialer Draft
 scheduleDraftSave();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [newMsg, isInternal, ccInput, bccInput, open, taskId, task]);

 // #1156 — Tiptap onUpdate / onBlur hooks
 useEffect(() => {
 if (!tiptapEditor) return;
 const handleUpdate = () => scheduleDraftSave();
 const handleBlur = () => flushDraftSave();
 tiptapEditor.on('update', handleUpdate);
 tiptapEditor.on('blur', handleBlur);
 return () => {
 try { tiptapEditor.off('update', handleUpdate); tiptapEditor.off('blur', handleBlur); } catch (_) {}
 };
 }, [tiptapEditor, scheduleDraftSave, flushDraftSave]);

 // #1156 — beforeunload: sendBeacon (synchroner Last-Save)
 useEffect(() => {
 if (!open || !taskId) return;
 const handler = () => {
 try {
 const body = getCurrentDraftBody();
 if (draftIsEmpty(body) && !ccInput && !bccInput) return;
 const isOms = task?.source === 'oms';
 const payload = JSON.stringify({ body, is_internal: !!isInternal, cc_emails: ccInput || '', bcc_emails: bccInput || '' });
 const blob = new Blob([payload], { type: 'application/json' });
 // sendBeacon ist POST-only. Backend PUT akzeptiert — wir nutzen POST-Fallback via X-HTTP-Method-Override nicht;
 // stattdessen fetch keepalive (Chrome/Firefox unterstützt es).
 fetch(`${API}/api/cases/${taskId}/draft${isOms ? '?oms=1' : ''}`, {
 method: 'PUT', credentials: 'include', keepalive: true,
 headers: { 'Content-Type': 'application/json' },
 body: payload,
 }).catch(() => {});
 } catch (_) { /* failure-soft */ }
 };
 window.addEventListener('beforeunload', handler);
 return () => window.removeEventListener('beforeunload', handler);
 }, [open, taskId, task?.source, getCurrentDraftBody, draftIsEmpty, isInternal, ccInput, bccInput]);

 // #1171 — Keepalive-Flush bei Unmount (SPA-Nav triggert kein beforeunload).
 // Latest-Closure-in-Ref-Pattern: cleanup useEffect läuft mit leerem Dep-Array,
 // braucht aber aktuelle Werte für taskId/body/isInternal/cc/bcc beim Unmount.
 const flushOnUnmountRef = useRef(() => {});
 flushOnUnmountRef.current = () => {
 if (!taskId) return;
 if (Date.now() < draftSuppressUntilRef.current) return;
 const body = getCurrentDraftBody();
 const sig = buildDraftSignature(body);
 if (sig === lastSavedDraftRef.current) return; // nichts Neues — Backend ist aktuell
 lastSavedDraftRef.current = sig;
 const isOms = task?.source === 'oms';
 const payload = JSON.stringify({ body, is_internal: !!isInternal, cc_emails: ccInput || '', bcc_emails: bccInput || '' });
 try {
 fetch(`${API}/api/cases/${taskId}/draft${isOms ? '?oms=1' : ''}`, {
 method: 'PUT', credentials: 'include', keepalive: true,
 headers: { 'Content-Type': 'application/json' },
 body: payload,
 }).catch(() => {});
 } catch (_) { /* failure-soft */ }
 // Parent-Liste sofort patchen — Backend wird gleich denselben Stand haben
 if (typeof onDraftChange === 'function') {
 try { onDraftChange(taskId, !draftIsEmpty(body)); } catch (_) {}
 }
 };

 // #1156 — Cleanup beim Unmount / Modal-Close: pending Timer canceln + flush
 useEffect(() => {
 return () => {
 if (draftSaveTimerRef.current) { clearTimeout(draftSaveTimerRef.current); draftSaveTimerRef.current = null; }
 try { flushOnUnmountRef.current?.(); } catch (_) {}
 };
 }, []);

 // #1246 — Linked Claims für Smart-Button-Tab (nur lokale Cases, OMS hat keinen cases.id-FK)
 const fetchLinkedClaims = useCallback(async () => {
 if (!task?.id || task.is_oms) { setLinkedClaims([]); return; }
 setLinkedClaimsLoading(true);
 try {
 const d = await api(`/api/cases/${task.id}/linked-claims`);
 setLinkedClaims(Array.isArray(d?.rows) ? d.rows : []);
 } catch { setLinkedClaims([]); } finally { setLinkedClaimsLoading(false); }
 }, [task?.id, task?.is_oms]);
 useEffect(() => { fetchLinkedClaims(); }, [fetchLinkedClaims]);

 // #1248 — Per-User View-Tracking (fire-and-forget). Resettet has_active_linked_claims
 // in Cases-Liste + Indigo-Dot in linked Claims im Claim-Detail beim nächsten Aufruf.
 useEffect(() => {
 if (!task?.id || task.is_oms) return;
 fetch(`${API}/api/cases/${task.id}/mark-viewed`, { method: 'POST', credentials: 'include' }).catch(() => {});
 }, [task?.id, task?.is_oms]);

 const handleUnlinkClaim = async (linkId) => {
 if (!window.confirm(t('links.confirmUnlink', 'Verknüpfung wirklich entfernen?'))) return;
 setUnlinkingClaim(linkId);
 try {
 const r = await fetch(`${API}/api/claim-case-links/${linkId}`, { method: 'DELETE', credentials: 'include' });
 if (!r.ok) {
 const err = await r.json().catch(() => ({}));
 setToast({ message: err.message || err.error || 'Fehler', type: 'error' });
 return;
 }
 await fetchLinkedClaims();
 load(true);
 } catch (err) {
 setToast({ message: err.message || 'Fehler', type: 'error' });
 } finally {
 setUnlinkingClaim(null);
 }
 };

 // #1060 WebSocket: Live-Presence + Audit-Stream — only for local cases
 useEffect(() => {
 if (!open || !task?.id || task.is_oms) return; // OMS-Cases haben kein audit_log (FK auf cases.id)
 const caseId = task.id;
 const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
 const wsUrl = `${proto}//${window.location.host}/ws/cases?case_id=${caseId}`;
 let ws = null;
 let reconnectTimer = null;
 let heartbeatTimer = null;
 let attempts = 0;
 let cancelled = false;
 let openedAt = 0;
 // #1837 — nie 0 ms: ein sofortiger Neuaufbau macht aus jedem Server-Close eine
 // Heissschleife (gemessen ~10 Handshakes je Fall im Netzwerkprotokoll).
 const backoff = [1000, 2000, 5000, 10000, 30000];
 // #1837 — Codes, nach denen ein Neuaufbau schadet statt hilft:
 // 4001 kein/ungültiges Cookie und 4002 fehlendes case_id ändern sich durch
 // Wiederholen nicht; 4003 „Replaced by new tab" heisst, dass eine ANDERE
 // Verbindung desselben Benutzers für denselben Fall übernommen hat — wer sich
 // hier zurückverbindet, wirft die andere hinaus, die sich ihrerseits
 // zurückverbindet: ein Ping-Pong ohne Ende. Diese Verbindung bleibt unten und
 // kommt erst zurück, wenn der Tab wieder in den Vordergrund geholt wird.
 const KEIN_NEUAUFBAU = new Set([4001, 4002, 4003]);
 let ruhtBisSichtbar = false;

 const connect = () => {
 if (cancelled) return;
 // Doppel-Verbindung ausschliessen (AK4: genau EINE Verbindung je offenem Fall)
 if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) return;
 ruhtBisSichtbar = false;
 try {
 ws = new WebSocket(wsUrl);
 } catch (e) {
 console.warn('[Cases WS] connect threw', e);
 return;
 }
 ws.addEventListener('open', () => {
 openedAt = Date.now();
 // Start heartbeat every 30s
 heartbeatTimer = setInterval(() => {
 if (ws && ws.readyState === WebSocket.OPEN) {
 try { ws.send(JSON.stringify({ type: 'heartbeat' })); } catch {}
 }
 }, 30000);
 });
 ws.addEventListener('message', (ev) => {
 try {
 const msg = JSON.parse(ev.data);
 if (msg.type === 'presence-update' && Array.isArray(msg.users)) {
 setPresenceUsers(msg.users);
 } else if (msg.type === 'audit-update' && msg.entry) {
 setTask(prev => {
 if (!prev) return prev;
 const existing = Array.isArray(prev.audit_log) ? prev.audit_log : [];
 // De-dupe by id
 if (existing.some(a => a.id === msg.entry.id)) return prev;
 return { ...prev, audit_log: [msg.entry, ...existing].slice(0, 200) };
 });
 setAuditTick(n => n + 1);
 }
 } catch (e) {
 // ignore parse errors
 }
 });
 ws.addEventListener('close', (ev) => {
 if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
 if (cancelled) return;
 if (KEIN_NEUAUFBAU.has(ev.code)) { ruhtBisSichtbar = (ev.code === 4003); return; }
 // #1837 — Der Zähler wird NUR zurückgesetzt, wenn die Verbindung wirklich
 // gestanden hat. Ein Reset im open-Handler hebt die Bremse bei jedem Kick
 // wieder auf, und der Backoff wächst nie über die erste Stufe hinaus.
 if (openedAt && Date.now() - openedAt >= 10000) attempts = 0;
 openedAt = 0;
 const stufe = backoff[Math.min(attempts, backoff.length - 1)];
 attempts++;
 reconnectTimer = setTimeout(connect, stufe + Math.floor(Math.random() * 500));
 });
 ws.addEventListener('error', () => {
 // close handler will trigger reconnect
 });
 };

 // #1837 — Der nach 4003 zurückgestellte Tab holt sich die Verbindung zurück,
 // sobald er wieder angesehen wird: dann ist ER der Tab, in dem gearbeitet wird.
 const beiSichtbarkeit = () => {
 if (cancelled || document.visibilityState !== 'visible') return;
 if (!ruhtBisSichtbar) return;
 attempts = 0;
 connect();
 };
 document.addEventListener('visibilitychange', beiSichtbarkeit);

 connect();

 return () => {
 cancelled = true;
 document.removeEventListener('visibilitychange', beiSichtbarkeit);
 if (heartbeatTimer) clearInterval(heartbeatTimer);
 if (reconnectTimer) clearTimeout(reconnectTimer);
 if (ws) {
 try { ws.close(1000, 'Unmount'); } catch {}
 ws = null;
 }
 setPresenceUsers([]);
 };
 }, [open, task?.id, task?.is_oms]);

 // #1837 — Bereinigen + Sanitizen EINMAL je Nachricht statt bei jedem Render. Liefert den
 // stabilen HTML-String, an dem `MessageBody` seine memo-Entscheidung trifft: solange sich
 // weder die Nachricht noch der Quote-Aufklapper ändert, bleibt der String identisch, das
 // memo greift und der DOM-Knoten wird nicht angefasst. Nebeneffekt: DOMPurify läuft nicht
 // mehr bei jedem Tastendruck im Antwortfeld über den kompletten Verlauf.
 const msgBodyHtml = useMemo(() => {
 const out = new Map();
 for (const m of (messages || [])) {
 const isEmailMsg = m.message_type === 'email' || m.message_type === 'email_inbound';
 // #1241 — Mails durch extractCleanContent strippen (Logo/Banner/Footer raus);
 // showFullMap (Quote-History) bleibt unverändert für #1026.
 const rawBody = showFullMap[m.id] && m.body_full ? m.body_full : m.body;
 const cleaned = isEmailMsg ? extractCleanContent(rawBody) : rawBody;
 out.set(m.id, DOMPurify.sanitize(cleaned, PURIFY_CONFIG));
 }
 return out;
 }, [messages, showFullMap]);

 // Scroll to newest message (bottom) after messages load
 const justSentRef = useRef(false);
 useEffect(() => {
 if (chatEndRef.current) {
 chatEndRef.current.scrollIntoView({ behavior: justSentRef.current ? 'smooth' : 'auto', block: 'end' });
 justSentRef.current = false;
 }
 }, [messages]);

 // #1155 — Pflicht-Kategorie beim Abschließen: wenn key='done' UND noch keine Kategorie gesetzt,
 // Modal öffnen, das den Agent zur Kategorie-Auswahl zwingt. Stage-Push erst nach Bestätigung.
 const setStage = async (key, opts = {}) => {
 // #1254 — opts.skipCategoryCheck überspringt den Guard, wenn die Kategorie gerade
 // im Done-Modal gesetzt wurde (task.category_id im Closure noch stale → sonst 2. Klick nötig).
 if (key === 'done' && !opts.skipCategoryCheck && !(task && task.category_id)) {
 setDoneCategoryRequiredOpen(true);
 return;
 }
 try {
 const st = stages.find(s=>s.key===key);
 if (!st) return;
 await api(`/api/cases/${taskId}/stage${task?.source === 'oms' ? '?src=oms' : ''}`, { method:'PUT', body:{stage_id:st.id, stage_name:st.name} });
 setTask(p => ({...p, stage_key:key, stage_id_num:st.id, stage_name:st.name}));
 onRefresh?.();
 } catch(e) { setToast({message:e.message,type:'error'}); }
 };
 const setPrio = async (p) => { try { await api(`/api/cases/${taskId}/priority${task?.source === 'oms' ? '?src=oms' : ''}`, { method:'PUT', body:{priority:p} }); setTask(prev => ({...prev, priority:p})); onRefresh?.(); } catch(e) { setToast({message:e.message,type:'error'}); } };
 const setAssign = async (uid) => { try { await api(`/api/cases/${taskId}/assign${task?.source === 'oms' ? '?src=oms' : ''}`, { method:'PUT', body:{user_ids: uid ? [Number(uid)] : []} }); load(true); onRefresh?.(); } catch(e) { setToast({message:e.message,type:'error'}); } };
 // #1066 — Retry-Send für fehlgeschlagene Reply-Comments
 const [retryingCommentId, setRetryingCommentId] = useState(null);
 const retrySend = async (commentId) => {
 if (!commentId) return;
 setRetryingCommentId(commentId);
 try {
 const res = await api(`/api/cases/${taskId}/message/${commentId}/retry`, { method: 'POST' });
 if (res.send_status === 'sent') {
 setToast({ message: t('cases.send.toastRetrySent').replace('{address}', res.recipient || ''), type: 'success' });
 } else if (res.send_status === 'no_recipient') {
 setToast({ message: t('cases.send.toastNoRecipient'), type: 'warning' });
 } else {
 setToast({ message: t('cases.send.toastRetryFailed').replace('{error}', res.error || 'unknown'), type: 'error' });
 }
 await load(true);
 } catch (e) {
 setToast({ message: e.message || 'Retry failed', type: 'error' });
 } finally {
 setRetryingCommentId(null);
 }
 };

 // Kunde zuordnen
 const [kundeSearch, setKundeSearch] = useState('');
 const [kundeSearchOpen, setKundeSearchOpen] = useState(false);
 const [kundenList, setKundenList] = useState([]);
 const kundeRef = useRef(null);

 useEffect(() => {
 if (kundeSearchOpen && !kundenList.length) {
 api('/api/kunden').then(rows => setKundenList(Array.isArray(rows) ? rows : [])).catch(() => {});
 }
 }, [kundeSearchOpen, kundenList.length]);

 useEffect(() => {
 if (!kundeSearchOpen) return;
 const onClick = (e) => { if (kundeRef.current && !kundeRef.current.contains(e.target)) setKundeSearchOpen(false); };
 document.addEventListener('mousedown', onClick);
 return () => document.removeEventListener('mousedown', onClick);
 }, [kundeSearchOpen]);

 // ESC closes lightbox
 useEffect(() => {
 if (!lightboxUrl) return;
 const onKey = (e) => { if (e.key === 'Escape') setLightboxUrl(null); };
 document.addEventListener('keydown', onKey);
 return () => document.removeEventListener('keydown', onKey);
 }, [lightboxUrl]);

 const filteredKunden = useMemo(() => {
 if (!kundeSearch.trim()) return kundenList.slice(0, 15);
 const q = kundeSearch.toLowerCase();
 return kundenList.filter(k => k.name?.toLowerCase().includes(q)).slice(0, 15);
 }, [kundenList, kundeSearch]);

 const assignCustomer = async (kundeId) => {
 try {
 const res = await api(`/api/cases/${taskId}/assign-customer`, { method: 'POST', body: { kunde_id: kundeId ?? null } });
 setKundeSearchOpen(false);
 setKundeSearch('');
 if (kundeId) {
 if (res.kunde_name) setTask(p => ({ ...p, company_name: res.kunde_name, company_id_num: kundeId }));
 setToast({ message: res.email_saved ? t('cases.customerAssignedWithEmail') : t('cases.customerAssigned'), type: 'success' });
 } else {
 // #1374 — Zuweisung entfernt
 setTask(p => ({ ...p, company_name: null, company_id_num: null }));
 setCustomerInfo(null);
 setToast({ message: t('cases.customerRemoved'), type: 'success' });
 }
 load(true);
 onRefresh?.();
 } catch (e) { setToast({ message: e.message, type: 'error' }); }
 };

 const [omsCredsMissing, setOmsCredsMissing] = useState(false);

 const [taskDuplicateWarn, setTaskDuplicateWarn] = useState(null);
 const [taskCategories, setTaskCategories] = useState([]);

 const openTaskModal = async () => {
 if (!task) return;
 setTaskForm({
 title: '', assigned_to: '', priority: 'normal', due_date: '', description: '', category_id: '',
 });
 setTaskDuplicateWarn(null);
 setTaskModalOpen(true);
 // Load users + categories + duplicate check in parallel
 Promise.all([
 fetch(`${API}/api/chatter/users/search?q=a`, { credentials: 'include' }).then(r => r.ok ? r.json() : { users: [] }),
 fetch(`${API}/api/user-tasks/categories`, { credentials: 'include' }).then(r => r.ok ? r.json() : { categories: [] }),
 fetch(`${API}/api/user-tasks/entity/case/${task.id}`, { credentials: 'include' }).then(r => r.ok ? r.json() : { tasks: [] }),
 ]).then(([ud, cd, ed]) => {
 setTaskUsers(ud.users || []);
 setTaskCategories(cd.categories || []);
 const openTasks = (ed.tasks || []).filter(t => t.status !== 'erledigt');
 if (openTasks.length > 0) setTaskDuplicateWarn(openTasks);
 }).catch(() => {});
 };

 const submitTask = async () => {
 if (!taskForm.title.trim() || !taskForm.assigned_to || taskSubmitting) return;
 setTaskSubmitting(true);
 try {
 await api('/api/user-tasks', {
 method: 'POST',
 body: {
 title: taskForm.title.trim(),
 entity_type: 'case', entity_id: task.id,
 entity_label: `Case ${formatCaseId(task.id, task.source)} — ${(task.name || task.company_name || '').substring(0, 80)}`,
 source: 'case', priority: taskForm.priority,
 due_date: taskForm.due_date || null,
 description: taskForm.description || null,
 assigned_to: Number(taskForm.assigned_to),
 ...(taskForm.category_id ? { category_id: Number(taskForm.category_id) } : {}),
 },
 });
 setToast({ message: t('cases.taskCreated'), type: 'success' });
 setTaskModalOpen(false);
 // Refresh linked tasks
 fetch(`${API}/api/user-tasks/entity/case/${task.id}`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : { tasks: [] })
 .then(j => setLinkedTasks(j.tasks || []))
 .catch(() => {});
 } catch (e) {
 setToast({ message: e.message || t('common.error'), type: 'error' });
 }
 setTaskSubmitting(false);
 };

 const sendMsg = async () => {
 // Get content from TiptapEditor or textarea fallback
 const htmlContent = editorRef.current?.getHTML?.() || '';
 const msgBody = task?.source === 'email' ? htmlContent : newMsg.trim();
 if (!msgBody?.trim() || msgBody === '<p></p>' || sending) return;
 // #1058 Punkt 2: CC/BCC parsen (Komma- oder Semikolon-Trenner, Whitespace strippen, Duplikate entfernen)
 const parseAddrs = (s) => Array.from(new Set(
 String(s || '')
 .split(/[,;\s]+/)
 .map(x => x.trim())
 .filter(x => x && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x))
 ));
 const ccArr = isInternal ? [] : parseAddrs(ccInput);
 const bccArr = isInternal ? [] : parseAddrs(bccInput);
 // #1676 — nur Inline-Bilder mitschicken, die noch im Body referenziert sind
 // (User kann eingefügte Bilder wieder löschen → kein Ghost-Anhang)
 const referencedInlineIds = inlineImages
 .filter(a => msgBody.includes(`/api/cases/attachments/${a.id}/inline`))
 .map(a => a.id);
 setSending(true);
 try {
 // #1751 — Pending-Dateien erst JETZT hochladen; danach binden sie via
 // attachment_ids an die gesendete Nachricht (comment_id gesetzt, nie Orphan).
 const pendingIds = [];
 const toUpload = pendingFiles.filter(f => {
 const prevId = uploadedPendingRef.current[`${f.name}|${f.size}`];
 if (prevId) { pendingIds.push(prevId); return false; }
 return true;
 });
 if (toUpload.length) {
 const formData = new FormData();
 for (const f of toUpload) formData.append('file', f);
 const up = await apiUpload(`/api/cases/${taskId}/attachments${task?.source === 'oms' ? '?src=oms' : ''}`, formData);
 for (const a of up.attachments || []) {
 uploadedPendingRef.current[`${a.original_name}|${a.size_bytes}`] = a.id;
 pendingIds.push(a.id);
 }
 }
 const res = await api(`/api/cases/${taskId}/message${task?.source === 'oms' ? '?src=oms' : ''}`, {
 method: 'POST',
 body: {
 body: msgBody,
 is_internal: isInternal,
 attachment_ids: [...pendingIds, ...referencedInlineIds],
 cc: ccArr,
 bcc: bccArr,
 },
 });
 // #1156 — Draft wurde Backend-seitig gelöscht; Save kurz unterdrücken bis State leer ist
 draftSuppressUntilRef.current = Date.now() + 3000;
 lastSavedDraftRef.current = '';
 setDraftRestoreInfo(null);
 if (draftSaveTimerRef.current) { clearTimeout(draftSaveTimerRef.current); draftSaveTimerRef.current = null; }
 // #1171 — Liste sofort patchen: Backend hat Draft beim Send entfernt
 if (typeof onDraftChange === 'function') {
 try { onDraftChange(taskId, false); } catch (_) { /* failure-soft */ }
 }
 setNewMsg('');
 setInlineImages([]);
 setPendingFiles([]);
 uploadedPendingRef.current = {};
 setCcInput('');
 setBccInput('');
 setShowBcc(false);
 setOmsCredsMissing(false);
 if (editorRef.current) editorRef.current.commands?.clearContent();
 if (taRef.current) taRef.current.style.height = 'auto';
 // #1066 — Status-Toast basierend auf send_status statt nur email_sent_to
 if (res.send_status === 'sent' && res.email_sent_to) {
 setToast({ message: t('cases.emailSent').replace('{address}', res.email_sent_to), type: 'success' });
 } else if (res.send_status === 'no_recipient') {
 setToast({ message: t('cases.send.toastNoRecipient'), type: 'warning' });
 } else if (res.send_status === 'failed') {
 setToast({ message: t('cases.send.toastFailed'), type: 'error' });
 } else if (res.email_sent_to) {
 // Legacy fallback (kein send_status → wie bisher)
 setToast({ message: t('cases.emailSent').replace('{address}', res.email_sent_to), type: 'success' });
 }
 // Silent reload — no skeleton/blink, dialog stays open
 justSentRef.current = true;
 await load(true);
 // #1187 — Nach erfolgreichem externen Reply Status-Pflicht-Pop-up öffnen
 // (nicht bei internen Notizen, nicht bei Send-Fehler)
 if (!isInternal) {
 setStatusModalOpen(true);
 }
 }
 catch(e) {
 if (e.message?.includes('OMS-Zugangsdaten') || e.message?.includes('OMS credentials')) {
 setOmsCredsMissing(true);
 }
 setToast({message:e.message,type:'error'});
 }
 finally { setSending(false); }
 };

 // #1751 — Drop/Büroklammer/Paste legen Dateien nur noch als PENDING in den Composer
 // (kein Sofort-Upload → kein Orphan am falschen Case, #1750). Funktionales
 // setPendingFiles ist Pflicht: Editor-/Drop-Closures halten den Mount-State (#1685).
 // Limits wie CreateCaseModal #1324/#1247 (Server-Limit /attachments).
 const handleFileUpload = useCallback((files) => {
 const list = Array.from(files || []);
 if (!list.length) return;
 const MAX_FILES = 10;
 const MAX_TOTAL_BYTES = 25 * 1024 * 1024;
 let limitHit = false;
 setPendingFiles(prev => {
 const next = [...prev];
 for (const f of list) {
 if (next.find(x => x.name === f.name && x.size === f.size)) continue;
 if (next.length >= MAX_FILES || next.reduce((s, x) => s + x.size, 0) + f.size > MAX_TOTAL_BYTES) { limitHit = true; continue; }
 next.push(f);
 }
 return next;
 });
 // Updater läuft synchron beim Dispatch; Toast außerhalb der Render-Phase feuern
 if (limitHit) setTimeout(() => setToast({ message: t('cases.pendingAttachmentsLimit'), type: 'warning' }), 0);
 }, [t]);

 // #1676 — Inline-Bild-Upload (Paste/Drag in den Editor): gleicher Endpoint/Speicher
 // wie normale Anhänge, aber NICHT in den Chips — der Editor fügt <img> ein.
 const uploadInlineImage = async (file) => {
 const formData = new FormData();
 formData.append('file', file);
 const res = await apiUpload(`/api/cases/${taskId}/attachments${task?.source === 'oms' ? '?src=oms' : ''}`, formData);
 const att = res.attachments?.[0];
 if (att) setInlineImages(prev => [...prev, att]);
 return att || null;
 };

 // #1058 Punkt 5: Drag & Drop Multi-File für Reply
 const [dragActive, setDragActive] = useState(false);
 const dragCounterRef = useRef(0);
 const handleDragEnter = useCallback((e) => {
 e.preventDefault(); e.stopPropagation();
 if (e.dataTransfer?.types?.includes('Files')) {
 dragCounterRef.current += 1;
 setDragActive(true);
 }
 }, []);
 const handleDragLeave = useCallback((e) => {
 e.preventDefault(); e.stopPropagation();
 dragCounterRef.current -= 1;
 if (dragCounterRef.current <= 0) {
 dragCounterRef.current = 0;
 setDragActive(false);
 }
 }, []);
 const handleDragOver = useCallback((e) => {
 e.preventDefault(); e.stopPropagation();
 if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
 }, []);
 const handleDrop = useCallback((e) => {
 e.preventDefault(); e.stopPropagation();
 dragCounterRef.current = 0;
 setDragActive(false);
 const files = Array.from(e.dataTransfer?.files || []);
 if (files.length) handleFileUpload(files);
 }, [handleFileUpload]);

 // #1131 Idee 3: Auto-Resize bis 400px (vorher 160px) — wächst dynamisch mit Newlines
 const adjustTa = () => { if (taRef.current) { taRef.current.style.height = 'auto'; taRef.current.style.height = Math.min(taRef.current.scrollHeight, 400) + 'px'; } };

 if (!open) return null;

 // Filter messages — oldest first (backend sends ASC, newest at bottom)
 const allVisibleMsgs = messages.filter((m, i, arr) => {
 const b = m.body?.trim();
 if (!b || b === '<p><br></p>' || b === '<br>' || b === '<p></p>') return false;
 // #1026 — Initiale Email NIE in der Konversation anzeigen, sie steht oben
 // unter "PROBLEMBESCHREIBUNG" (description_html). Backend liefert is_initial.
 if (m.is_initial) return false;
 // Fallback (Legacy): Strip-Compare gegen task.description, falls is_initial
 // nicht gesetzt (alte OMS-Pfade). Greift nur, wenn der allererste Eintrag
 // textuell identisch ist.
 if (task?.description && i === 0) {
 const strip = s => (s || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
 if (strip(b) === strip(task.description)) return false;
 }
 return true;
 });

 // Separate internal notes from conversation
 const isMsgInternalFn = (m) => m.message_type === 'internal' || m.is_internal === true;
 const interneNotizen = allVisibleMsgs.filter(m => isMsgInternalFn(m));
 const visibleMsgs = allVisibleMsgs.filter(m => !isMsgInternalFn(m));

 // #1506 — CC der ERSTEN eingehenden Mail für die Kopf-Pill (SSoT = m.cc wie im Thread).
 // WICHTIG: aus dem ROHEN messages-Array ableiten, NICHT aus visibleMsgs — die initiale
 // Inbound-Mail (die das CC trägt) wird in visibleMsgs per is_initial (#1026) ausgefiltert.
 // Inbound = email_inbound (Local-Case) bzw. OMS-'email' nicht von WeShip-Domain (analog Thread-Logik).
 const _ccAgentDomains = ['@weship.eu', '@weship.at', '@dynaflow.at'];
 const _ccIsAgent = (e) => e && _ccAgentDomains.some(d => e.toLowerCase().includes(d));
 const _firstInboundCc = messages.find(m => {
 const inbound = m.message_type === 'email_inbound' || (m.message_type === 'email' && !_ccIsAgent(m.email_from));
 return inbound && Array.isArray(m.cc) && m.cc.length > 0;
 });
 const headerCc = _firstInboundCc && Array.isArray(_firstInboundCc.cc) ? _firstInboundCc.cc : [];

 // #1754 — An-Empfänger der ERSTEN eingehenden Mail für die Kopf-Pill (SSoT = m.to wie im Thread,
 // = to_addresses mit Fallback [to_address]/[delivered_to] aus cases.js). Gleiche Inbound-Erkennung
 // wie beim CC-Pill; jede Inbound-Mail trägt ein to → dies ist die erste eingehende Mail.
 const _firstInboundTo = messages.find(m => {
 const inbound = m.message_type === 'email_inbound' || (m.message_type === 'email' && !_ccIsAgent(m.email_from));
 return inbound && Array.isArray(m.to) && m.to.length > 0;
 });
 const headerTo = _firstInboundTo && Array.isArray(_firstInboundTo.to) ? _firstInboundTo.to : [];

 // Group by day
 const dayGroups = [];
 let lastDay = '';
 for (const m of visibleMsgs) {
 const day = m.date ? new Date(m.date).toDateString() : '';
 if (day !== lastDay) { dayGroups.push({ type: 'sep', date: m.date }); lastDay = day; }
 dayGroups.push({ type: 'msg', msg: m });
 }
 const msgCount = visibleMsgs.length;

 // Stage color helper (by key) — hex for inline styles where needed
 const stageColor = (key) => {
 if (key === 'response_required') return '#ef4444';
 if (key === 'waiting_for_support') return '#f59e0b';
 if (key === 'in_progress') return '#3b82f6';
 if (key === 'solution_proposed') return '#338edc';
 if (key === 'clarification_with_recipient') return '#f59e0b';
 if (key === 'done') return '#22c55e';
 return MODULE_COLOR;
 };

 // Normalize Odoo HTML: collapse excessive whitespace/newlines
 const normalizeHtml = (html) => {
 if (!html) return '';
 return html
 .replace(/(\s*<br\s*\/?>\s*){3,}/gi, '<br><br>') // max 2 consecutive <br>
 .replace(/(<p>\s*<\/p>\s*){2,}/gi, '<p></p>') // collapse empty <p> runs
 .replace(/\n{3,}/g, '\n\n'); // max 2 consecutive newlines
 };

 if (!open) return null;

 // Direct conditional rendering (NOT a Wrapper component — inline components
 // would create new refs on every state update → unmount/remount → focus loss)
 const contentClass = fullPage
 ? 'flex flex-col flex-1 min-h-0'
 : 'relative bg-surface-elevated border border-line-hover rounded-card shadow-elevated overflow-y-auto';
 const contentStyle = fullPage
 ? {}
 : { width: '90vw', maxWidth: 1800, minWidth: 800, maxHeight: '90vh' };

 const innerContent = (
 <div className={contentClass} style={contentStyle}>

 {(loading || (!task && !loadError)) ? (
 <div className="flex-1 p-6 space-y-4 animate-pulse">
 <div className="flex items-center gap-3">
 <div className="h-5 w-5 rounded bg-[var(--bg-hover)]" />
 <div className="h-5 w-16 rounded bg-[var(--bg-hover)]" />
 <div className="h-5 flex-1 rounded bg-[var(--bg-hover)]" />
 <div className="h-5 w-20 rounded bg-[var(--bg-hover)]" />
 </div>
 <div className="h-3 w-48 rounded bg-[var(--bg-hover)]" />
 <div className="border-t border-line my-4" />
 <div className="flex gap-6">
 <div className="flex-1 space-y-3">
 {[1,2,3,4,5].map(i => (
 <div key={i} className="h-4 rounded bg-[var(--bg-hover)]" style={{ width: i % 2 === 0 ? '60%' : '85%' }} />
 ))}
 </div>
 <div className="w-64 space-y-3">
 {[1,2,3].map(i => (
 <div key={i} className="h-10 rounded bg-[var(--bg-hover)]" />
 ))}
 </div>
 </div>
 </div>
 ) : task ? (
 <div className="flex flex-1 min-h-0 overflow-hidden">
 {/* Left column: Header (pinned) + scrollable content + reply box */}
 <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">

 {/* ════ HEADER — Breadcrumb + Title-Area (inside left column, non-scrolling) ════ */}
 <div className="flex-shrink-0">
 {/* ── Breadcrumb-Bar (Style wie RecruitingDetailUnified) ── */}
 <div className="px-6 pt-5 pb-3 border-b flex-shrink-0 bg-white dark:bg-[var(--bg-card)]"
 style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <div className="flex items-center justify-between flex-wrap gap-3">
 {/* Left: Back + Titel */}
 <div className="flex items-center gap-3 min-w-0">
 <button onClick={onClose}
 className="text-sm flex items-center gap-1 font-medium flex-shrink-0 hover:opacity-80 transition-opacity"
 style={{ color: '#338edc' }}>
 <ChevronLeft size={16} />
 {t('cases.title')}
 </button>
 <span style={{ color: 'var(--text-muted, #9CA3AF)' }}>/</span>
 <h1 className="text-base font-bold truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
 {formatCaseId(task.id, task.display_source || task.source)} {task.company_name || ''}
 </h1>
 </div>
 {/* Right: Prev/Next + Task + Watch + Close */}
 <div className="flex items-center gap-2 flex-shrink-0">
 {/* #1058 Punkt 9: Pfeile + Counter */}
 {navIndex >= 0 && navList.length > 1 && (
 <div className="flex items-center gap-1.5 mr-1">
 <button
 onClick={goPrev}
 disabled={!prevEntry}
 title={t('cases.navPrev')}
 className="w-7 h-7 rounded-lg flex items-center justify-center border border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:text-[#338edc] hover:bg-[var(--bg-inset,#F8F9FC)] dark:hover:bg-[var(--bg-card-hover)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
 <ChevronLeft size={14} />
 </button>
 <span className="text-[11px] font-medium tabular-nums" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
 {t('cases.navCounter', { current: navIndex + 1, total: navList.length })}
 </span>
 <button
 onClick={goNext}
 disabled={!nextEntry}
 title={t('cases.navNext')}
 className="w-7 h-7 rounded-lg flex items-center justify-center border border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:text-[#338edc] hover:bg-[var(--bg-inset,#F8F9FC)] dark:hover:bg-[var(--bg-card-hover)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
 <ChevronLeft size={14} className="rotate-180" />
 </button>
 </div>
 )}
 <button onClick={openTaskModal}
 className="px-3 py-1.5 text-xs border rounded-lg transition-colors flex items-center gap-1.5 border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary,#4B5563)] dark:text-[var(--text-tertiary,#6B7280)] hover:bg-[var(--bg-inset,#F8F9FC)] dark:hover:bg-[var(--bg-card-hover)] disabled:opacity-40">
 <CheckSquare className="w-3.5 h-3.5" />
 {t('cases.createTask', 'Create Task')}
 </button>
 {/* #1240 — Case→Klärung Konvertierung: nur lokale (kein OMS) offene + nicht-konvertierte Cases */}
 {task.source !== 'oms' && !task.converted_to_claim_id && task.stage_key !== 'done' && (
   <button onClick={() => setConvertModalOpen(true)}
     className="px-3 py-1.5 text-xs border rounded-lg transition-colors flex items-center gap-1.5 border-[#338edc]/30 text-[#338edc] hover:bg-[#338edc]/5">
     <GitBranch className="w-3.5 h-3.5" />
     {t('cases.convert.button') || 'Zu Klärung konvertieren'}
   </button>
 )}
 <button onClick={e => { e.stopPropagation(); toggleWatch(task.id); }}
 className={`px-3 py-1.5 text-xs border rounded-lg transition-colors flex items-center gap-1.5 ${
 watchedIds.has(task.id)
 ? 'bg-primary-50 text-[#338edc] border-[#338edc]/30 dark:bg-primary-900/20 font-semibold'
 : 'border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary,#4B5563)] dark:text-[var(--text-tertiary,#6B7280)] hover:bg-[var(--bg-inset,#F8F9FC)] dark:hover:bg-[var(--bg-card-hover)]'
 }`}>
 <Eye className="w-3.5 h-3.5" />
 {watchedIds.has(task.id) ? t('cases.watching') : t('cases.watch')}
 </button>
 {!fullPage && (
 <button onClick={onClose} className="text-[var(--text-secondary,#4B5563)] hover:text-content flex-shrink-0 w-8 h-8 rounded-lg hover:bg-surface-hover flex items-center justify-center transition-colors">
 <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
 </button>
 )}
 </div>
 </div>
 </div>

 {/* ── Title-Area ── */}
 <div className="flex items-start justify-between px-6 py-5 border-b border-[var(--border-table,#F3F4F6)] bg-white dark:bg-[var(--bg-card)] dark:border-[rgba(255,255,255,0.06)]">
 {/* Left: Title + Description + Meta-Chips */}
 <div className="flex-1 min-w-0">
 {/* Zeile 1: ID-Pill + Titel */}
 <div className="flex items-center gap-3 mb-1.5">
 <span className="text-xs font-medium px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/15 dark:text-blue-400 dark:border-blue-500/20 flex-shrink-0">
 {formatCaseId(task.id, task.display_source || task.source)}
 </span>
 <h1 className="text-[17px] font-semibold text-[var(--text-primary,#1A1A2E)] dark:text-content truncate">
 {task.name}
 </h1>
 </div>

 {/* Zeile 2: Beschreibung — NUR wenn anders als Betreff, max 1 Zeile */}
 {(() => {
 const snippet = task.ai_summary || (task.description ? stripHtml(task.description).substring(0, 120) : '');
 return snippet && snippet.trim().toLowerCase() !== (task.name || '').trim().toLowerCase() ? (
 <p className="text-[13px] text-[var(--text-tertiary,#6B7280)] truncate max-w-2xl mb-3">
 {snippet}
 </p>
 ) : <div className="mb-3" />;
 })()}

 {/* Zeile 3: Meta-Chips MIT farbigem Background + Border */}
 <div className="flex items-center gap-2 flex-wrap">
 {task.company_name && (
 <Link to={`/kunden/${task.company_id_num || ''}`}
 onClick={e => e.stopPropagation()}
 className="inline-flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1.5 rounded-lg dark:bg-blue-500/15 dark:border-blue-500/20 dark:text-blue-400 hover:text-[#338edc] transition-colors">
 <Building size={13} className="text-blue-400" />
 {task.company_name}
 </Link>
 )}
 <span className="inline-flex items-center gap-1.5 text-xs text-[var(--text-secondary,#4B5563)] bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] px-2.5 py-1.5 rounded-lg dark:bg-[var(--bg-inset,#F8F9FC)] dark:border-[var(--border-default,#E5E7EB)] dark:text-[var(--text-tertiary,#6B7280)]">
 <Clock size={13} className="text-[var(--text-tertiary,#6B7280)]" />
 {relTime(task.create_date, t)}
 </span>
 {task.source && (
 <span className="inline-flex items-center gap-1.5 text-xs text-purple-700 bg-purple-50 border border-purple-200 px-2.5 py-1.5 rounded-lg dark:bg-purple-500/15 dark:border-purple-500/20 dark:text-purple-400">
 <Mail size={13} className="text-purple-400" />
 {t('cases.header.via')} {task.source}
 </span>
 )}
 {task.contact_name && (
 <span className="inline-flex items-center gap-1.5 text-xs text-cyan-700 bg-cyan-50 border border-cyan-200 px-2.5 py-1.5 rounded-lg dark:bg-cyan-500/15 dark:border-cyan-500/20 dark:text-cyan-400">
 <User size={13} className="text-cyan-400" />
 {task.contact_name}
 </span>
 )}
 {/* #1754 — An-Empfänger der ersten eingehenden Mail (nur ab 2), vor dem CC-Pill */}
 <ToHeaderPill to={headerTo} t={t} />
 {/* #1506 — CC-Empfänger der ersten eingehenden Mail */}
 <CcHeaderPill cc={headerCc} t={t} />
 {task.tags?.length > 0 && task.tags.map(tg => (
 <span key={tg.id} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-red-700 bg-red-50 border border-red-200 dark:bg-red-500/15 dark:border-red-500/20 dark:text-red-400">
 <Tag size={10} /> {tg.name}
 </span>
 ))}
 </div>
 </div>

 {/* Right: Status + SLA — hier, nicht woanders */}
 <div className="flex flex-col items-end gap-2.5 flex-shrink-0 ml-8">
 {/* #1060 Live-Presence avatar stack */}
 {presenceUsers.length > 0 && (
 <AvatarStack users={presenceUsers} t={t} currentUserId={currentUser?.id} />
 )}
 {/* Status Badge */}
 {(() => {
 const getStageColorCls = (key) => {
 const map = {
 response_required: 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/20',
 waiting_for_support: 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/20',
 in_progress: 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/15 dark:text-blue-400 dark:border-blue-500/20',
 clarification_with_recipient: 'bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-500/15 dark:text-purple-400 dark:border-purple-500/20',
 solution_proposed: 'bg-cyan-50 text-cyan-700 border border-cyan-200 dark:bg-cyan-500/15 dark:text-cyan-400 dark:border-cyan-500/20',
 done: 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-500/15 dark:text-green-400 dark:border-green-500/20',
 };
 return map[key] || 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-primary,#1A1A2E)] border border-[var(--border-default,#E5E7EB)] dark:bg-[rgba(107,114,128,0.15)] dark:text-[var(--text-tertiary,#6B7280)] dark:border-[var(--border-default,#E5E7EB)]/20';
 };
 return (
 <span className={`text-xs font-medium px-3.5 py-1.5 rounded-full ${getStageColorCls(task.stage_key)}`}>
 {getStageLabel(task.stage_name)}
 </span>
 );
 })()}
 {/* SLA Pill — #1062 SSoT via getEffectiveSla() */}
 {(() => {
 const eff = getEffectiveSla(task, t);
 if (!eff) return null;
 return (
 <div className="flex gap-2 flex-wrap justify-end">
 <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full ${eff.pillCls}`}>
 <span className="relative flex h-1.5 w-1.5 shrink-0">
 {eff.pulse && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />}
 <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${eff.dotCls}`} />
 </span>
 {eff.label}
 </span>
 </div>
 );
 })()}
 </div>
 </div>
 </div>

 {/* Scrollable area: Description + Notes + Conversation */}
 <div className="flex-1 overflow-y-auto">
 {/* #1853 — Vorschlag der Daily Cases Analyse: Auto-Responder-Wiedereröffnung */}
 {analyseVorschlag && task.stage_key !== 'done' && (
 <div className="px-8 pt-4">
   <div className="rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-subtle,#F9FAFB)] px-4 py-3 flex items-start gap-3">
     <Info size={16} className="text-[var(--primary,#338edc)] mt-0.5 shrink-0" />
     <div className="flex-1 min-w-0">
       <div className="text-[13px] font-semibold text-[var(--text-primary,#1A1A2E)]">{t('cases.analyseVorschlag.title')}</div>
       <div className="text-[12px] text-[var(--text-secondary,#4B5563)] mt-0.5">{t('cases.analyseVorschlag.hint')}</div>
     </div>
     <button
       onClick={() => setStage('done')}
       className="px-3 py-1.5 rounded-md bg-[var(--primary,#338edc)] text-white text-[12px] font-medium shrink-0"
     >{t('cases.analyseVorschlag.confirm')}</button>
     <button
       onClick={() => {
         setAnalyseVorschlag(false);
         const u = new URL(window.location.href);
         u.searchParams.delete('aktion');
         window.history.replaceState({}, '', u.pathname + (u.search || '') + u.hash);
       }}
       className="px-3 py-1.5 rounded-md border border-[var(--border-default,#E5E7EB)] text-[12px] text-[var(--text-secondary,#4B5563)] shrink-0"
     >{t('cases.analyseVorschlag.dismiss')}</button>
   </div>
 </div>
 )}
 {/* #1279 FlowAI Vorschlags- & Approval-Panel (P1c) — "Lass FlowAI übernehmen" + Aktions-Kette */}
 <div className="px-8 pt-4">
   <FlowApprovalBanner
     caseId={task.is_oms ? null : task.id}
     omsCaseId={task.is_oms ? task.id : (task.odoo_case_id || null)}
     isOms={!!task.is_oms}
     t={t}
   />
 </div>
 {/* Description */}
 {/* #1220 — Beschreibungs-Card nur rendern wenn Beschreibung vorhanden.
     Bei manual-create mit "Initiale Mail senden"=ON oder is_internal landet der
     Body in case_comments — description bleibt leer und die Card faellt weg. */}
 {(task.description_html || (task.description && String(task.description).trim())) && (
 <div className="px-8 py-4 border-b border-[var(--border-table,#F3F4F6)] dark:border-[var(--border-table,#F3F4F6)]">
 <div className="text-[11px] font-semibold tracking-wide text-[var(--text-tertiary,#6B7280)] uppercase mb-2">{t('cases.problemDescription')}</div>
 {/* #1026 — Bevorzugt description_html (Backend-sanitized initialer Mail-Body mit Absätzen). Fallback: plain-text-description. */}
 {/* #1069 H3 — Original-anzeigen-Toggle: wenn description_full vorhanden (Quote-History/Forward-Block), Toggle rendern. */}
 <CaseDescriptionBody html={(showFullMap.description && task.description_full)
 ? DOMPurify.sanitize(task.description_full, PURIFY_CONFIG)
 : (task.description_html
   ? DOMPurify.sanitize(task.description_html, PURIFY_CONFIG)
   : normalizeHtml(task.description))} />
 {task.description_full && (
 <div className="mt-1.5">
 <button
 type="button"
 onClick={() => setShowFullMap(p => ({ ...p, description: !p.description }))}
 className="text-[11px] font-medium text-[#338edc] hover:text-[#2876c0] underline-offset-2 hover:underline transition-colors"
 >
 {showFullMap.description ? t('cases.showLessEmail') : t('cases.showFullEmail')}
 </button>
 </div>
 )}
 </div>
 )}

 {/* Pinned Internal Notes */}
 {interneNotizen.length > 0 && (
 <div className="px-8 pt-4 pb-2">
 <div className="flex items-center gap-2 mb-2">
 <Pin className="w-3.5 h-3.5 text-amber-500" />
 <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
 {t('cases.interneNotizen')} ({interneNotizen.length})
 </span>
 </div>
 <div className="flex flex-col gap-2">
 {interneNotizen.slice(0, showAllNotizen ? undefined : 2).map(notiz => {
 const author = notiz.author_id ? notiz.author_id[1] : (notiz.email_from || 'System');
 return (
 <div key={notiz.id} className="rounded-lg border-l-[3px] border-amber-400 bg-amber-50 dark:bg-amber-500/10 px-3 py-2.5">
 <div className="flex items-center justify-between mb-1">
 <span className="text-xs font-medium text-amber-700 dark:text-amber-300 inline-flex items-center gap-1">
 <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
 {author}
 </span>
 <span className="text-[10px] text-amber-500 dark:text-amber-400/70">{relTime(notiz.date, t)}</span>
 </div>
 <div className="text-sm text-amber-900 dark:text-amber-100 whitespace-pre-wrap [&_p]:mb-1 [&_p:last-child]:mb-0"
 dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notiz.body, PURIFY_CONFIG) }} />
 </div>
 );
 })}
 </div>
 {interneNotizen.length > 2 && (
 <button
 onClick={() => setShowAllNotizen(v => !v)}
 className="mt-1.5 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 font-medium"
 >
 {showAllNotizen
 ? t('cases.wenigerAnzeigen')
 : `+${interneNotizen.length - 2} ${t('cases.weitereNotizen')}`
 }
 </button>
 )}
 </div>
 )}

 {/* Linked Tasks */}
 {linkedTasks.length > 0 && (
 <div className="mx-8 mt-4 border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
 <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--bg-inset,#F8F9FC)] border-b border-[var(--border-default,#E5E7EB)]">
 <div className="flex items-center gap-2">
 <CheckSquare className="w-3.5 h-3.5 text-[var(--text-tertiary,#6B7280)]" />
 <span className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{t('cases.linkedTasks', 'Verknüpfte Aufgaben')}</span>
 <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
 {linkedTasks.filter(lt => lt.status !== 'erledigt').length} {t('cases.ltOpen', 'offen')}
 </span>
 </div>
 <button onClick={openTaskModal}
 className="text-xs font-medium text-[#338edc] hover:text-[#2876c0] flex items-center gap-1">
 + {t('cases.addTask', 'Aufgabe')}
 </button>
 </div>
 <div className="divide-y divide-[var(--border-table,#F3F4F6)]">
 {linkedTasks.map(lt => {
 const isDone = lt.status === 'erledigt';
 const dueDate = lt.due_date ? new Date(lt.due_date + 'T00:00:00') : null;
 const isOverdue = !isDone && dueDate && !isNaN(dueDate.getTime()) && dueDate < new Date(new Date().toDateString());
 const statusBadge = {
 offen: 'bg-blue-100 text-blue-700',
 in_bearbeitung: 'bg-amber-100 text-amber-700',
 erledigt: 'bg-green-100 text-green-700',
 verschoben: 'bg-amber-100 text-amber-700',
 };
 const statusLabel = { offen: t('cases.ltStatusOpen', 'Offen'), in_bearbeitung: t('cases.ltStatusWip', 'In Arbeit'), erledigt: t('cases.ltStatusDone', 'Erledigt'), verschoben: t('cases.ltStatusDeferred', 'Verschoben') };
 const initials = lt.assigned_name ? lt.assigned_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '';
 return (
 <div
 key={lt.id}
 onClick={() => setTaskSlideOverId(lt.id)}
 className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors ${isDone ? 'opacity-60' : ''}`}
 >
 <span className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${isOverdue ? 'bg-red-100 text-red-700' : (statusBadge[lt.status] || statusBadge.offen)}`}>
 {isOverdue ? t('cases.ltOverdue', 'Überfällig') : (statusLabel[lt.status] || lt.status)}
 </span>
 <span className={`text-sm flex-1 truncate hover:text-[#338edc] hover:underline ${isDone ? 'line-through text-[var(--text-tertiary,#6B7280)]' : 'text-[var(--text-primary,#1A1A2E)]'}`}>
 {lt.title}
 </span>
 {lt.assigned_name && (
 <span className="flex items-center gap-1.5 flex-shrink-0">
 <span className="w-5 h-5 rounded-full bg-[#338edc] text-white text-[9px] font-semibold flex items-center justify-center">{initials}</span>
 <span className="text-xs text-[var(--text-tertiary,#6B7280)]">{lt.assigned_name}</span>
 </span>
 )}
 {dueDate && !isNaN(dueDate.getTime()) && (
 <span className={`text-xs flex items-center gap-1 flex-shrink-0 ${isOverdue ? 'text-red-600 font-medium' : 'text-[var(--text-tertiary,#6B7280)]'}`}>
 <Clock className="w-3.5 h-3.5" /> {dueDate.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
 </span>
 )}
 </div>
 );
 })}
 </div>
 </div>
 )}

 {/* #1075: Zeiterfassung (#989) ist nun in der rechten Sidebar zwischen Anhänge und Erstellt platziert. */}

 {/* Tab-Bar (Konversation | Intern | Claims) — Konversation+Intern wenn Chatter aktiviert; Claims (#1246) wenn nicht OMS */}
 {(chatterEnabled || !task?.is_oms) && (
 <div className="px-8 pt-4">
 <div className="inline-flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)]">
 <button
 type="button"
 onClick={() => setActiveContentTab('konversation')}
 className={`px-4 py-1.5 rounded-[7px] text-xs font-semibold transition-colors ${
 activeContentTab === 'konversation'
 ? 'bg-white text-[var(--text-primary,#1A1A2E)] shadow-sm'
 : 'text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-primary,#1A1A2E)]'
 }`}
 >
 {t('cases.conversation')}
 {msgCount > 0 && <span className="ml-1.5 text-[var(--text-tertiary,#6B7280)]">({msgCount})</span>}
 </button>
 {chatterEnabled && (
 <button
 type="button"
 onClick={() => setActiveContentTab('intern')}
 className={`px-4 py-1.5 rounded-[7px] text-xs font-semibold transition-colors ${
 activeContentTab === 'intern'
 ? 'bg-white text-[var(--text-primary,#1A1A2E)] shadow-sm'
 : 'text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-primary,#1A1A2E)]'
 }`}
 >
 {t('cases.internTab', 'Intern')}
 </button>
 )}
 {!task?.is_oms && (
 <button
 type="button"
 onClick={() => setActiveContentTab('claims')}
 className={`px-4 py-1.5 rounded-[7px] text-xs font-semibold transition-colors ${
 activeContentTab === 'claims'
 ? 'bg-white text-[var(--text-primary,#1A1A2E)] shadow-sm'
 : 'text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-primary,#1A1A2E)]'
 }`}
 >
 {t('cases.claimsTab', 'Klärungen')}
 {(task?.counts?.linked_claims ?? linkedClaims.length) > 0 && (
 <span className="ml-1.5 text-[var(--text-tertiary,#6B7280)]">({task?.counts?.linked_claims ?? linkedClaims.length})</span>
 )}
 </button>
 )}
 </div>
 </div>
 )}

 {/* Intern-Tab: UniversalChatter */}
 {chatterEnabled && activeContentTab === 'intern' && (
 <div className="px-8 py-5">
 <UniversalChatter entityType="case" entityId={taskId} />
 </div>
 )}

 {/* Claims-Tab: Linked Claims (#1246) */}
 {!task?.is_oms && activeContentTab === 'claims' && (
 <div className="px-8 py-5">
 <LinkedEntityList
 rows={linkedClaims}
 loading={linkedClaimsLoading}
 entityType="claim"
 onOpen={(row) => detailNavigate(`/claims/${row.claim_id}`)}
 onUnlink={handleUnlinkClaim}
 onLinkClick={() => setLinkClaimModalOpen(true)}
 unlinking={unlinkingClaim}
 />
 </div>
 )}

 {/* Conversation */}
 <div ref={scrollRef} className={`px-8 py-5 ${activeContentTab !== 'konversation' ? 'hidden' : ''}`}>
 {!chatterEnabled && (
 <h3 className="text-[10px] font-bold text-content-tertiary uppercase tracking-widest mb-4 flex items-center gap-2">
 <span>💬 {t('cases.conversation')}</span>
 {msgCount > 0 && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-surface-inset text-[var(--text-secondary)]">{msgCount}</span>}
 </h3>
 )}
 {visibleMsgs.length === 0 ? (
 <div className="flex flex-col items-center justify-center text-content-tertiary opacity-40 py-10">
 <span className="text-3xl mb-2">💬</span>
 <span className="text-sm">{t('cases.noMessages')}</span>
 </div>
 ) : (
 <div className="space-y-1">
 {dayGroups.map((item, idx) => {
 if (item.type === 'sep') {
 return (
 <div key={`sep-${idx}`} className="flex items-center gap-3 py-3">
 <div className="flex-1 border-t border-line" />
 <span className="text-[10px] text-content-tertiary font-medium px-2">{fmtDayLabel(item.date)}</span>
 <div className="flex-1 border-t border-line" />
 </div>
 );
 }
 const m = item.msg;
 const author = m.author_id ? m.author_id[1] : (m.email_from || 'System');
 const authorId = m.author_id ? m.author_id[0] : null;
 const isMsgInternal = m.message_type === 'internal' || m.is_internal === true;
 const isSystem = m.message_type === 'system' || m.message_type === 'stage_notification' || m.message_type === 'notification';
 const isEmail = m.message_type === 'email' || m.message_type === 'email_inbound';
 const isOutbound = m.message_type === 'email_outbound';
 // Customer = LEFT (green), WeShip = RIGHT (blue)
 // Domain-based detection for OMS cases where message_type alone isn't enough
 const _agentDomains = ['@weship.eu', '@weship.at', '@dynaflow.at'];
 const _isAgentEmail = (e) => e && _agentDomains.some(d => e.toLowerCase().includes(d));
 const isFromCustomer = (() => {
 if (isMsgInternal) return false;
 // Local case: explicit types
 if (m.message_type === 'email_inbound') return true;
 if (m.message_type === 'email_outbound' || m.message_type === 'manual') return false;
 // OMS 'email' type = customer email (unless from WeShip domain)
 if (m.message_type === 'email') return !_isAgentEmail(m.email_from);
 // OMS 'comment': use email_from domain to distinguish
 if (m.message_type === 'comment') {
 if (_isAgentEmail(m.email_from)) return false;
 if (m.email_from) return true;
 // No email_from: compare with task.from_email if available
 if (task?.from_email && m.author_id?.[1] && m.author_id[1].toLowerCase().includes(task.from_email.split('@')[0]?.toLowerCase())) return true;
 return false;
 }
 return false;
 })();
 const alignRight = isMsgInternal || !isFromCustomer;

 if (isSystem) {
 return (
 <div key={m.id} className="flex items-center gap-3 my-4">
 <div className="flex-1 h-px bg-[var(--bg-inset,#F8F9FC)] dark:bg-[var(--bg-inset,#F8F9FC)]" />
 <span className="text-[11px] text-[var(--text-tertiary,#6B7280)] dark:text-[var(--text-secondary,#4B5563)] whitespace-nowrap">
 {m.body?.replace(/<[^>]+>/g, '')}
 </span>
 <div className="flex-1 h-px bg-[var(--bg-inset,#F8F9FC)] dark:bg-[var(--bg-inset,#F8F9FC)]" />
 </div>
 );
 }

 return (
 <div key={m.id} className={`flex gap-3 mb-4 ${alignRight ? 'flex-row-reverse' : 'flex-row'}`}>
 {/* #1059 Punkt 11 — Avatar-Bild aus users.avatar_path wenn interner Verfasser, sonst Initialen */}
 {m.author_user_id && m.author_avatar_url ? (
 <img src={m.author_avatar_url} alt={author}
 className="w-8 h-8 rounded-full object-cover flex-shrink-0"
 onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = 'flex'); }} />
 ) : null}
 <div style={{ display: m.author_user_id && m.author_avatar_url ? 'none' : 'flex' }}
 className={`w-8 h-8 rounded-full items-center justify-center flex-shrink-0 text-[11px] font-medium ${
 isMsgInternal ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400'
 : alignRight ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400'
 : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400'
 }`}>
 {initials(author)}
 </div>
 <div className="max-w-[75%]">
 <div className={`flex items-center gap-2 mb-1 ${alignRight ? 'justify-end' : ''}`}>
 {!alignRight && <span className="text-xs font-medium text-[var(--text-primary,#1A1A2E)] dark:text-content">{author}</span>}
 {isEmail && !isMsgInternal && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] dark:bg-[rgba(107,114,128,0.1)] dark:text-[var(--text-tertiary,#6B7280)]">{t('cases.email')}</span>}
 {/* #1335 — An-Empfänger (To) pro Nachricht, analog CC/BCC. Leeres Feld wird nicht angezeigt. */}
 {Array.isArray(m.to) && m.to.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] dark:bg-[rgba(107,114,128,0.1)] dark:text-[var(--text-tertiary,#6B7280)]" title={m.to.join(', ')}>{t('cases.to')}: {m.to.length === 1 ? m.to[0] : `${m.to.length}`}</span>}
 {/* #1058 Punkt 2: CC/BCC im Verlauf anzeigen wenn vorhanden */}
 {Array.isArray(m.cc) && m.cc.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] dark:bg-[rgba(107,114,128,0.1)] dark:text-[var(--text-tertiary,#6B7280)]" title={m.cc.join(', ')}>CC: {m.cc.length === 1 ? m.cc[0] : `${m.cc.length}`}</span>}
 {Array.isArray(m.bcc) && m.bcc.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] dark:bg-[rgba(107,114,128,0.1)] dark:text-[var(--text-tertiary,#6B7280)]" title={m.bcc.join(', ')}>BCC: {m.bcc.length === 1 ? m.bcc[0] : `${m.bcc.length}`}</span>}
 {/* #1066 — Send-Status-Pille basiert auf send_status statt nur comment_type */}
 {isOutbound && (() => {
 const ss = m.send_status;
 if (ss === 'sent') {
 const tooltip = m.send_recipient
 ? `${t('cases.send.sentTooltip')}: ${m.send_recipient}${m.sent_at ? ' · ' + new Date(m.sent_at).toLocaleString() : ''}`
 : t('cases.send.sentTooltip');
 return <span title={tooltip} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 font-medium">{t('cases.send.sent')}</span>;
 }
 if (ss === 'failed') {
 const tooltip = m.send_error ? `${t('cases.send.errorPrefix')}: ${m.send_error}` : t('cases.send.failedTooltip');
 return (
 <span className="inline-flex items-center gap-1">
 <span title={tooltip} className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400 font-medium">{t('cases.send.failed')}</span>
 {m.comment_id && (
 <button
 type="button"
 disabled={retryingCommentId === m.comment_id}
 onClick={() => retrySend(m.comment_id)}
 className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:hover:bg-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
 title={t('cases.send.retryTooltip')}
 >{retryingCommentId === m.comment_id ? '...' : t('cases.send.retryButton')}</button>
 )}
 </span>
 );
 }
 if (ss === 'pending') {
 return <span title={t('cases.send.pendingTooltip')} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 font-medium">{t('cases.send.pending')}</span>;
 }
 if (ss === 'no_recipient') {
 const tooltip = m.send_error || t('cases.send.noRecipientTooltip');
 return <span title={tooltip} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 dark:bg-slate-500/10 dark:text-slate-400 font-medium">{t('cases.send.noRecipient')}</span>;
 }
 if (ss === 'cancelled') {
 const tooltip = m.sent_at ? `${t('cases.send.cancelledTooltip')} · ${new Date(m.sent_at).toLocaleString()}` : t('cases.send.cancelledTooltip');
 return <span title={tooltip} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 line-through dark:bg-slate-500/10 dark:text-slate-400 font-medium">{t('cases.send.cancelled')}</span>;
 }
 // Legacy / NULL: kein Pille (statt fälschlich „Gesendet")
 return null;
 })()}
 {isMsgInternal && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 font-medium">{t('cases.commentInternal')}</span>}
 <span className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{relTime(m.date, t)}</span>
 {alignRight && <span className="text-xs font-medium text-[var(--text-primary,#1A1A2E)] dark:text-content">{author}</span>}
 </div>
 <MessageBody
 html={msgBodyHtml.get(m.id) || ''}
 className={`text-[13px] leading-relaxed p-3.5 text-content ${m.body_format === 'html' ? '' : 'whitespace-pre-wrap'} [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_li]:mb-0.5 [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:my-2 [&_blockquote]:opacity-70 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:my-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:my-2 [&_h3]:text-sm [&_h3]:font-medium [&_h3]:my-1.5 [&_strong]:font-semibold [&_em]:italic [&_a]:underline [&_img]:max-w-full [&_img]:h-auto [&_img]:my-1 [&_table]:border-collapse [&_table]:my-2 [&_th]:border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-[var(--bg-inset,#F8F9FC)] [&_td]:border [&_td]:px-2 [&_td]:py-1 [&_hr]:my-3 [&_hr]:border-t ${
 alignRight ? 'rounded-l-xl rounded-br-xl' : 'rounded-r-xl rounded-bl-xl'
 } ${
 isMsgInternal
 ? 'bg-amber-50/50 dark:bg-[rgba(245,158,11,0.06)] border border-amber-200 dark:border-amber-900'
 : alignRight
 ? 'bg-blue-50/60 dark:bg-[rgba(51,142,220,0.08)] border border-blue-100 dark:border-blue-900 [&_a]:text-blue-500 [&_a]:underline'
 : 'bg-emerald-50/60 dark:bg-[rgba(52,211,153,0.08)] border border-emerald-200 dark:border-emerald-900 [&_a]:text-blue-500 [&_a]:underline'
 }`}
 borderLeft={isMsgInternal ? '3px dashed #FBBF24' : !alignRight ? '3px solid #34D399' : undefined}
 borderRight={!isMsgInternal && alignRight ? '3px solid #60A5FA' : undefined}
 />
 {/* #1026 — Toggle "Vollständige E-Mail anzeigen" wenn body_full ≠ body (also wenn Quote-History gestrippt wurde). */}
 {m.body_full && m.body_full !== m.body && (
 <div className={`mt-1.5 ${alignRight ? 'text-right' : ''}`}>
 <button
 type="button"
 onClick={() => setShowFullMap(p => ({ ...p, [m.id]: !p[m.id] }))}
 className="text-[11px] font-medium text-[#338edc] hover:text-[#2876c0] underline-offset-2 hover:underline transition-colors"
 >
 {showFullMap[m.id] ? t('cases.showLessEmail') : t('cases.showFullEmail')}
 </button>
 </div>
 )}
 {/* #1241 — Original-E-Mail Toggle (öffnet Modal mit Voll-HTML im Sandboxed-Iframe) */}
 {isEmail && (m.body_html || m.body) && (
 <div className={`mt-1 ${alignRight ? 'text-right' : ''}`}>
 <button
 type="button"
 onClick={() => setMailOriginalModal({ open: true, html: m.body_html || m.body_full || m.body || '', subject: m.subject || '' })}
 className="text-[10px] px-2 py-0.5 rounded border border-line text-content-tertiary hover:text-content hover:bg-surface-hover"
 title={t('cases.viewOriginalMail') || 'Original-E-Mail anzeigen'}
 >
 📧 {t('cases.originalMail') || 'Original anzeigen'}
 </button>
 </div>
 )}
 {m.ai_classification === 'gratitude_only' && (
 <div className={`flex items-center gap-1 mt-1 text-[10px] italic text-[var(--text-tertiary)] ${alignRight ? 'justify-end' : ''}`}>
 <span>🤖</span> <span>{t('cases.aiGratitudeHint')}</span>
 </div>
 )}
 {m.ai_classification === 'action_needed' && (
 <div className={`flex items-center gap-1 mt-1 text-[10px] italic text-primary-500 dark:text-primary-400 ${alignRight ? 'justify-end' : ''}`}>
 <span>🤖</span> <span>{t('cases.aiActionHint')}</span>
 </div>
 )}
 {/* #1154 — Anhänge dieser Nachricht als Chips (zusätzlich zur Sidebar-Liste) */}
 {Array.isArray(m.attachments) && m.attachments.length > 0 && (
 <div className={`flex flex-wrap gap-1.5 mt-2 ${alignRight ? 'justify-end' : ''}`}>
 {m.attachments.map(att => {
 const isOms = att.source === 'oms';
 const downloadUrl = isOms
 ? `${API}/api/cases/oms-attachments/${att.oms_id}/download`
 : `${API}/api/cases/attachments/${att.id}/download`;
 const inlineUrl = isOms
 ? `${API}/api/cases/oms-attachments/${att.oms_id}/inline`
 : downloadUrl;
 const mime = att.mime_type || '';
 const isImage = mime.startsWith('image/');
 const isPdf = mime === 'application/pdf' || (att.original_name || '').toLowerCase().endsWith('.pdf');
 const previewable = isImage || isPdf;
 return (
 <div key={`${att.source || 'x'}_${att.id || att.oms_id}`}
 className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-line bg-[var(--bg-inset,#F8F9FC)] dark:bg-[var(--bg-card)] hover:border-[#338edc]/60 text-[11px] max-w-[260px]">
 {isImage ? (
 <button onClick={() => openLightbox(inlineUrl, 'image')} className="w-4 h-4 rounded overflow-hidden flex-shrink-0">
 <img src={inlineUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
 </button>
 ) : isPdf ? (
 <FileText className="w-3 h-3 text-red-500 flex-shrink-0" />
 ) : (
 <Paperclip className="w-3 h-3 text-content-tertiary flex-shrink-0" />
 )}
 {previewable ? (
 <button onClick={() => openLightbox(inlineUrl, isPdf ? 'pdf' : 'image')}
 className="text-[#338edc] hover:underline truncate" title={att.original_name}>
 {att.original_name}
 </button>
 ) : (
 <a href={downloadUrl} className="text-[#338edc] hover:underline truncate"
 target="_blank" rel="noopener noreferrer" title={att.original_name}>
 {att.original_name}
 </a>
 )}
 <a href={downloadUrl} download={att.original_name || ''}
 title={t('cases.download')}
 className="ml-0.5 text-[var(--text-tertiary)] hover:text-[#338edc] flex-shrink-0">
 <FileText className="w-3 h-3" />
 </a>
 </div>
 );
 })}
 </div>
 )}
 </div>
 </div>
 );
 })}
 <div ref={chatEndRef} />
 </div>
 )}
 </div>
 </div>{/* /scrollable area */}

 {/* Compose area — sticky at bottom, outside scroll */}
 <div className="flex-shrink-0">
 {omsCredsMissing && (
 <div className="px-8 py-2 bg-amber-500/10 border-t border-amber-500/20">
 <div className="flex items-center gap-2 text-xs text-amber-500">
 <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
 <span>{t('cases.omsCredentialsMissing')}</span>
 <a href="/profil" className="ml-auto text-primary-400 hover:underline whitespace-nowrap">{t('cases.omsCredentialsLink')}</a>
 </div>
 </div>
 )}
 <div
 className={`relative border-t px-8 py-4 transition-colors ${
 dragActive
 ? 'border-[#338edc] bg-[#338edc]/5'
 : 'border-[var(--border-default,#E5E7EB)] dark:border-line bg-[rgba(249,250,251,0.5)] dark:bg-surface-card'
 }`}
 onDragEnter={handleDragEnter}
 onDragLeave={handleDragLeave}
 onDragOver={handleDragOver}
 onDrop={handleDrop}>
 {dragActive && (
 <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none border-2 border-dashed border-[#338edc] rounded-md bg-[#338edc]/10">
 <div className="text-[#338edc] font-semibold text-sm flex items-center gap-2">
 <Paperclip className="w-4 h-4" />
 {t('cases.dropFilesHere')}
 </div>
 </div>
 )}
 {pendingFiles.length > 0 && (
 <div className="flex flex-wrap gap-1.5 mb-2">
 {pendingFiles.map(f => (
 <span key={`${f.name}-${f.size}`} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-surface-inset border border-line text-[10px] text-content">
 <Paperclip className="w-3 h-3 text-content-tertiary" />
 {f.name}
 <span className="text-content-tertiary">{f.size < 1024 ? `${f.size} B` : f.size < 1024 * 1024 ? `${(f.size / 1024).toFixed(1)} KB` : `${(f.size / 1024 / 1024).toFixed(1)} MB`}</span>
 <button onClick={() => setPendingFiles(prev => prev.filter(x => !(x.name === f.name && x.size === f.size)))} className="text-content-tertiary hover:text-red-500">
 <X className="w-3 h-3" />
 </button>
 </span>
 ))}
 </div>
 )}
 <div className="space-y-2">
 {/* #1058 Punkt 2: CC/BCC nur bei E-Mail-Cases und nicht bei interner Notiz */}
 {task?.source === 'email' && !isInternal && (
 <div className="space-y-1.5">
 {/* #1114 — An:-Feld editierbar, vor-ausgefüllt mit Resolved Recipient */}
 <div className="flex items-center gap-2">
 <span className="text-[11px] font-medium text-content-tertiary w-8 flex-shrink-0">{t('cases.to')}</span>
 <input
 type="email"
 value={toInput}
 onChange={e => setToInput(e.target.value)}
 onBlur={async () => {
   const normalized = String(toInput || '').trim().toLowerCase();
   const current = String(resolvedRecipient.address || '').trim().toLowerCase();
   if (!normalized || normalized === current) return;
   if (!/^[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(normalized)) return;
   try {
     await api(`/api/cases/${taskId}`, { method: 'PATCH', body: JSON.stringify({ manual_customer_email: normalized }) });
     setResolvedRecipient({ address: normalized, source: 'manual', name: resolvedRecipient.name });
   } catch (e) {
     setToast({ message: e.message, type: 'error' });
   }
 }}
 placeholder={t('cases.toPlaceholder')}
 className="flex-1 text-xs px-2.5 py-1.5 rounded-[8px] border border-[var(--border-default,#E5E7EB)] dark:border-line bg-white dark:bg-surface-inset text-content placeholder:text-content-tertiary focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20"
 />
 {resolvedRecipient.source && (
 <span
   className="text-[10px] text-content-tertiary whitespace-nowrap"
   title={t('cases.toResolvedSourceLabel').replace('{source}', resolvedRecipient.source)}
 >
   {resolvedRecipient.source === 'manual'
     ? t('cases.toResolvedSource.manual')
     : resolvedRecipient.source === 'kunde'
       ? t('cases.toResolvedSource.kunde')
       : t('cases.toResolvedSource.inbound')}
 </span>
 )}
 {/* #1131 Idee 2: CC/BCC als Links — Default hidden */}
 {!showCc && (
 <button type="button" onClick={() => setShowCc(true)} className="text-[11px] text-[#338edc] hover:underline whitespace-nowrap">
 {t('cases.addCc')}
 </button>
 )}
 {!showBcc && (
 <button type="button" onClick={() => setShowBcc(true)} className="text-[11px] text-[#338edc] hover:underline whitespace-nowrap">
 {t('cases.addBcc')}
 </button>
 )}
 </div>
 {showCc && (
 <div className="flex items-center gap-2">
 <span className="text-[11px] font-medium text-content-tertiary w-8 flex-shrink-0">{t('cases.cc')}</span>
 <input
 type="text"
 value={ccInput}
 onChange={e => setCcInput(e.target.value)}
 placeholder={t('cases.ccPlaceholder')}
 className="flex-1 text-xs px-2.5 py-1.5 rounded-[8px] border border-[var(--border-default,#E5E7EB)] dark:border-line bg-white dark:bg-surface-inset text-content placeholder:text-content-tertiary focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20"
 />
 <button type="button" onClick={() => { setShowCc(false); setCcInput(''); }} className="text-[11px] text-content-tertiary hover:text-red-500 whitespace-nowrap">
 {t('cases.hideCc')}
 </button>
 </div>
 )}
 {showBcc && (
 <div className="flex items-center gap-2">
 <span className="text-[11px] font-medium text-content-tertiary w-8 flex-shrink-0">{t('cases.bcc')}</span>
 <input
 type="text"
 value={bccInput}
 onChange={e => setBccInput(e.target.value)}
 placeholder={t('cases.bccPlaceholder')}
 className="flex-1 text-xs px-2.5 py-1.5 rounded-[8px] border border-[var(--border-default,#E5E7EB)] dark:border-line bg-white dark:bg-surface-inset text-content placeholder:text-content-tertiary focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20"
 />
 <button type="button" onClick={() => { setShowBcc(false); setBccInput(''); }} className="text-[11px] text-content-tertiary hover:text-red-500 whitespace-nowrap">
 {t('cases.hideBcc')}
 </button>
 </div>
 )}
 </div>
 )}
 {draftRestoreInfo && (
 <div className="flex items-center gap-2 px-3 py-1.5 rounded-[8px] bg-primary-100/60 dark:bg-primary-900/20 text-[11px] text-[#338edc] dark:text-primary-300 border border-primary-200/50 dark:border-primary-800/30">
 <span>📝</span>
 <span>
 {draftRestoreInfo.minutesAgo < 1
 ? t('cases.draftRestoredJustNow')
 : t('cases.draftRestoredMinutes').replace('{minutes}', draftRestoreInfo.minutesAgo)}
 </span>
 <button type="button" onClick={() => setDraftRestoreInfo(null)} className="ml-auto text-[10px] opacity-60 hover:opacity-100">×</button>
 </div>
 )}
 {task?.source === 'email' ? (
 <TiptapEditor
 onSubmit={sendMsg}
 isInternal={isInternal}
 placeholder={isInternal ? t('cases.editorPlaceholderInternal') : t('cases.editorPlaceholder')}
 t={t}
 editorRefCb={e => { editorRef.current = e; setTiptapEditor(e); }}
 allowInlineImages={task?.source === 'email'}
 onInlineImageUpload={uploadInlineImage}
 onInlineImageError={msg => setToast({ message: msg, type: 'error' })}
 onNonImageFiles={handleFileUpload}
 onDropConsumed={() => { dragCounterRef.current = 0; setDragActive(false); }}
 />
 ) : (
 <textarea ref={taRef} value={newMsg}
 onChange={e => { setNewMsg(e.target.value); adjustTa(); }}
 onPaste={e => {
 // #1751 — eingefügte Dateien/Bilder → Pending-Chips (wie Drop)
 const files = Array.from(e.clipboardData?.files || []);
 if (files.length) { e.preventDefault(); handleFileUpload(files); }
 }}
 onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendMsg(); }}
 onBlur={() => flushDraftSave()}
 placeholder={isInternal ? t('cases.internalNotePlaceholder') : t('cases.customerMsgPlaceholder')}
 className={`w-full border rounded-[10px] px-4 py-3 text-sm text-content resize-y focus:outline-none focus:ring-2 focus:ring-[#338edc]/10 min-h-[80px] max-h-[400px] ${
 isInternal
 ? 'bg-amber-500/5 border-amber-500/20 focus:border-amber-500/40 placeholder:text-amber-500/40'
 : 'bg-white dark:bg-surface-inset border-[var(--border-default,#E5E7EB)] dark:border-line focus:border-[#338edc] placeholder:text-content-tertiary'
 }`}
 rows={3} />
 )}
 {/* #1131 Idee 1: Signatur kollabiert — 1-Line-Hint, click toggles Preview */}
 {task?.source === 'email' && !isInternal && userSig?.signatur_use_cases && userSig?.signatur_html?.trim() && (
 <div className="rounded-[10px] border border-dashed border-[var(--border-default,#E5E7EB)] dark:border-line bg-[var(--bg-inset,#F8F9FC)] dark:bg-surface-inset">
 <button type="button" onClick={() => setShowSignaturePreview(v => !v)}
   className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-[var(--bg-hover,#F3F4F6)] dark:hover:bg-surface-hover rounded-[10px] transition-colors">
 <span className="text-[11px] text-content-tertiary truncate">
 <span className="font-medium">{t('cases.signaturePreviewLabel')}:</span> {t('cases.signatureHintCollapsed')}
 </span>
 <span className="text-[10px] text-[#338edc] hover:underline whitespace-nowrap flex-shrink-0">
 {showSignaturePreview ? t('cases.signatureHidePreview') : t('cases.signatureShowPreview')}
 </span>
 </button>
 {showSignaturePreview && (
 <div className="px-3 pb-2 pt-1 border-t border-[var(--border-default,#E5E7EB)]/50 dark:border-line/50">
 <div className="text-[12px] text-content-secondary [&_p]:my-0 [&_a]:underline" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(userSig.signatur_html, PURIFY_CONFIG) }} />
 </div>
 )}
 </div>
 )}
 <div className="flex items-center justify-between">
 <button onClick={() => setIsInternal(!isInternal)}
 className="flex items-center gap-1.5 py-1 select-none" title={isInternal ? t('cases.internalNote') : t('cases.toCustomer')}>
 <span className={`w-4 h-4 rounded flex items-center justify-center border transition-colors ${isInternal ? 'bg-amber-500 border-amber-500' : 'border-line-input bg-surface-input'}`}>
 {isInternal && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
 </span>
 <span className={`text-xs font-medium ${isInternal ? 'text-amber-500' : 'text-content-tertiary'}`}>{t('cases.intern')}</span>
 </button>
 <div className="flex items-center gap-1.5">
 {task?.source === 'email' && (
 <TemplateDropdown caseId={taskId} onInsert={(html) => {
 if (editorRef.current) editorRef.current.commands?.insertContent(html);
 else setNewMsg(prev => prev + html);
 }} t={t} />
 )}
 <button onClick={() => fileInputRef.current?.click()}
 className="flex-shrink-0 p-2 rounded-[10px] text-content-tertiary hover:text-content hover:bg-surface-hover transition-colors"
 title={t('cases.addAttachment')}>
 <Paperclip className="w-4 h-4" />
 </button>
 <input ref={fileInputRef} type="file" multiple className="hidden"
 onChange={e => { handleFileUpload(e.target.files); e.target.value = ''; }} />
 <button onClick={sendMsg} disabled={sending}
 className="flex-shrink-0 px-4 py-2 rounded-[10px] text-white text-xs font-semibold flex items-center gap-1.5 transition-all disabled:opacity-40 hover:brightness-110"
 style={{ backgroundColor: isInternal ? '#6B7280' : '#338edc' }}
 title={isInternal ? t('cases.commentInternal') : t('cases.toCustomer')}>
 {sending
 ? <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
 : <Send className="w-3.5 h-3.5" />
 }
 {!sending && <span>{t('cases.send')}</span>}
 </button>
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>{/* /left column */}

 {/* Right sidebar — 4 Cards */}
 <div className="w-[380px] flex-shrink-0 min-h-0 border-l border-[var(--border-table,#F3F4F6)] dark:border-[var(--border-table,#F3F4F6)] overflow-y-auto bg-white dark:bg-[var(--bg-card)]">
 <div className="px-5 py-4 space-y-4">

 {/* ─── Card 1: SLA Status ─── */}
 {(task.sla_klasse || task.sla_stunden || task.date_deadline || task.sla_next_response_deadline) && (() => {
 const esc = task.sla_escalation_level || 'none';
 const dot = escalationDot(esc);
 const isPaused = task.sla_pausiert || !!task.sla_paused_at;
 const nextMin = deadlineToMinutes(task.sla_next_response_deadline);
 const verbl = nextMin != null ? nextMin : task.sla_verbleibend_min;
 const isOverdue = verbl != null && verbl < 0;
 const urgency = slaUrgency(verbl, task.sla_stunden, esc);

 // First response
 const frMet = task.sla_first_response_met;
 const frDeadline = task.sla_first_response_deadline;
 const frMin = deadlineToMinutes(frDeadline);
 const frOverdue = frMet === false || (frMet == null && frMin != null && frMin < 0);
 const frUrgency = frMet === true ? 'ok' : frMet === false ? 'critical' : slaUrgency(frMin, task.sla_stunden, 'none');

 // Next response / general SLA
 const deadlineDate = task.sla_next_response_deadline || task.sla_deadline || task.date_deadline;
 const fmtDeadlineFull = (d) => d ? new Date(d).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' }) + ', ' + new Date(d).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : null;
 const pausedMin = task.sla_paused_duration_minutes || 0;

 const SlaDot = ({ color }) => {
 const cls = color === 'red' ? 'bg-red-500' : color === 'amber' ? 'bg-amber-500' : 'bg-emerald-500';
 const ping = color === 'red';
 return (
 <span className="relative flex h-2 w-2 flex-shrink-0">
 {ping && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${cls} opacity-75`} />}
 <span className={`relative inline-flex rounded-full h-2 w-2 ${cls}`} />
 </span>
 );
 };

 const dotColor = (urg) => urg === 'critical' ? 'red' : urg === 'warning' ? 'amber' : 'green';

 return (
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-table,#F3F4F6)] dark:border-[var(--border-default,#E5E7EB)] rounded-xl p-4">
 <div className="flex items-center justify-between mb-4">
 <h4 className="text-[11px] font-semibold uppercase tracking-[0.8px]" style={{ color: 'var(--text-tertiary)' }}>
 {t('cases.detail.sla')}
 </h4>
 <SlaKlasseBadge task={task} />
 </div>

 {/* Erste Antwort */}
 <div className="flex items-start gap-2.5 mb-3.5">
 <SlaDot color={frMet === true ? 'green' : frOverdue ? 'red' : dotColor(frUrgency)} />
 <div className="flex-1 min-w-0">
 <div className="text-sm font-semibold text-content">{t('cases.detail.firstResponse')}</div>
 {frMet === true ? (
 <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
 {t('cases.detail.firstResponseMet')}
 </div>
 ) : frDeadline ? (
 <>
 <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
 {t('cases.detail.dueBy')} {fmtDeadlineFull(frDeadline)}
 </div>
 {frOverdue && <div className="text-xs text-red-500 font-medium mt-0.5">{slaTimeReadable(Math.abs(frMin))} {t('cases.detail.overdue')}</div>}
 </>
 ) : (
 <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>—</div>
 )}
 </div>
 </div>

 {/* Antwort-SLA */}
 <div className="flex items-start gap-2.5">
 <SlaDot color={isPaused ? 'amber' : isOverdue ? 'red' : dotColor(urgency)} />
 <div className="flex-1 min-w-0">
 <div className="text-sm font-semibold text-content">{t('cases.detail.responseSla')}</div>
 {deadlineDate ? (
 <>
 <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
 {t('cases.detail.dueBy')} {fmtDeadlineFull(deadlineDate)}
 </div>
 {isOverdue && !isPaused && <div className="text-xs text-red-500 font-medium mt-0.5">{slaTimeReadable(Math.abs(verbl))} {t('cases.detail.overdue')}</div>}
 </>
 ) : (
 <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>—</div>
 )}
 </div>
 </div>

 {isPaused && (
 <div className="flex items-center gap-1.5 mt-3 text-xs text-amber-500 font-medium">
 <Pause className="w-3.5 h-3.5" />
 {pausedMin > 0
 ? <>{t('cases.detail.paused')} {slaTimeReadable(pausedMin)}</>
 : task.sla_paused_at
 ? <>{t('cases.detail.pausedSince')} {new Date(task.sla_paused_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</>
 : t('cases.detail.pausedShort')
 }
 </div>
 )}

 {/* Progress bar */}
 {task.sla_stunden && !isPaused && (
 <div className="mt-3">
 <SlaProgressBar task={task} />
 </div>
 )}
 </div>
 );
 })()}

 {/* ─── Card 2: Status + Zuweisung + Metadaten ─── */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-4 space-y-4">
 {/* Status */}
 <div>
 <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block mb-1.5" style={{ color: 'var(--text-tertiary)' }}>{t('cases.detail.status')}</label>
 <select className="w-full bg-[var(--bg-input)] border rounded-[10px] px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-[#338edc]/10 focus:border-[#338edc] transition-colors"
 style={{ borderColor: 'var(--border-input)' }}
 value={task.stage_key || ''} onChange={e => setStage(e.target.value)}>
 {stages.map(s => <option key={s.key} value={s.key}>{getStageLabel ? getStageLabel(s.name) : s.name}</option>)}
 </select>
 </div>

 {/* Zugewiesen */}
 <div>
 <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block mb-1.5" style={{ color: 'var(--text-tertiary)' }}>{t('cases.detail.assigned')}</label>
 <select className="w-full bg-[var(--bg-input)] border rounded-[10px] px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-[#338edc]/10 focus:border-[#338edc] transition-colors"
 style={{ borderColor: 'var(--border-input)' }}
 value={task.assigned_users?.[0]?.id || ''} onChange={e => setAssign(e.target.value)}>
 <option value="">{t('cases.detail.nobody')}</option>
 {omsUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
 </select>
 {task.vertretung_info?.is_deputy && (
 <div className="flex items-center gap-1.5 mt-1.5 px-2.5 py-1.5 rounded-lg bg-amber-500/5 border border-amber-500/15">
 <Users className="w-3 h-3 text-amber-500 flex-shrink-0" />
 <span className="text-[11px] text-[var(--text-secondary)]">
 {t('cases.youAreDeputy')}
 </span>
 </div>
 )}
 </div>

 {/* Priorität */}
 <div>
 <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block mb-1.5" style={{ color: 'var(--text-tertiary)' }}>{t('cases.detail.priority')}</label>
 <div><Stars value={task.priority} onChange={setPrio} /></div>
 </div>

 {/* Kategorie */}
 <div>
 <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block mb-1.5" style={{ color: 'var(--text-tertiary)' }}>{t('cases.detail.category')}</label>
 <select className="w-full bg-[var(--bg-input)] border rounded-[10px] px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-[#338edc]/10 focus:border-[#338edc] transition-colors"
 style={{ borderColor: 'var(--border-input)' }}
 value={task.category_id || ''}
 onChange={async e => {
 const catId = e.target.value || null;
 try {
 await api(`/api/cases/${task.id}/category${task?.source === 'oms' ? '?src=oms' : ''}`, { method: 'PUT', body: { category_id: catId ? Number(catId) : null } });
 await load(true);
 onRefresh?.();
 } catch {}
 }}>
 <option value="">{t('cases.detail.noCategory')}</option>
 {allCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
 </select>
 </div>

 {/* Tags */}
 <div>
 <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block mb-1.5" style={{ color: 'var(--text-tertiary)' }}>{t('cases.detail.tags')}</label>
 <div className="flex flex-wrap gap-1.5">
 {(task.local_tags || []).map(tg => (
 <TagPill key={tg.id} tag={tg} onRemove={async () => {
 try {
 await api(`/api/cases/${task.id}/tags/${tg.id}${task?.source === 'oms' ? '?src=oms' : ''}`, { method: 'DELETE' });
 await load(true);
 onRefresh?.();
 onTagsChange?.();
 } catch {}
 }} />
 ))}
 <TagAddDropdown allTags={allTags} assignedIds={(task.local_tags || []).map(tg => tg.id)}
 onAdd={async (tagId) => {
 try {
 await api(`/api/cases/${task.id}/tags/${tagId}${task?.source === 'oms' ? '?src=oms' : ''}`, { method: 'POST' });
 await load(true);
 onRefresh?.();
 onTagsChange?.();
 } catch {}
 }}
 onCreate={async (name) => {
 try {
 const res = await api('/api/cases/tags', { method: 'POST', body: { name, color: 'gray' } });
 if (res.tag?.id) {
 await api(`/api/cases/${task.id}/tags/${res.tag.id}${task?.source === 'oms' ? '?src=oms' : ''}`, { method: 'POST' });
 await load(true);
 onRefresh?.();
 onTagsChange?.();
 }
 } catch {}
 }} t={t} />
 </div>
 </div>

 {/* Attachments — #1058 Punkt 6: Hover-Pattern + Lightbox + Download-All */}
 {task.attachments?.length > 0 && (
 <div>
 <div className="flex items-center justify-between mb-1.5">
 <label className="text-[11px] font-semibold uppercase tracking-[0.8px]" style={{ color: 'var(--text-tertiary)' }}>
 {t('cases.attachments')} ({task.attachments.length})
 </label>
 {task.attachments.length > 1 && (
 <button
 onClick={() => {
 task.attachments.forEach((att, i) => {
 const isOms = att.source === 'oms';
 const url = isOms
 ? `${API}/api/cases/oms-attachments/${att.oms_id}/download`
 : `${API}/api/cases/attachments/${att.id}/download`;
 setTimeout(() => {
 const a = document.createElement('a');
 a.href = url;
 a.download = att.original_name || '';
 document.body.appendChild(a);
 a.click();
 a.remove();
 }, i * 150);
 });
 }}
 className="text-[10px] font-medium text-[#338edc] hover:underline flex items-center gap-1"
 title={t('cases.downloadAll')}>
 <FileText className="w-3 h-3" />
 {t('cases.downloadAll')}
 </button>
 )}
 </div>
 <div className="space-y-1.5">
 {task.attachments.map(att => {
 const isOms = att.source === 'oms';
 const downloadUrl = isOms
 ? `${API}/api/cases/oms-attachments/${att.oms_id}/download`
 : `${API}/api/cases/attachments/${att.id}/download`;
 const mime = att.mime_type || '';
 const isImage = mime.startsWith('image/');
 const isPdf = mime === 'application/pdf' || (att.original_name || '').toLowerCase().endsWith('.pdf');
 const inlineUrl = isOms
 ? `${API}/api/cases/oms-attachments/${att.oms_id}/inline`
 : downloadUrl;
 const previewable = isImage || isPdf;
 return (
 <div key={att.id} className="group flex items-center gap-2 text-xs px-1.5 py-1 rounded-md hover:bg-[var(--bg-inset,#F8F9FC)] dark:hover:bg-[var(--bg-card-hover)] transition-colors">
 {isImage ? (
 <button onClick={() => openLightbox(inlineUrl, 'image')} className="w-6 h-6 rounded border border-line overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80">
 <img src={inlineUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
 </button>
 ) : isPdf ? (
 <FileText className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
 ) : (
 <Paperclip className="w-3 h-3 text-content-tertiary flex-shrink-0" />
 )}
 {previewable ? (
 <button onClick={() => openLightbox(inlineUrl, isPdf ? 'pdf' : 'image')}
 className="text-[#338edc] hover:underline truncate flex-1 text-left">
 {att.original_name}
 </button>
 ) : (
 <a href={downloadUrl}
 className="text-[#338edc] hover:underline truncate flex-1"
 target="_blank" rel="noopener noreferrer">
 {att.original_name}
 </a>
 )}
 <span className="text-content-tertiary text-[10px] flex-shrink-0">{att.size_bytes ? `${(att.size_bytes / 1024).toFixed(0)}KB` : ''}</span>
 {/* Hover-Actions: Preview + Download */}
 <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
 {previewable && (
 <button
 onClick={() => openLightbox(inlineUrl, isPdf ? 'pdf' : 'image')}
 title={t('cases.preview')}
 className="w-5 h-5 rounded flex items-center justify-center text-[var(--text-tertiary)] hover:text-[#338edc] hover:bg-white dark:hover:bg-[var(--bg-card)]">
 <Eye className="w-3 h-3" />
 </button>
 )}
 <a
 href={downloadUrl}
 download={att.original_name || ''}
 title={t('cases.download')}
 className="w-5 h-5 rounded flex items-center justify-center text-[var(--text-tertiary)] hover:text-[#338edc] hover:bg-white dark:hover:bg-[var(--bg-card)]">
 <FileText className="w-3 h-3" />
 </a>
 </div>
 </div>
 );
 })}
 </div>
 </div>
 )}

 {/* Zeiterfassung (#989, #1075 compact in Sidebar) — nur eigene Einträge sichtbar; gated im Backend */}
 {task?.id && <CaseZeiterfassungPanel caseId={task.id} kundeId={task.company_id_num || task.kunde_id || null} t={t} compact />}

 {/* Erstellt */}
 <div className="text-xs" style={{ color: 'var(--text-secondary)' }} title={fmtDateTime(task.create_date)}>
 {t('cases.created')}: {fmtDate(task.create_date)} <span style={{ color: 'var(--text-tertiary)' }}>({relTime(task.create_date, t)})</span>
 </div>

 {/* Delete button */}
 {task.source !== 'oms' && (
 <button onClick={() => setConfirmDeleteCase(true)}
 className="flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/15 border border-transparent hover:border-red-200 dark:hover:border-red-800 transition-colors">
 <Trash2 className="w-3.5 h-3.5" />
 {t('cases.delete')}
 </button>
 )}
 </div>

 {/* ─── Card 3: Kunde ─── */}
 <div className="bg-blue-50/30 border border-blue-100 dark:bg-blue-950/20 dark:border-blue-900 rounded-xl p-4">
 <div className="text-[10px] font-semibold tracking-wide text-[var(--text-tertiary,#6B7280)] uppercase mb-2">
 {t('cases.detail.customer')}
 </div>
 {task.company_name || customerInfo ? (() => {
 const ci = customerInfo;
 const name = ci?.name || task.company_name;
 const kundeInitials = name ? name.substring(0, 2).toUpperCase() : '?';
 return (
 <div>
 <div className="flex items-center gap-2.5 mb-2">
 <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400 flex items-center justify-center text-[11px] font-medium">
 {kundeInitials}
 </div>
 <div>
 <div className="text-[13px] font-medium text-[var(--text-primary,#1A1A2E)] dark:text-content">{name}</div>
 {ci?.segment && (
 <div className="text-[11px] text-[var(--text-tertiary,#6B7280)]">Segment {ci.segment}</div>
 )}
 </div>
 </div>
 {(ci?.email || task.from_email) && (
 <a href={`mailto:${ci?.email || task.from_email}`} className="text-[11px] text-blue-500 hover:underline block mb-1">
 {ci?.email || task.from_email}
 </a>
 )}
 {/* #1114 — Bevorzugte Reply-Adresse (wenn != kontakt_email) */}
 {resolvedRecipient?.address && ci?.email &&
   String(resolvedRecipient.address).toLowerCase() !== String(ci.email).toLowerCase() && (
   <div className="mt-1 mb-1 p-1.5 rounded bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20">
     <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-0.5">
       {t('cases.preferredReplyAddress')}
     </div>
     <a href={`mailto:${resolvedRecipient.address}`} className="text-[11px] text-amber-700 dark:text-amber-400 hover:underline block break-all">
       {resolvedRecipient.address}
     </a>
     <div className="text-[10px] text-amber-700/70 dark:text-amber-400/70 mt-0.5">
       {t('cases.preferredReplyAddressHint')}
     </div>
   </div>
 )}
 {ci?.telefon && (
 <a href={`tel:${ci.telefon}`} className="text-[11px] text-blue-500 hover:underline block mb-1">
 {ci.telefon}
 </a>
 )}
 {(ci?.id || task.company_id_num) && (
 <Link to={`/kunden/${ci?.id || task.company_id_num}`}
 className="text-[11px] text-blue-500 hover:underline flex items-center gap-1 mt-1"
 onClick={e => e.stopPropagation()}>
 <ExternalLink size={11} />
 {t('cases.detail.customerDetail')}
 </Link>
 )}
 {/* #1374 — Kunde wechseln / entfernen (nur lokale Cases) */}
 {task.source !== 'oms' && (
 <div ref={kundeRef}>
 <div className="flex items-center gap-3 mt-2 pt-2 border-t" style={{ borderColor: 'var(--border-table)' }}>
 <button onClick={() => setKundeSearchOpen(o => !o)}
 className="text-[11px] font-medium text-[#338edc] hover:underline">
 {t('cases.changeCustomer')}
 </button>
 <button onClick={() => setConfirmRemoveCustomer(true)}
 className="text-[11px] font-medium text-red-500 hover:underline">
 {t('cases.removeCustomer')}
 </button>
 </div>
 {kundeSearchOpen && (
 <div className="border rounded-[10px] overflow-hidden mt-2" style={{ borderColor: 'var(--border-input)' }}>
 <input type="text" autoFocus value={kundeSearch} onChange={e => setKundeSearch(e.target.value)}
 placeholder={t('cases.searchCustomer')}
 className="w-full px-3 py-2 text-xs bg-[var(--bg-input)] border-b text-content focus:outline-none" style={{ borderColor: 'var(--border-input)' }} />
 <div className="max-h-40 overflow-y-auto bg-[var(--bg-card)]">
 {filteredKunden.length === 0 && <div className="px-3 py-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>{t('cases.noResults')}</div>}
 {filteredKunden.map(k => (
 <button key={k.id} onClick={() => assignCustomer(k.id)}
 className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-card-hover)] transition-colors flex items-center gap-1.5">
 <Building className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
 <span className="font-medium text-content truncate">{k.name}</span>
 </button>
 ))}
 </div>
 </div>
 )}
 </div>
 )}
 </div>
 );
 })() : (
 <div className="relative" ref={kundeRef}>
 {task.from_email && (
 <div className="text-xs mb-2 flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}>
 <Mail className="w-3 h-3 flex-shrink-0" /> {task.from_email}
 </div>
 )}
 {!kundeSearchOpen ? (
 <button onClick={() => setKundeSearchOpen(true)}
 className="w-full px-3 py-2 rounded-[10px] text-xs font-semibold bg-[#EBF4FD] dark:bg-[#338edc]/10 text-[#338edc] hover:bg-[#d6eafb] dark:hover:bg-[#338edc]/20 border border-[#338edc]/20 transition-colors text-center">
 {t('cases.assignCustomer')}
 </button>
 ) : (
 <div className="border rounded-[10px] overflow-hidden" style={{ borderColor: 'var(--border-input)' }}>
 <input type="text" autoFocus value={kundeSearch} onChange={e => setKundeSearch(e.target.value)}
 placeholder={t('cases.searchCustomer')}
 className="w-full px-3 py-2 text-xs bg-[var(--bg-input)] border-b text-content focus:outline-none" style={{ borderColor: 'var(--border-input)' }} />
 <div className="max-h-40 overflow-y-auto bg-[var(--bg-card)]">
 {filteredKunden.length === 0 && <div className="px-3 py-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>{t('cases.noResults')}</div>}
 {filteredKunden.map(k => (
 <button key={k.id} onClick={() => assignCustomer(k.id)}
 className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-card-hover)] transition-colors flex items-center gap-1.5">
 <Building className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
 <span className="font-medium text-content truncate">{k.name}</span>
 </button>
 ))}
 </div>
 </div>
 )}
 </div>
 )}
 </div>

 {/* #1240 — InfoBox: Case wurde zu Klärung konvertiert */}
 {task.converted_to_claim_id && (
   <div className="bg-[#338edc]/5 border border-[#338edc]/30 rounded-xl p-4">
     <div className="flex items-start gap-2">
       <GitBranch className="w-4 h-4 text-[#338edc] flex-shrink-0 mt-0.5" />
       <div className="text-xs leading-relaxed">
         <div className="font-semibold text-[#338edc] mb-1">{t('cases.detail.convertedTitle') || 'Zu Klärung konvertiert'}</div>
         <div style={{ color: 'var(--text-secondary)' }}>
           {t('cases.detail.convertedAt') || 'Konvertiert am'} {task.converted_at ? new Date(task.converted_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
         </div>
         <button
           type="button"
           onClick={() => detailNavigate(`/claims/${task.converted_to_claim_id}`)}
           className="mt-2 text-[#338edc] hover:underline font-medium"
         >
           {t('cases.detail.openClaim') || 'Klärung öffnen'} →
         </button>
       </div>
     </div>
   </div>
 )}

 {/* ─── Card 4: Zeitleiste (Related Cases) ─── */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-table,#F3F4F6)] dark:border-[var(--border-default,#E5E7EB)] rounded-xl p-4">
 <h4 className="text-[11px] font-semibold uppercase tracking-[0.8px] mb-1" style={{ color: 'var(--text-tertiary)' }}>{t('cases.detail.timeline')}</h4>
 <div className="text-xs mb-3" style={{ color: 'var(--text-tertiary)' }}>{t('cases.detail.moreCases')} {task.company_name || '—'}</div>
 {relatedCases.length > 0 ? relatedCases.map(rc => {
 const stageColor = rc.stage_key === 'done' ? 'bg-emerald-500' : rc.stage_key === 'in_progress' ? 'bg-[#338edc]' : rc.stage_key === 'waiting_for_support' ? 'bg-amber-500' : 'bg-[var(--text-muted,#9CA3AF)]';
 return (
 <button key={rc.id}
 className="flex justify-between items-center py-2.5 w-full text-left border-b hover:bg-[var(--bg-card-hover)] transition-colors -mx-1 px-1 rounded"
 style={{ borderColor: 'var(--border-table)' }}
 onClick={() => detailNavigate(`/cases/${rc.id}`)}
 >
 <div className="min-w-0 flex-1">
 <div className="text-xs font-medium text-content truncate">{formatCaseId(rc.id, rc.source)} {rc.title?.length > 30 ? rc.title.slice(0, 30) + '...' : rc.title}</div>
 <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{relTime(rc.created_at, t)}</div>
 </div>
 <span className={`inline-flex rounded-full h-2 w-2 flex-shrink-0 ml-2 ${stageColor}`} />
 </button>
 );
 }) : (
 <div className="text-xs italic" style={{ color: 'var(--text-tertiary)' }}>{t('cases.detail.noCases')}</div>
 )}
 </div>

 {/* ─── Card 5: Status-Verlauf (#1032) ─── */}
 {Array.isArray(task.status_history) && task.status_history.length > 0 && (() => {
 // DB-Stage → OMS-Stage-Name (kompatibel mit getStageLabelShort)
 const DB_TO_OMS = { 'new': 'response required', 'in_progress': 'in progress', 'waiting_for_support': 'waiting for support', 'clarification_with_recipient': 'clarification with recipient', 'resolved': 'solution proposed', 'closed': 'done' };
 const stageLabel = (s) => s ? (getStageLabelShort(DB_TO_OMS[s] || s) || s) : '—';
 return (
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-table,#F3F4F6)] dark:border-[var(--border-default,#E5E7EB)] rounded-xl p-4">
 <h4 className="text-[11px] font-semibold uppercase tracking-[0.8px] mb-1" style={{ color: 'var(--text-tertiary)' }}>{t('cases.detail.statusHistoryTitle')}</h4>
 <div className="text-xs mb-3" style={{ color: 'var(--text-tertiary)' }}>{t('cases.detail.statusHistorySubtitle')}</div>
 <div className="space-y-2.5">
 {task.status_history.map(h => {
 const fromLabel = stageLabel(h.status_from);
 const toLabel = stageLabel(h.status_to);
 const dt = new Date(h.changed_at);
 const dateStr = dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
 const timeStr = dt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
 return (
 <div key={h.id} className="text-xs leading-snug border-b last:border-b-0 pb-2 last:pb-0" style={{ borderColor: 'var(--border-table)' }}>
 <div className="flex items-center gap-1.5 flex-wrap">
 <span className="font-medium text-content">{fromLabel}</span>
 <span style={{ color: 'var(--text-tertiary)' }}>→</span>
 <span className="font-medium" style={{ color: '#338edc' }}>{toLabel}</span>
 </div>
 <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
 {dateStr} {timeStr} • {h.changed_by_name}
 {h.is_automatic && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: 'var(--bg-inset,#F3F4F6)', color: 'var(--text-tertiary)' }}>{t('cases.detail.statusHistoryAuto')}</span>}
 </div>
 {h.note && <div className="text-[11px] mt-1 italic" style={{ color: 'var(--text-secondary)' }}>{h.note}</div>}
 </div>
 );
 })}
 </div>
 </div>
 );
 })()}

 {/* ─── Card 6: Audit-Log (#1060) ─── */}
 {Array.isArray(task.audit_log) && task.audit_log.length > 0 && (() => {
 const ACTION_META = {
 viewed: { icon: Eye, color: 'var(--text-tertiary)', bg: 'var(--bg-inset,#F3F4F6)' },
 opened: { icon: PlusCircle, color: '#338edc', bg: 'rgba(51,142,220,0.10)' },
 replied: { icon: Send, color: '#338edc', bg: 'rgba(51,142,220,0.10)' },
 status_changed: { icon: GitBranch, color: '#6366F1', bg: 'rgba(99,102,241,0.10)' },
 tag_changed: { icon: Tag, color: '#A855F7', bg: 'rgba(168,85,247,0.10)' },
 assigned: { icon: UserCheck, color: '#10B981', bg: 'rgba(16,185,129,0.10)' },
 attachment_uploaded: { icon: Paperclip, color: '#F59E0B', bg: 'rgba(245,158,11,0.10)' },
 };
 const formatDetails = (action, details) => {
 if (!details || typeof details !== 'object') return null;
 try {
 if (action === 'replied') {
 const bits = [];
 if (details.is_internal) bits.push(t('cases.detail.auditDetail.internal'));
 if (details.email_sent && details.recipient) bits.push(`→ ${details.recipient}`);
 if (details.direction === 'inbound') bits.push(t('cases.detail.auditDetail.inbound'));
 return bits.length ? bits.join(' • ') : null;
 }
 if (action === 'tag_changed') {
 if (details.added) return `+ ${details.tag_name || ''}`;
 if (details.removed) return `− ${details.tag_name || ''}`;
 }
 if (action === 'assigned') {
 if (details.assigned_to === null || details.assigned_to === undefined || details.assigned_to === '') return t('cases.detail.auditDetail.unassigned');
 return `→ ${details.assigned_to}`;
 }
 if (action === 'attachment_uploaded') {
 const cnt = details.count || (Array.isArray(details.files) ? details.files.length : 0);
 return `${cnt} ${cnt === 1 ? t('cases.detail.auditDetail.file') : t('cases.detail.auditDetail.files')}`;
 }
 if (action === 'status_changed' && details.from && details.to) {
 // #1192 — DB-Enums → deutsche UI-Labels (kompatibel mit Card 5)
 const DB_TO_OMS = { 'new': 'response required', 'in_progress': 'in progress', 'waiting_for_support': 'waiting for support', 'clarification_with_recipient': 'clarification with recipient', 'resolved': 'solution proposed', 'closed': 'done' };
 const lbl = (s) => s ? (getStageLabelShort(DB_TO_OMS[s] || s) || s) : '—';
 return `${lbl(details.from)} → ${lbl(details.to)}`;
 }
 } catch (e) { /* swallow */ }
 return null;
 };
 return (
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-table,#F3F4F6)] dark:border-[var(--border-default,#E5E7EB)] rounded-xl p-4">
 <h4 className="text-[11px] font-semibold uppercase tracking-[0.8px] mb-1" style={{ color: 'var(--text-tertiary)' }}>{t('cases.detail.auditLogTitle')}</h4>
 <div className="text-xs mb-3" style={{ color: 'var(--text-tertiary)' }}>{t('cases.detail.auditLogSubtitle')}</div>
 <div className="space-y-2.5">
 {task.audit_log.map(a => {
 const meta = ACTION_META[a.action] || { icon: History, color: 'var(--text-tertiary)', bg: 'var(--bg-inset,#F3F4F6)' };
 const Icon = meta.icon;
 const dt = new Date(a.created_at);
 const dateStr = dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
 const timeStr = dt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
 const detailStr = formatDetails(a.action, a.details);
 const actionLabel = t(`cases.detail.auditAction.${a.action}`) || a.action;
 return (
 <div key={a.id} className="flex items-start gap-2.5 text-xs leading-snug border-b last:border-b-0 pb-2 last:pb-0" style={{ borderColor: 'var(--border-table)' }}>
 <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: meta.bg }}>
 <Icon size={11} style={{ color: meta.color }} />
 </div>
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-1.5 flex-wrap">
 <span className="font-medium" style={{ color: meta.color }}>{actionLabel}</span>
 {detailStr && <span style={{ color: 'var(--text-secondary)' }}>{detailStr}</span>}
 </div>
 <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
 {a.user_name || '—'} • {dateStr} {timeStr}
 </div>
 </div>
 </div>
 );
 })}
 </div>
 </div>
 );
 })()}

 </div>{/* /sidebar-inner */}
 </div>{/* /sidebar */}
 </div>

 ) : loadError ? (
 <div className="flex items-center justify-center py-16">
 <div className="flex flex-col items-center gap-4 text-center">
 <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
 <AlertCircle className="w-6 h-6 text-red-500" />
 </div>
 <div>
 <p className="text-sm font-medium text-content">{t('cases.loadError')}</p>
 <p className="text-xs text-content-secondary mt-1">{t('cases.loadErrorHint')}</p>
 </div>
 <button
 onClick={load}
 className="flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg transition-colors hover:bg-primary-50 dark:hover:bg-primary-500/10"
 style={{ color: MODULE_COLOR, borderColor: MODULE_COLOR + '40' }}
 >
 <RefreshCw className="w-4 h-4" />
 {t('cases.retry')}
 </button>
 </div>
 </div>
 ) : (
 <div className="flex-1 flex items-center justify-center text-[var(--text-secondary)]">{t('cases.notFound')}</div>
 )}
 </div>
 );

 const executeDeleteCase = useCallback(async () => {
 setConfirmDeleteCase(false);
 if (!task) return;
 try {
 await api(`/api/cases/${task.id}`, { method: 'DELETE' });
 onClose();
 onRefresh?.();
 } catch (e) { setToast({ message: e.message, type: 'error' }); }
 }, [task, onClose, onRefresh]);

 const extras = (
 <>
 {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
 <StatusAfterSendModal
 isOpen={statusModalOpen}
 currentStatus={task?.stage_key}
 stages={stages}
 onCancel={() => setStatusModalOpen(false)}
 onSelect={async (newKey) => {
 setStatusModalOpen(false);
 if (!newKey || newKey === task?.stage_key) return;
 try {
 await setStage(newKey);
 const st = stages.find(s => s.key === newKey);
 setToast({ message: t('cases.statusAfterSend.toastSet', 'Status auf „{label}" gesetzt').replace('{label}', st ? (getStageLabel(st.name) || st.name) : newKey), type: 'success' });
 } catch (e) {
 setToast({ message: e.message || t('common.error'), type: 'error' });
 }
 }}
 />
 <DoneCategoryRequiredModal
   isOpen={doneCategoryRequiredOpen}
   allCategories={allCategories}
   onCancel={() => setDoneCategoryRequiredOpen(false)}
   onConfirm={async (catId) => {
     try {
       await api(`/api/cases/${task.id}/category${task?.source === 'oms' ? '?src=oms' : ''}`, { method: 'PUT', body: { category_id: Number(catId) } });
       setTask(p => p ? { ...p, category_id: Number(catId) } : p);
       setDoneCategoryRequiredOpen(false);
       // #1254 — Guard überspringen: task.category_id ist im Closure noch stale (setTask async),
       // sonst würde setStage das Modal erneut öffnen und ein 2. Klick wäre nötig.
       await setStage('done', { skipCategoryCheck: true });
       onRefresh?.();
     } catch (e) {
       setToast({ message: e.message || t('common.error'), type: 'error' });
     }
   }}
 />
 <ConvertCaseToClaimModal
   isOpen={convertModalOpen}
   caseTask={task}
   onClose={() => setConvertModalOpen(false)}
   onSuccess={(data) => {
     setConvertModalOpen(false);
     setToast({ message: t('cases.convert.successToast', 'Klärung {nr} erstellt').replace('{nr}', data.claim_number || ''), type: 'success' });
     setTask(prev => prev ? { ...prev, converted_to_claim_id: data.claim_id, converted_at: new Date().toISOString(), stage_key: 'done' } : prev);
     onRefresh?.();
     setTimeout(() => detailNavigate(`/claims/${data.claim_id}`), 600);
   }}
 />
 {/* #1246 — Verknüpfen-Modal */}
 <LinkEntityModal
   isOpen={linkClaimModalOpen}
   onClose={() => setLinkClaimModalOpen(false)}
   mode="pick-claim"
   sourceId={task?.id}
   customerId={task?.company_id_num}
   onLinked={() => { fetchLinkedClaims(); load(true); }}
 />
 {/* #1241 — Original-Mail Modal (Sandboxed Iframe) */}
 <MailOriginalModal
   open={mailOriginalModal.open}
   onClose={() => setMailOriginalModal({ open: false, html: '', subject: '' })}
   html={mailOriginalModal.html}
   subject={mailOriginalModal.subject}
 />
 <ConfirmDialog
 open={confirmDeleteCase}
 onCancel={() => setConfirmDeleteCase(false)}
 onConfirm={executeDeleteCase}
 title={t('cases.delete')}
 message={t('cases.deleteConfirm', { id: task?.id })}
 confirmLabel={t('common.delete')}
 cancelLabel={t('common.cancel')}
 variant="danger"
 zIndex={110}
 />
 {/* #1374 — Kunden-Zuweisung entfernen */}
 <ConfirmDialog
 open={confirmRemoveCustomer}
 onCancel={() => setConfirmRemoveCustomer(false)}
 onConfirm={() => { setConfirmRemoveCustomer(false); assignCustomer(null); }}
 title={t('cases.removeCustomer')}
 message={t('cases.removeCustomerConfirm', { name: customerInfo?.name || task?.company_name || '' })}
 confirmLabel={t('cases.removeCustomer')}
 cancelLabel={t('common.cancel')}
 variant="danger"
 zIndex={110}
 />
 {lightboxUrl && (
 <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center cursor-pointer"
 onClick={() => setLightboxUrl(null)}
 onKeyDown={e => { if (e.key === 'Escape') setLightboxUrl(null); }}
 tabIndex={0}
 role="button">
 <button onClick={() => setLightboxUrl(null)}
 className="absolute top-4 right-4 text-white/80 hover:text-white z-10">
 <X className="w-6 h-6" />
 </button>
 {lightboxKind === 'pdf' ? (
 <iframe
 src={lightboxUrl}
 title="PDF preview"
 className="w-[92vw] h-[92vh] rounded-lg shadow-2xl bg-white"
 onClick={e => e.stopPropagation()}
 />
 ) : (
 <img src={lightboxUrl} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
 onClick={e => e.stopPropagation()} />
 )}
 </div>
 )}
 {/* Task-Create Modal */}
 {taskModalOpen && (
 <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40" onClick={() => setTaskModalOpen(false)}>
 <div className="bg-[var(--bg-card)] rounded-xl shadow-xl border border-[var(--border-default)] w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
 <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>{t('cases.createTask')}</h3>

 {/* Duplicate Warning */}
 {taskDuplicateWarn && taskDuplicateWarn.length > 0 && (
 <div className="mb-4 p-3 rounded-lg border border-amber-300 bg-amber-50" style={{ color: 'var(--text-primary)' }}>
 <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 mb-1">
 <AlertCircle className="w-3.5 h-3.5" />
 {t('cases.taskDuplicateWarn', 'Es gibt bereits offene Aufgaben zu diesem Case:')}
 </div>
 {taskDuplicateWarn.map(dt => (
 <div key={dt.id} className="text-xs text-amber-600 ml-5">• {dt.title} ({dt.assigned_name || `User #${dt.assigned_to}`})</div>
 ))}
 </div>
 )}

 <div className="space-y-3">
 {/* Title — empty, required */}
 <div>
 <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>{t('common.title')} *</label>
 <input className="w-full border rounded-lg px-3 py-2 text-sm bg-[var(--bg-input)] text-[var(--text-primary)]" style={{ borderColor: 'var(--border-input)' }}
 value={taskForm.title} onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))}
 placeholder={t('cases.taskTitlePlaceholder', 'Was soll getan werden?')} autoFocus />
 </div>

 {/* Description — prominent, min 4 rows */}
 <div>
 <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>{t('common.description')} *</label>
 <textarea className="w-full border rounded-lg px-3 py-2 text-sm bg-[var(--bg-input)] text-[var(--text-primary)] resize-y" rows={4} style={{ borderColor: 'var(--border-input)', minHeight: '96px' }}
 value={taskForm.description} onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))}
 placeholder={t('cases.taskDescPlaceholder', 'Erklaere die Aufgabe so, dass der Empfaenger alles versteht ohne den Case lesen zu muessen')} />
 </div>

 {/* Assign to — required */}
 <div>
 <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>{t('meineAufgaben.assignTo')} *</label>
 <select className="w-full border rounded-lg px-3 py-2 text-sm bg-[var(--bg-input)] text-[var(--text-primary)]" style={{ borderColor: 'var(--border-input)' }}
 value={taskForm.assigned_to} onChange={e => setTaskForm(f => ({ ...f, assigned_to: e.target.value }))}>
 <option value="">{t('cases.taskSelectUser', '— Bitte waehlen —')}</option>
 {taskUsers.map(u => <option key={u.id} value={u.id}>{u.name || `${u.vorname || ''} ${u.nachname || ''}`.trim() || u.email}</option>)}
 </select>
 </div>

 {/* Optional: Priority + Category + Deadline */}
 <div className="grid grid-cols-3 gap-3">
 <div>
 <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>{t('meineAufgaben.priority')}</label>
 <select className="w-full border rounded-lg px-3 py-2 text-sm bg-[var(--bg-input)] text-[var(--text-primary)]" style={{ borderColor: 'var(--border-input)' }}
 value={taskForm.priority} onChange={e => setTaskForm(f => ({ ...f, priority: e.target.value }))}>
 <option value="low">{t('meineAufgaben.prio.low')}</option>
 <option value="normal">{t('meineAufgaben.prio.normal')}</option>
 <option value="high">{t('meineAufgaben.prio.high')}</option>
 <option value="urgent">{t('meineAufgaben.prio.urgent')}</option>
 </select>
 </div>
 <div>
 <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>{t('tasks.deadline')}</label>
 <input type="date" className="w-full border rounded-lg px-3 py-2 text-sm bg-[var(--bg-input)] text-[var(--text-primary)]" style={{ borderColor: 'var(--border-input)' }}
 value={taskForm.due_date} onChange={e => setTaskForm(f => ({ ...f, due_date: e.target.value }))} />
 </div>
 <div>
 <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>{t('meineAufgaben.category', 'Kategorie')}</label>
 <select className="w-full border rounded-lg px-3 py-2 text-sm bg-[var(--bg-input)] text-[var(--text-primary)]" style={{ borderColor: 'var(--border-input)' }}
 value={taskForm.category_id || ''} onChange={e => setTaskForm(f => ({ ...f, category_id: e.target.value }))}>
 <option value="">—</option>
 {taskCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
 </select>
 </div>
 </div>

 {/* Auto: Case Reference (read-only) */}
 <div className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-inset, #F8F9FC)' }}>
 <LinkIcon className="w-3.5 h-3.5 shrink-0" style={{ color: '#338edc' }} />
 <span style={{ color: 'var(--text-secondary)' }}>
 {t('cases.linkedCase', 'Verknuepfter Case:')} <strong style={{ color: 'var(--text-primary)' }}>{formatCaseId(task?.id, task?.source)} — {task?.name || task?.company_name || ''}</strong>
 </span>
 <span className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: '#FEE2E2', color: '#DC2626' }}>CASE</span>
 </div>
 </div>

 {/* Footer */}
 <p className="text-[11px] mt-4 mb-3" style={{ color: 'var(--text-tertiary)' }}>
 {t('cases.taskFooterHint', 'Der Empfaenger sieht den Task in seinem Aufgaben-Eingang. Der verknuepfte Case ist als Referenz hinterlegt.')}
 </p>
 <div className="flex justify-end gap-2">
 <button onClick={() => setTaskModalOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)]">{t('common.cancel')}</button>
 <button onClick={submitTask} disabled={!taskForm.title.trim() || !taskForm.assigned_to || taskSubmitting}
 className="px-4 py-2 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4] disabled:opacity-50">{taskSubmitting ? '...' : t('cases.createTask')}</button>
 </div>
 </div>
 </div>
 )}
 {/* Task Slide-Over (inline detail view) */}
 {taskSlideOverId && (
 <TaskSlideOver
 taskId={taskSlideOverId}
 onClose={() => setTaskSlideOverId(null)}
 onRefresh={() => {
 if (task?.id) api(`/cases/${task.id}/tasks`).then(d => setLinkedTasks(d.tasks || [])).catch(() => {});
 }}
 t={t}
 />
 )}
 </>
 );

 // Conditional wrapping — NOT an inline component (would cause focus loss)
 if (fullPage) {
 return (
 <div className="h-full flex flex-col bg-white dark:bg-[#0F1117] overflow-hidden">
 {innerContent}
 {extras}
 </div>
 );
 }
 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center p-3" onKeyDown={e => { if (e.key === 'Escape') onClose(); }}>
 <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
 {innerContent}
 {extras}
 </div>
 );
}


// ════════════════════════════════════════════════════════════════
// QUICK ACTION WIDGETS (for Kanban cards)
// ════════════════════════════════════════════════════════════════
function QDrop({ options, value, onChange, placeholder }) {
 const [open, setOpen] = useState(false);
 const ref = useRef(null);
 useEffect(() => {
 const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
 if (open) document.addEventListener('mousedown', h);
 return () => document.removeEventListener('mousedown', h);
 }, [open]);
 return (
 <div ref={ref} className="relative" onClick={e => e.stopPropagation()}>
 <button onClick={() => setOpen(!open)} className="px-2 py-1 rounded-lg bg-surface-inset border border-line text-[10px] font-medium text-[var(--text-secondary)] hover:text-content hover:border-line-hover transition-colors">{placeholder}</button>
 {open && (
 <div className="absolute top-full left-0 mt-1 z-40 bg-surface-elevated border border-line-hover rounded-card shadow-elevated py-1 min-w-[160px] max-h-48 overflow-y-auto">
 {options.map(o => (
 <button key={o.id} onClick={() => { onChange(o.id); setOpen(false); }}
 className={`w-full text-left px-3 py-1.5 text-xs hover:bg-surface-hover transition-colors ${o.id===value?'font-semibold':'text-content-secondary'}`}
 style={o.id===value ? { color: MODULE_COLOR } : {}}>
 {o.label || o.name}
 </button>
 ))}
 </div>
 )}
 </div>
 );
}
function QReply({ taskId, onSent, t }) {
 const [exp, setExp] = useState(false);
 const [txt, setTxt] = useState('');
 const [busy, setBusy] = useState(false);
 const send = async () => {
 if (!txt.trim()||busy) return; setBusy(true);
 try { await api(`/api/cases/${taskId}/message`, { method:'POST', body:{body:txt.trim()} }); setTxt(''); setExp(false); onSent?.(); }
 catch {} finally { setBusy(false); }
 };
 if (!exp) return <button onClick={e => { e.stopPropagation(); setExp(true); }} className="px-2 py-1 rounded-lg bg-surface-inset border border-line text-[10px] font-medium text-[var(--text-secondary)] hover:text-content hover:border-line-hover transition-colors">💬 {t('cases.reply')}</button>;
 return (
 <div className="mt-1.5 w-full" onClick={e => e.stopPropagation()}>
 <textarea autoFocus value={txt} onChange={e => setTxt(e.target.value)}
 onKeyDown={e => { if (e.key==='Enter' && (e.ctrlKey||e.metaKey)) send(); if (e.key==='Escape') { setExp(false); setTxt(''); } }}
 className="w-full bg-surface-inset border border-line rounded-lg px-2.5 py-1.5 text-xs text-content resize-none focus:outline-none focus:border-primary-500/50" rows={2} placeholder={t('cases.ctrlEnterSend')} />
 <div className="flex gap-1 mt-1">
 <button onClick={send} disabled={busy||!txt.trim()} className="px-2.5 py-1 rounded-lg text-[10px] font-semibold text-white disabled:opacity-40" style={{ backgroundColor: MODULE_COLOR }}>{busy?'...':t('cases.sendBtn')}</button>
 <button onClick={() => { setExp(false); setTxt(''); }} className="px-2.5 py-1 rounded-lg text-[10px] text-[var(--text-secondary)] hover:text-content bg-surface-inset">{t('cases.cancelBtn')}</button>
 </div>
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// KANBAN CARD – Compact layout
// ════════════════════════════════════════════════════════════════
function KCard({ task, stages, omsUsers, onOpen, onPreview, onRefresh, isRR, isDone, isWaiting, t }) {
 const { getStageLabel } = useLanguage();
 const [hov, setHov] = useState(false);
 const clickTimer = useRef(null);
 const stale = hrsSince(task.date_last_stage_update);
 const isOrange = stale > 48 && !isRR;
 const isRedPulse = isRR && stale > 24;
 // Overdue indicator for waiting cases
 const waitingHrs = isWaiting ? stale : 0;
 const isEscalated = isWaiting && waitingHrs >= 8;
 const isOverdue = isWaiting && waitingHrs >= 4 && !isEscalated;

 const dragStart = (e) => { e.dataTransfer.setData('text/plain', JSON.stringify({ taskId: task.id, source: task.source })); e.dataTransfer.effectAllowed = 'move'; };
 const qStage = async (key) => { try { const st = stages.find(s=>s.key===key); if (!st) return; await api(`/api/cases/${task.id}/stage${task?.source === 'oms' ? '?src=oms' : ''}`, { method:'PUT', body:{stage_id:st.id, stage_name:st.name} }); onRefresh?.(); } catch {} };
 const qAssign = async (uid) => { try { await api(`/api/cases/${task.id}/assign${task?.source === 'oms' ? '?src=oms' : ''}`, { method:'PUT', body:{user_ids:uid?[Number(uid)]:[]} }); onRefresh?.(); } catch {} };
 const qPrio = async (p) => { try { await api(`/api/cases/${task.id}/priority${task?.source === 'oms' ? '?src=oms' : ''}`, { method:'PUT', body:{priority:p} }); onRefresh?.(); } catch {} };

 // Summary line: AI summary or fallback to description snippet
 const summaryText = task.ai_summary || (() => {
 const plain = stripHtml(task.description);
 return plain ? (plain.length > 60 ? plain.substring(0, 60) + '...' : plain) : null;
 })();

 // ── Compact "Erledigt" card ──
 const handleClick = () => {
 if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; onOpen(task.id, task.source); return; }
 clickTimer.current = setTimeout(() => { clickTimer.current = null; if (onPreview) onPreview(task.id, task.source); else onOpen(task.id, task.source); }, 250);
 };

 if (isDone) {
 return (
 <div draggable onDragStart={dragStart} onClick={handleClick} title={task.name}
 className="group bg-surface-card border border-line rounded-card cursor-pointer hover:shadow-card hover:-translate-y-0.5 transition-all duration-200"
 style={{ padding: '8px 12px' }}>
 <div className="text-[13px] font-medium text-content-secondary truncate">{task.name}</div>
 <div className="flex items-center gap-2 mt-1 text-[10px] text-content-tertiary">
 {(task.company_name || task.partner_name) && <span className="truncate">{task.company_name || task.partner_name}</span>}
 <span className="ml-auto flex-shrink-0">{relTime(task.write_date, t)}</span>
 </div>
 </div>
 );
 }

 let bdr = {};
 if (isRedPulse) bdr = { borderColor: 'rgba(239,68,68,0.7)' };
 else if (isEscalated) bdr = { borderColor: 'rgba(239,68,68,0.6)' };
 else if (isOverdue) bdr = { borderColor: 'rgba(245,158,11,0.6)' };
 else if (isOrange) bdr = { borderColor: 'rgba(251,146,60,0.6)' };

 const borderCls = isRedPulse ? 'border-red-500/70' : isEscalated ? 'border-red-500/60' : isOverdue ? 'border-amber-500/60' : isOrange ? 'border-orange-400/60' : 'border-line';

 return (
 <div draggable onDragStart={dragStart} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
 onClick={handleClick} title={task.name}
 className={`group relative bg-surface-card border ${borderCls} rounded-card cursor-pointer shadow-card hover:shadow-elevated hover:-translate-y-0.5 transition-all duration-200`}
 style={{ padding: '10px 12px', ...bdr }}>

 {/* Red pulse dot for response required */}
 {isRedPulse && <span className="absolute top-2.5 right-2.5 flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" /></span>}

 {/* Row 0: SLA Badge */}
 {task.sla_klasse && (
 <div className="flex items-center justify-between mb-1">
 <SlaKlasseBadge task={task} />
 <Stars value={task.priority} onChange={qPrio} />
 </div>
 )}

 {/* Row 1: Priority stars + Source badge + Title (single line truncate) */}
 <div className="flex items-center gap-1.5 mb-1">
 {!task.sla_klasse && <Stars value={task.priority} onChange={qPrio} />}
 {task.source === 'email' && (
 <span className="flex-shrink-0 inline-flex items-center px-1 py-0.5 rounded text-[8px] font-bold bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400" title="Email">
 <Mail className="w-3 h-3" />
 </span>
 )}
 <span className="text-[13px] font-semibold text-content truncate leading-snug flex-1">{task.name}</span>
 </div>

 {/* Row 2: Company name */}
 {(task.company_name || task.partner_name) && (
 <div className="flex items-center gap-1 mb-1 text-[12px] text-[var(--text-secondary)] truncate">
 <Building className="w-3 h-3 flex-shrink-0" />
 <span className="truncate font-semibold text-content-secondary">{task.company_name || task.partner_name}</span>
 </div>
 )}

 {/* Row 3: AI summary or description fallback */}
 {summaryText && (
 <div className="mb-1.5 text-[11px] text-content-tertiary leading-snug line-clamp-2">
 {summaryText}
 </div>
 )}

 {/* Row 3.5: SLA Timer + Progress */}
 {task.sla_stunden && (
 <div className="mb-1.5">
 <SlaTimerLine task={task} t={t} />
 </div>
 )}

 {/* Row 4: Footer – message count + case number + time left, avatar right */}
 <div className="flex items-center justify-between pt-1.5 border-t border-line/60">
 <div className="flex items-center gap-2 text-[10px] text-content-tertiary">
 {task.message_count > 0 && <span className="flex items-center gap-0.5">💬 <span className="font-medium">{task.message_count}</span></span>}
 {task.case_number && <span className="font-mono text-primary-400">#{task.case_number}</span>}
 <span>{relTime(task.create_date, t)}</span>
 </div>
 <div className="flex items-center gap-1">
 {task.tags?.length > 0 && task.tags.slice(0,2).map(tg => (
 <span key={tg.id} className="px-1.5 py-0.5 rounded text-[8px] font-bold text-white leading-none" style={{ backgroundColor: tagCol(tg.color) }}>{tg.name.length > 8 ? tg.name.substring(0,8) : tg.name}</span>
 ))}
 {task.assigned_users?.length > 0 && (
 <div className="relative w-5 h-5 rounded-full bg-gradient-to-br from-primary-500 to-purple-500 flex items-center justify-center ring-1 ring-surface-card ml-0.5 overflow-hidden" title={task.assigned_users[0].name}>
 {task.assigned_users[0].avatar_url ? (
 <img src={task.assigned_users[0].avatar_url} alt={task.assigned_users[0].name}
 className="w-full h-full object-cover"
 onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = 'flex'); }} />
 ) : null}
 <span style={{ display: task.assigned_users[0].avatar_url ? 'none' : 'flex' }}
 className="absolute inset-0 items-center justify-center text-[7px] text-white font-bold leading-none">
 {initials(task.assigned_users[0].name)}
 </span>
 </div>
 )}
 </div>
 </div>

 {/* Hover quick-actions */}
 {hov && (
 <div className="absolute bottom-0 left-0 right-0 bg-surface-elevated/95 backdrop-blur-sm border-t border-line rounded-b-xl p-2 flex flex-wrap gap-1.5 items-start" onClick={e => e.stopPropagation()}>
 <QDrop options={stages.map(s => ({ id:s.key, label:getStageLabel(s.name) }))} value={task.stage_key} onChange={qStage} placeholder={t('cases.stage')} />
 <QDrop options={omsUsers.map(u => ({ id:u.id, label:u.name }))} value={task.assigned_users?.[0]?.id} onChange={qAssign} placeholder={t('cases.assign')} />
 <QReply taskId={task.id} onSent={onRefresh} t={t} />
 </div>
 )}
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// KANBAN COLUMN
// ════════════════════════════════════════════════════════════════
function KCol({ stage, tasks, stages, omsUsers, onOpen, onPreview, onRefresh, rrId, doneId, waitingId, collapsed, onToggleCollapse, t }) {
 const { getStageLabel } = useLanguage();
 const [dragOver, setDragOver] = useState(false);
 const [expanded, setExpanded] = useState(false);
 const LIMIT = 5;
 const drop = async (e) => {
 e.preventDefault(); setDragOver(false);
 try { const { taskId, source } = JSON.parse(e.dataTransfer.getData('text/plain')); await api(`/api/cases/${taskId}/stage${source === 'oms' ? '?src=oms' : ''}`, { method:'PUT', body:{stage_id:stage.id, stage_name:stage.name} }); onRefresh?.(); }
 catch {}
 };
 const isRR = stage.key === rrId, isD = stage.key === doneId;
 let bg = {};
 if (isRR) bg = { backgroundColor: 'rgba(231,76,60,0.05)' };
 else if (isD) bg = { backgroundColor: 'rgba(34,197,94,0.05)' };

 // ─── Collapsed state ───
 if (collapsed) {
 return (
 <div
 className={`flex-shrink-0 w-[48px] flex flex-col items-center rounded-card border border-line cursor-pointer hover:border-line-hover transition-all ${dragOver?'ring-2 ring-offset-2 ring-offset-surface':''}`}
 style={{ ...bg, minHeight: '400px', ...(dragOver ? { '--tw-ring-color': MODULE_COLOR } : {}) }}
 onClick={() => onToggleCollapse?.(stage.key)}
 onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={drop}
 >
 <div className="pt-3 pb-2 flex-shrink-0">
 <svg className="w-3 h-3 text-[var(--text-secondary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
 <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
 </svg>
 </div>
 <div className="flex-1 flex items-center justify-center">
 <div className="flex flex-col items-center gap-2" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
 <span className={`text-[11px] font-bold uppercase tracking-wide ${isRR ? 'text-red-400' : isD ? 'text-green-500' : 'text-content'}`}>
 {getStageLabel(stage.name)}
 </span>
 <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isRR&&tasks.length>0?'bg-red-500/15 text-red-400':isD?'bg-green-500/15 text-green-500':'bg-surface-inset text-[var(--text-secondary)]'}`} style={{ writingMode: 'horizontal-tb' }}>
 {tasks.length}
 </span>
 </div>
 </div>
 </div>
 );
 }

 return (
 <div className={`flex-shrink-0 w-[300px] flex flex-col rounded-card border border-line transition-all ${dragOver?'ring-2 ring-offset-2 ring-offset-surface':''}`}
 style={{ ...bg, ...(dragOver ? { '--tw-ring-color': MODULE_COLOR } : {}) }}
 onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={drop}>
 <div className="px-4 py-3 border-b border-line flex items-center justify-between cursor-pointer select-none" onClick={() => onToggleCollapse?.(stage.key)}>
 <div className="flex items-center gap-2">
 <svg className="w-3 h-3 text-[var(--text-secondary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
 <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
 </svg>
 {isRR && <span className="w-2 h-2 rounded-full bg-red-500" />}
 {isD && <span className="w-2 h-2 rounded-full bg-green-500" />}
 <h3 className="text-[11px] font-bold text-content uppercase tracking-wide">{getStageLabel(stage.name)}</h3>
 </div>
 <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${isRR&&tasks.length>0?'bg-red-500/15 text-red-400':isD?'bg-green-500/15 text-green-500':'bg-surface-inset text-[var(--text-secondary)]'}`}>{tasks.length}</span>
 </div>
 <div className={`flex-1 overflow-y-auto p-3 ${isD ? 'space-y-1.5' : 'space-y-2.5'} min-h-[120px]`}>
 {(expanded || tasks.length <= LIMIT ? tasks : tasks.slice(0, LIMIT)).map(tk => <KCard key={`${tk.source}-${tk.id}`} task={tk} stages={stages} omsUsers={omsUsers} onOpen={onOpen} onPreview={onPreview} onRefresh={onRefresh} isRR={tk.stage_key===rrId} isDone={tk.stage_key===doneId} isWaiting={tk.stage_key===waitingId} t={t} />)}
 {!expanded && tasks.length > LIMIT && (
 <button onClick={() => setExpanded(true)} className="w-full py-2 text-[11px] font-semibold text-[var(--text-secondary)] hover:text-content bg-surface-inset border border-line rounded-lg transition-colors hover:border-line-hover">
 {t('cases.showMore', { count: tasks.length - LIMIT })}
 </button>
 )}
 {tasks.length===0 && <div className="flex flex-col items-center justify-center text-content-tertiary py-10 opacity-40"><svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg><span className="text-xs">{t('cases.noCases')}</span></div>}
 </div>
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// LIST VIEW
// ════════════════════════════════════════════════════════════════
// ─── Configurable Columns ───
const DEFAULT_COLUMNS = [
 { id: 'ticket_id', label: 'cases.columns.id', sortKey: 'id', defaultWidth: 70, minWidth: 50 },
 { id: 'kunde', label: 'cases.columns.kunde', sortKey: 'partner', defaultWidth: 200, minWidth: 120 },
 { id: 'titel', label: 'cases.columns.titel', sortKey: 'name', defaultWidth: 300, minWidth: 150 },
 { id: 'status', label: 'cases.columns.status', sortKey: 'stage', defaultWidth: 150, minWidth: 100 },
 { id: 'category', label: 'cases.columns.category', sortKey: null, defaultWidth: 120, minWidth: 80 },
 { id: 'tags', label: 'cases.columns.tags', sortKey: null, defaultWidth: 150, minWidth: 100 },
 { id: 'prio', label: 'cases.columns.prio', sortKey: 'priority', defaultWidth: 80, minWidth: 60 },
 { id: 'sla', label: 'cases.columns.sla', sortKey: 'sla', defaultWidth: 160, minWidth: 100 },
 { id: 'zugewiesen', label: 'cases.columns.zugewiesen', sortKey: null, defaultWidth: 150, minWidth: 100 },
 { id: 'aktiv', label: 'cases.columns.aktiv', sortKey: null, defaultWidth: 110, minWidth: 70 }, // #1081
 { id: 'ai', label: 'cases.columns.ai', sortKey: null, defaultWidth: 130, minWidth: 44 }, // #1136X — AI-Suggestion-Badge
 { id: 'erstellt', label: 'cases.columns.erstellt', sortKey: 'create_date', defaultWidth: 130, minWidth: 80 },
];
const COL_STORAGE_KEY = 'weship_cases_column_config';

function loadColumnConfig() {
 try {
 const saved = localStorage.getItem(COL_STORAGE_KEY);
 if (!saved) return null;
 const parsed = JSON.parse(saved);
 if (!Array.isArray(parsed) || !parsed.length) return null;
 // Merge with defaults to pick up any newly-added columns
 const knownIds = new Set(parsed.map(p => p.id));
 const merged = parsed.map(p => {
 const def = DEFAULT_COLUMNS.find(d => d.id === p.id);
 return def ? { ...def, width: p.width ?? def.defaultWidth, visible: p.visible !== false } : null;
 }).filter(Boolean);
 DEFAULT_COLUMNS.forEach(d => { if (!knownIds.has(d.id)) merged.push({ ...d, width: d.defaultWidth, visible: true }); });
 return merged;
 } catch { return null; }
}

function saveColumnConfig(cols) {
 try { localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(cols.map(c => ({ id: c.id, width: c.width, visible: c.visible })))); } catch {}
}

// ─── Bulk Action Components ───
function BulkDropdown({ label, options, onSelect }) {
 const [open, setOpen] = useState(false);
 const ref = useRef(null);
 useEffect(() => {
 if (!open) return;
 const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
 document.addEventListener('mousedown', h);
 return () => document.removeEventListener('mousedown', h);
 }, [open]);
 return (
 <div className="relative" ref={ref}>
 <button onClick={() => setOpen(!open)}
 className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-white dark:bg-[var(--bg-inset,#F8F9FC)] border border-line rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] dark:hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
 {label}
 <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>
 </button>
 {open && (
 <div className="absolute left-0 top-full mt-1 w-52 bg-surface-elevated border border-line rounded-lg shadow-sm z-50 py-1 max-h-64 overflow-y-auto">
 {options.map(opt => (
 <button key={opt.value ?? 'null'} onClick={() => { onSelect(opt.value); setOpen(false); }}
 className="w-full text-left px-3 py-1.5 text-sm text-content hover:bg-surface-hover transition-colors truncate">
 {opt.label}
 </button>
 ))}
 </div>
 )}
 </div>
 );
}

function BulkActionBar({ count, stages, omsUsers, onBulk, onDelete, allEmail, onClear, t }) {
 const { getStageLabel } = useLanguage();
 return (
 <div className="sticky top-0 z-30 flex items-center gap-3 px-4 py-2.5 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg mb-3">
 <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
 {t('cases.bulk.selected', { count })}
 </span>
 <div className="h-5 w-px bg-primary-200 dark:bg-primary-700" />
 <BulkDropdown label={t('cases.bulk.changeStage')} options={stages.map(s => ({ value: s.key, label: getStageLabel(s.name) || s.name }))} onSelect={v => onBulk('stage', v)} />
 <BulkDropdown label={t('cases.bulk.assign')} options={[{ value: '', label: '– ' + t('cases.unassigned') }, ...omsUsers.map(u => ({ value: u.id, label: u.name }))]} onSelect={v => onBulk('assign', v)} />
 <BulkDropdown label={t('cases.bulk.priority')} options={[
 { value: 0, label: '☆☆☆ ' + t('cases.bulk.noPrio') },
 { value: 1, label: '★☆☆ ' + t('cases.bulk.low') },
 { value: 2, label: '★★☆ ' + t('cases.bulk.medium') },
 { value: 3, label: '★★★ ' + t('cases.bulk.high') },
 ]} onSelect={v => onBulk('priority', v)} />
 {allEmail && (
 <button onClick={onDelete}
 className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-white dark:bg-[var(--bg-inset,#F8F9FC)] border border-red-300 dark:border-red-800 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
 <Trash2 className="w-3.5 h-3.5" />
 {t('cases.bulk.delete')}
 </button>
 )}
 <div className="flex-1" />
 <button onClick={onClear} className="p-1 text-primary-400 hover:text-primary-600 dark:hover:text-primary-300 transition-colors">
 <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
 </button>
 </div>
 );
}

// ─── Simple Filter Dropdown (#1247) ───
// Generischer Dropdown für Pills-zu-Dropdown-Konvertierung. Options-Array mit
// optionalen Count-Badges, Divider, Warn-Variante. Click-outside schließt.
//
// items: [
//   { key, label, badge?: number, icon?: JSX, variant?: 'warn' | 'divider' }
// ]
//
// Active-Item via `value` (key-match). Divider als pseudo-item zwischen Sektionen.
function SimpleFilterDropdown({ icon, triggerLabel, items, value, onChange, t, ariaLabel }) {
 const [open, setOpen] = useState(false);
 const ref = useRef(null);
 useEffect(() => {
  if (!open) return;
  const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
  document.addEventListener('mousedown', h);
  return () => document.removeEventListener('mousedown', h);
 }, [open]);
 const active = items.find(it => it && it.key === value);
 return (
  <div className="relative" ref={ref}>
   <button
    onClick={() => setOpen(!open)}
    aria-label={ariaLabel}
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors bg-surface-card ${
     open ? 'border-[#338edc] text-content' : 'border-line text-[var(--text-secondary)] hover:text-content hover:border-line-hover'
    }`}
   >
    {icon}
    <span className="truncate">{triggerLabel}</span>
    <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
     <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
    </svg>
   </button>
   {open && (
    <div className="absolute left-0 top-full mt-1 w-64 bg-surface-elevated border border-line rounded-lg shadow-sm z-50 overflow-hidden">
     <div className="max-h-80 overflow-y-auto py-1">
      {items.map((it, idx) => {
       if (!it) return null;
       if (it.variant === 'divider') {
        return <div key={`div-${idx}`} className="border-t border-line my-1" />;
       }
       const isActive = it.key === value;
       const isWarn = it.variant === 'warn';
       return (
        <button
         key={it.key}
         onClick={() => { onChange(it.key); setOpen(false); }}
         className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center justify-between gap-2 ${
          isActive
           ? (isWarn
              ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 font-semibold'
              : 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-semibold')
           : (isWarn
              ? 'text-red-600 dark:text-red-400 hover:bg-red-50/50 dark:hover:bg-red-900/10'
              : 'text-content hover:bg-surface-hover')
         }`}
        >
         <span className="flex items-center gap-1.5 truncate">
          {it.icon}
          {it.label}
         </span>
         {typeof it.badge === 'number' && (
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold leading-none ${
           isWarn
            ? 'bg-red-500/15 text-red-600 dark:text-red-400'
            : (isActive ? 'bg-primary-200 text-primary-800 dark:bg-primary-800 dark:text-primary-200' : 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)]')
          }`}>
           {it.badge}
          </span>
         )}
        </button>
       );
      })}
     </div>
    </div>
   )}
  </div>
 );
}

// ─── Kunde Filter Dropdown ───
function KundeFilterDropdown({ kundenList, value, onChange, t }) {
 const [open, setOpen] = useState(false);
 const [search, setSearch] = useState('');
 const ref = useRef(null);
 useEffect(() => {
 if (!open) return;
 const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
 document.addEventListener('mousedown', h);
 return () => document.removeEventListener('mousedown', h);
 }, [open]);
 const filtered = useMemo(() => {
 if (!search.trim()) return kundenList.slice(0, 20);
 const q = search.toLowerCase();
 return kundenList.filter(k => k.name?.toLowerCase().includes(q)).slice(0, 20);
 }, [kundenList, search]);
 const label = value === 'all' ? t('cases.filterKundeAll') : value === 'none' ? t('cases.filterKundeNone') : kundenList.find(k => k.id === Number(value))?.name || `#${value}`;
 const isActive = value !== 'all';
 return (
 <div className="relative" ref={ref}>
 <button onClick={() => setOpen(!open)}
 className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${
 isActive ? 'bg-[#338edc] text-white border-[#338edc]' : 'border-line text-[var(--text-secondary)] hover:text-content hover:border-line-hover bg-surface-card'
 }`}>
 <Building className="w-3 h-3" />
 <span className="max-w-[120px] truncate">{label}</span>
 {isActive && (
 <span className="ml-0.5 hover:bg-white/20 rounded-full p-0.5" onClick={e => { e.stopPropagation(); onChange('all'); setOpen(false); }}>
 <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
 </span>
 )}
 {!isActive && <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>}
 </button>
 {open && (
 <div className="absolute left-0 top-full mt-1 w-56 bg-surface-elevated border border-line rounded-lg shadow-sm z-50 overflow-hidden">
 <div className="p-1.5">
 <input type="text" autoFocus value={search} onChange={e => setSearch(e.target.value)}
 placeholder={t('cases.searchCustomer')}
 className="w-full px-2.5 py-1.5 text-xs bg-surface-inset border border-line rounded-lg text-content focus:outline-none focus:border-primary-500/50" />
 </div>
 <div className="max-h-56 overflow-y-auto py-1">
 <button onClick={() => { onChange('all'); setOpen(false); setSearch(''); }}
 className={`w-full text-left px-3 py-1.5 text-xs transition-colors truncate ${value === 'all' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-semibold' : 'text-content hover:bg-surface-hover'}`}>
 {t('cases.filterKundeAll')}
 </button>
 <button onClick={() => { onChange('none'); setOpen(false); setSearch(''); }}
 className={`w-full text-left px-3 py-1.5 text-xs transition-colors truncate ${value === 'none' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-semibold' : 'text-content hover:bg-surface-hover'}`}>
 {t('cases.filterKundeNone')}
 </button>
 <div className="border-t border-line my-1" />
 {filtered.map(k => (
 <button key={k.id} onClick={() => { onChange(k.id); setOpen(false); setSearch(''); }}
 className={`w-full text-left px-3 py-1.5 text-xs transition-colors truncate flex items-center gap-1.5 ${Number(value) === k.id ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-semibold' : 'text-content hover:bg-surface-hover'}`}>
 <Building className="w-3 h-3 text-content-tertiary flex-shrink-0" />
 {k.name}
 </button>
 ))}
 {filtered.length === 0 && <div className="px-3 py-2 text-xs text-content-tertiary">{t('cases.noResults')}</div>}
 </div>
 </div>
 )}
 </div>
 );
}

// ─── Tag Filter Dropdown (multi-select with search) ───
function TagFilterDropdown({ allTags, selected, onChange, label, isActive, t }) {
 const [open, setOpen] = useState(false);
 const [search, setSearch] = useState('');
 const ref = useRef(null);
 const inputRef = useRef(null);

 useEffect(() => {
 if (!open) return;
 const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
 document.addEventListener('mousedown', h);
 return () => document.removeEventListener('mousedown', h);
 }, [open]);

 useEffect(() => {
 if (open && inputRef.current) inputRef.current.focus();
 }, [open]);

 const filtered = useMemo(() => {
 if (!search.trim()) return allTags;
 const q = search.toLowerCase();
 return allTags.filter(tg => tg.name.toLowerCase().includes(q));
 }, [allTags, search]);

 const toggle = (tagId) => {
 const id = String(tagId);
 if (selected.includes(id)) {
 onChange(selected.filter(s => s !== id));
 } else {
 onChange([...selected, id]);
 }
 };

 return (
 <div className="relative" ref={ref}>
 <button onClick={() => setOpen(!open)}
 className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${
 isActive ? 'bg-[#338edc] text-white border-[#338edc]' : 'border-line text-[var(--text-secondary)] hover:text-content hover:border-line-hover bg-surface-card'
 }`}>
 <Tag className="w-3 h-3" />
 <span className="max-w-[120px] truncate">{label}</span>
 {isActive && (
 <span className="ml-0.5 hover:bg-white/20 rounded-full p-0.5" onClick={e => { e.stopPropagation(); onChange([]); setOpen(false); }}>
 <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
 </span>
 )}
 {!isActive && <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>}
 </button>
 {open && (
 <div className="absolute left-0 top-full mt-1 w-56 bg-surface-elevated border border-line rounded-lg shadow-sm z-50 overflow-hidden">
 <div className="p-1.5">
 <input ref={inputRef} type="text" value={search} onChange={e => setSearch(e.target.value)}
 placeholder={t('cases.searchTags')}
 className="w-full px-2.5 py-1.5 text-xs bg-surface-inset border border-line rounded-lg text-content focus:outline-none focus:border-primary-500/50" />
 </div>
 <div className="max-h-56 overflow-y-auto py-1">
 {filtered.map(tg => {
 const checked = selected.includes(String(tg.id));
 return (
 <button key={tg.id} onClick={() => toggle(tg.id)}
 className="w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center gap-2 hover:bg-surface-hover">
 <span className={`w-3.5 h-3.5 rounded flex items-center justify-center border flex-shrink-0 transition-colors ${
 checked ? 'bg-[#338edc] border-[#338edc]' : 'border-line bg-surface-input'
 }`}>
 {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
 </span>
 <span className={`w-2 h-2 rounded-full flex-shrink-0 ${CASE_TAG_COLORS[tg.color]?.split(' ')[0] || 'bg-[var(--text-muted,#9CA3AF)]'}`} />
 <span className="truncate text-content">{tg.name}</span>
 </button>
 );
 })}
 {filtered.length === 0 && <div className="px-3 py-2 text-xs text-content-tertiary">{t('cases.noResults')}</div>}
 </div>
 </div>
 )}
 </div>
 );
}

// ─── #1505 Custom-Filter: Konfigurator-Popover + Toggle-Leiste ───
const CF_FELDER = ['tag', 'kunde', 'status', 'kategorie', 'quelle', 'zugewiesen'];

function cfFeldLabel(feld, t) {
 const map = {
 tag: t('cases.cf.feldTag', 'Tag'),
 kunde: t('cases.cf.feldKunde', 'Kunde'),
 status: t('cases.cf.feldStatus', 'Status'),
 kategorie: t('cases.cf.feldKategorie', 'Kategorie'),
 quelle: t('cases.cf.feldQuelle', 'Quelle'),
 zugewiesen: t('cases.cf.feldZugewiesen', 'Zugewiesen'),
 };
 return map[feld] || feld;
}

// Multi-Wert-Auswahl (Checkbox-Panel) für EINE Bedingung
function CfValuePicker({ options, selected, onChange, t }) {
 const [open, setOpen] = useState(false);
 const [search, setSearch] = useState('');
 const ref = useRef(null);
 useEffect(() => {
 if (!open) return;
 const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
 document.addEventListener('mousedown', h);
 return () => document.removeEventListener('mousedown', h);
 }, [open]);
 const filtered = useMemo(() => {
 if (!search.trim()) return options;
 const q = search.toLowerCase();
 return options.filter(o => o.label.toLowerCase().includes(q));
 }, [options, search]);
 const toggle = (val) => {
 const v = String(val);
 if (selected.includes(v)) onChange(selected.filter(s => s !== v));
 else onChange([...selected, v]);
 };
 const label = selected.length === 0
 ? t('cases.cf.werteWaehlen', 'Werte wählen…')
 : t('cases.cf.werteCount', { count: selected.length, defaultValue: `${selected.length} gewählt` });
 // Inline (kein absolute) → wird vom overflow-y-auto-Container der Bedingungen nicht abgeschnitten
 return (
 <div className="w-full min-w-0" ref={ref}>
 <button type="button" onClick={() => setOpen(!open)}
 className={`w-full flex items-center justify-between gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${
 selected.length > 0 ? 'border-[#338edc] text-content bg-surface-card' : 'border-line text-[var(--text-secondary)] bg-surface-card hover:border-line-hover'
 }`}>
 <span className="truncate">{label}</span>
 <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>
 </button>
 {open && (
 <div className="mt-1 w-full bg-surface-inset border border-line rounded-lg overflow-hidden">
 {options.length > 6 && (
 <div className="p-1.5">
 <input type="text" autoFocus value={search} onChange={e => setSearch(e.target.value)}
 placeholder={t('cases.cf.suchen', 'Suchen…')}
 className="w-full px-2.5 py-1.5 text-xs bg-surface-card border border-line rounded-lg text-content focus:outline-none focus:border-primary-500/50" />
 </div>
 )}
 <div className="max-h-40 overflow-y-auto py-1">
 {filtered.map(o => {
 const checked = selected.includes(String(o.value));
 return (
 <button type="button" key={o.value} onClick={() => toggle(o.value)}
 className="w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center gap-2 hover:bg-surface-hover">
 <span className={`w-3.5 h-3.5 rounded flex items-center justify-center border flex-shrink-0 transition-colors ${
 checked ? 'bg-[#338edc] border-[#338edc]' : 'border-line bg-surface-card'
 }`}>
 {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
 </span>
 <span className="truncate text-content">{o.label}</span>
 </button>
 );
 })}
 {filtered.length === 0 && <div className="px-3 py-2 text-xs text-content-tertiary">{t('cases.noResults')}</div>}
 </div>
 </div>
 )}
 </div>
 );
}

// "+"-Button + Konfigurator-Popover + Liste der gespeicherten Custom-Filter (Toggle-Chips)
function CasesCustomFilters({ filters, optionsFor, onReload, t }) {
 const [editing, setEditing] = useState(null); // null = zu, {} oder Filter = offen
 const ref = useRef(null);
 const [name, setName] = useState('');
 const [bedingungen, setBedingungen] = useState([]);
 const [saving, setSaving] = useState(false);
 const [err, setErr] = useState('');

 useEffect(() => {
 if (!editing) return;
 const h = e => { if (ref.current && !ref.current.contains(e.target)) setEditing(null); };
 document.addEventListener('mousedown', h);
 return () => document.removeEventListener('mousedown', h);
 }, [editing]);

 const openNew = () => {
 setName('');
 setBedingungen([{ feld: 'tag', operator: 'ist', werte: [] }]);
 setErr('');
 setEditing({});
 };
 const openEdit = (f) => {
 setName(f.name || '');
 setBedingungen((f.bedingungen || []).map(b => ({ feld: b.feld, operator: b.operator, werte: (b.werte || []).map(String) })));
 setErr('');
 setEditing(f);
 };
 const setCond = (i, patch) => setBedingungen(bs => bs.map((b, idx) => idx === i ? { ...b, ...patch } : b));
 const addCond = () => setBedingungen(bs => [...bs, { feld: 'tag', operator: 'ist', werte: [] }]);
 const removeCond = (i) => setBedingungen(bs => bs.filter((_, idx) => idx !== i));

 const save = async () => {
 const nm = name.trim();
 if (!nm) { setErr(t('cases.cf.nameErforderlich', 'Name erforderlich')); return; }
 const clean = bedingungen.filter(b => Array.isArray(b.werte) && b.werte.length > 0);
 if (clean.length === 0) { setErr(t('cases.cf.mindEineBedingung', 'Mindestens eine Bedingung mit Wert nötig')); return; }
 setSaving(true); setErr('');
 try {
 const isEdit = editing && editing.id;
 await api(isEdit ? `/api/cases/custom-filters/${editing.id}` : '/api/cases/custom-filters', {
 method: isEdit ? 'PUT' : 'POST',
 body: { name: nm, bedingungen: clean, aktiv: isEdit ? editing.aktiv : true },
 });
 setEditing(null);
 onReload();
 } catch (e) {
 setErr(e.message || 'Fehler');
 } finally { setSaving(false); }
 };
 const toggle = async (f) => {
 try { await api(`/api/cases/custom-filters/${f.id}/toggle`, { method: 'PATCH', body: { aktiv: !f.aktiv } }); onReload(); } catch {}
 };
 const del = async () => {
 if (!editing || !editing.id) return;
 try { await api(`/api/cases/custom-filters/${editing.id}`, { method: 'DELETE' }); setEditing(null); onReload(); } catch {}
 };

 return (
 <div className="relative flex items-center gap-2 flex-wrap" ref={ref}>
 {filters.map(f => (
 <span key={f.id} className={`group inline-flex items-center rounded-lg border text-[11px] font-semibold transition-colors ${
 f.aktiv ? 'bg-[#338edc] text-white border-[#338edc]' : 'border-line text-[var(--text-secondary)] bg-surface-card hover:border-line-hover'
 }`}>
 <button type="button" onClick={() => toggle(f)} className="pl-3 pr-1.5 py-1.5 truncate max-w-[160px]" title={f.name}>{f.name}</button>
 <button type="button" onClick={() => openEdit(f)} aria-label={t('common.edit', 'Bearbeiten')}
 className={`pr-2 pl-0.5 py-1.5 rounded-r-lg ${f.aktiv ? 'hover:bg-white/20' : 'hover:bg-surface-hover'}`}>
 <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
 </button>
 </span>
 ))}

 <button type="button" onClick={openNew} aria-label={t('cases.cf.add', 'Custom-Filter hinzufügen')}
 className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border border-dashed border-line text-[var(--text-secondary)] bg-surface-card hover:text-content hover:border-[#338edc] transition-colors">
 <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
 <span>{t('cases.cf.add', 'Filter')}</span>
 </button>

 {editing && (
 <div className="absolute left-0 top-full mt-1 w-[380px] bg-surface-elevated border border-line rounded-lg shadow-sm z-50 p-3">
 <div className="text-xs font-bold text-content mb-2">{editing.id ? t('cases.cf.bearbeiten', 'Filter bearbeiten') : t('cases.cf.neu', 'Neuer Custom-Filter')}</div>
 <input type="text" value={name} onChange={e => setName(e.target.value)} maxLength={120}
 placeholder={t('cases.cf.namePlaceholder', 'Name (z. B. „Ohne Spam-Tag")')}
 className="w-full px-2.5 py-1.5 mb-2 text-xs bg-surface-inset border border-line rounded-lg text-content focus:outline-none focus:border-primary-500/50" />
 <div className="space-y-2 max-h-[320px] overflow-y-auto pr-0.5">
 {bedingungen.map((c, i) => (
 <div key={i}>
 {i > 0 && <div className="text-[10px] font-bold text-content-tertiary py-0.5">{t('cases.cf.und', 'UND')}</div>}
 <div className="rounded-lg border border-line bg-surface-card/40 p-2 space-y-1.5">
 <div className="flex items-center gap-1.5">
 <select value={c.feld} onChange={e => setCond(i, { feld: e.target.value, werte: [] })}
 className="flex-1 min-w-0 px-2 py-1.5 rounded-lg text-[11px] font-semibold border border-line text-content bg-surface-card focus:outline-none cursor-pointer">
 {CF_FELDER.map(f => <option key={f} value={f}>{cfFeldLabel(f, t)}</option>)}
 </select>
 <select value={c.operator} onChange={e => setCond(i, { operator: e.target.value })}
 className="flex-1 min-w-0 px-2 py-1.5 rounded-lg text-[11px] font-semibold border border-line text-content bg-surface-card focus:outline-none cursor-pointer">
 <option value="ist">{t('cases.cf.ist', 'ist')}</option>
 <option value="ist_nicht">{t('cases.cf.istNicht', 'ist nicht')}</option>
 </select>
 <button type="button" onClick={() => removeCond(i)} aria-label={t('common.remove', 'Entfernen')}
 className="p-1 rounded-lg text-content-tertiary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0">
 <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
 </button>
 </div>
 <CfValuePicker options={optionsFor(c.feld)} selected={c.werte} onChange={(w) => setCond(i, { werte: w })} t={t} />
 </div>
 </div>
 ))}
 </div>
 <button type="button" onClick={addCond}
 className="mt-2 flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold text-[#338edc] hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
 <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
 {t('cases.cf.bedingungHinzufuegen', 'Bedingung hinzufügen')}
 </button>
 {err && <div className="mt-2 text-[11px] text-red-500">{err}</div>}
 <div className="flex items-center justify-between mt-3 pt-2 border-t border-line">
 <div>
 {editing.id && (
 <button type="button" onClick={del}
 className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
 {t('common.delete', 'Löschen')}
 </button>
 )}
 </div>
 <div className="flex items-center gap-2">
 <button type="button" onClick={() => setEditing(null)}
 className="px-3 py-1.5 rounded-lg text-[11px] font-semibold border border-line text-[var(--text-secondary)] hover:text-content transition-colors">
 {t('common.cancel', 'Abbrechen')}
 </button>
 <button type="button" onClick={save} disabled={saving}
 className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
 style={{ backgroundColor: '#338edc' }}>
 {saving ? t('common.saving', 'Speichern…') : t('common.save', 'Speichern')}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}

// ─── Column Settings Dropdown ───
function ColumnSettings({ columns, onToggle, onReorder, onReset, t }) {
 const [open, setOpen] = useState(false);
 const ref = useRef(null);
 const dragIdx = useRef(null);

 useEffect(() => {
 if (!open) return;
 const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
 document.addEventListener('mousedown', handler);
 return () => document.removeEventListener('mousedown', handler);
 }, [open]);

 return (
 <div className="relative" ref={ref}>
 <button onClick={() => setOpen(!open)} className="p-1.5 rounded hover:bg-surface-hover transition-colors" title={t('cases.columns.settings')}>
 <svg className="w-4 h-4 text-content-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
 <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
 <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
 </svg>
 </button>
 {open && (
 <div className="absolute right-0 top-full mt-1 w-56 bg-surface-elevated border border-line rounded-lg shadow-sm z-50 py-2">
 <div className="px-3 pb-1.5 text-[10px] uppercase tracking-wider font-semibold text-content-tertiary">{t('cases.columns.settings')}</div>
 {columns.map((col, i) => (
 <div key={col.id} draggable
 onDragStart={() => { dragIdx.current = i; }}
 onDragOver={e => e.preventDefault()}
 onDrop={e => { e.preventDefault(); if (dragIdx.current !== null && dragIdx.current !== i) { onReorder(dragIdx.current, i); dragIdx.current = null; } }}
 className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-hover cursor-grab active:cursor-grabbing select-none"
 >
 <span className="text-content-tertiary text-xs">⋮⋮</span>
 <button onClick={() => onToggle(col.id)} className="flex items-center gap-2 flex-1 min-w-0">
 <span className={`w-4 h-4 rounded flex items-center justify-center border transition-colors flex-shrink-0 ${col.visible ? 'bg-[#338edc] border-[#338edc]' : 'border-line-input bg-surface-input'}`}>
 {col.visible && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
 </span>
 <span className="text-sm text-content truncate">{t(col.label)}</span>
 </button>
 </div>
 ))}
 <div className="border-t border-line mt-2 pt-2 px-3">
 <button onClick={() => { onReset(); setOpen(false); }} className="text-xs text-content-tertiary hover:text-[#338edc] transition-colors">{t('cases.columns.reset')}</button>
 </div>
 </div>
 )}
 </div>
 );
}

function ListView({ tasks, allTasks, stages, omsUsers, onOpen, onRefresh, rrId, doneId, waitingId, activeStage, onSetActiveStage, doneCount, setToast, watchedIds, toggleWatch, activeTimers = {}, listPresence = {}, currentUserId, vertretungFuerMap = null, t, lvUrgency = null, lvOnlyTimer = false, searchActive = false, searchPool = null, outsideMode = 'on_click', showOutside = false, onShowOutside = null, searchImmune = false, searchStrictKeys = null }) {
 // #1267 — Optionale, view-lokale Spalte „Vertretung für": NICHT Teil der konfigurierbaren
 // Spalten (DEFAULT_COLUMNS/localStorage), nur sichtbar wenn die Map gesetzt ist (Vertretungs-View).
 const showVertretungCol = !!vertretungFuerMap;
 const { getStageLabel } = useLanguage();
 const listNavigate = useNavigate();
 // #1352 Stufe 1 — Default-Sort „neuestes oben" (create_date DESC), ladeunabhängig.
 // SLA-Sort + alle anderen Spalten bleiben per Klick wählbar (nur der Default ändert sich).
 const [sortCol, setSortCol] = useState('create_date');
 const [sortDir, setSortDir] = useState('desc');
 // Client-seitige Paginierung über den gefilterten + sortierten Voll-Satz (Slice).
 // Suche/Filter/Vertretung/Stage-Counts arbeiten unverändert auf dem Voll-Satz vor dem Slice.
 const [page, setPage] = useState(1);
 const [pageSize, setPageSize] = useState(() => {
   const v = Number(localStorage.getItem('weship_cases_page_size'));
   return [25, 50, 100, 200].includes(v) ? v : 50;
 });
 const [columns, setColumns] = useState(() => loadColumnConfig() || DEFAULT_COLUMNS.map(c => ({ ...c, width: c.defaultWidth, visible: true })));
 const [selected, setSelected] = useState([]);
 // #1289: Status-Filter (Stage/Urgency/Timer) wird jetzt im Parent (CasesPage) in der sticky
 // Filterleiste gerendert; ListView erhält nur noch die Steuer-Werte als Props (lvUrgency, lvOnlyTimer)
 // und filtert damit die sichtbaren Zeilen. activeStage/onSetActiveStage kommen ebenfalls vom Parent.

 // #1032 — Defensive Sync: wenn stages noch nicht geladen waren beim Init und activeStage
 // auf einer Stage steht die nicht (mehr) existiert → reset auf 'all'.
 useEffect(() => {
 if (activeStage === 'all' || activeStage === 'done') return;
 if (!stages || stages.length === 0) return;
 const exists = stages.some(s => s.key === activeStage);
 if (!exists) onSetActiveStage('all');
 }, [stages, activeStage]);

 // Urgency metrics (SLA breached + overdue >24h) — exclude done + paused cases
 // #1062: SSoT via getEffectiveSla — fängt First-Response-Breach UND Resolution-Breach
 const urgencyMetrics = useMemo(() => {
 const slaBreached = tasks.filter(tk => {
 if (tk.stage_key === 'done' || tk.sla_pausiert) return false;
 const eff = getEffectiveSla(tk, t);
 return eff && eff.isOverdue && !eff.isPaused;
 });
 const overdue24h = tasks.filter(tk => tk.stage_key === 'waiting_for_support' && hrsSince(tk.date_last_stage_update) > 24);
 return { slaBreached, overdue24h };
 }, [tasks, t]);

 const toggle = (col) => {
 if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
 else { setSortCol(col); setSortDir(col === 'partner' ? 'asc' : 'desc'); }
 };

 const visibleTasks = useMemo(() => {
 let list = tasks;
 // #1801 — Filter-Respekt: der frühere #1365-Bypass (Suche überstimmt Stage-/Urgency-/
 // Timer-Filter) ist entfernt — Chip-Treffer laufen durch dieselben Zeilen-Filter;
 // ausgefilterte Treffer erreichen die Nutzerin über die Outside-Sektion (searchPool).
 // #1801-Fix (Regel 2.1) — AUSNAHME: Case-Nr-Suche ist filter-immun, alle Treffer
 // bleiben sichtbar (Badge markiert nicht-filterkonforme Zeilen, Outside-Menge = ∅).
 if (searchActive && searchImmune) return list;
 if (lvOnlyTimer) list = list.filter(t => !!activeTimers[t.id]);
 if (lvUrgency === 'sla') {
 const ids = new Set(urgencyMetrics.slaBreached.map(t => t.id));
 list = list.filter(t => ids.has(t.id));
 } else if (lvUrgency === 'overdue24h') {
 const ids = new Set(urgencyMetrics.overdue24h.map(t => t.id));
 list = list.filter(t => ids.has(t.id));
 } else if (activeStage !== 'all') {
 list = list.filter(t => t.stage_key === activeStage);
 }
 return list;
 }, [tasks, activeStage, lvUrgency, urgencyMetrics, lvOnlyTimer, activeTimers, searchActive, searchImmune]);

 // #1801-Fix — Badge „außerhalb Filter: <Status>" für immune Treffer, die die normale
 // Filterkette (searchStrictKeys) ODER die Zeilen-Filter (Stage/Done/Urgency/Timer)
 // nicht bestanden hätten.
 const isImmuneOutside = useMemo(() => {
 if (!searchActive || !searchImmune) return () => false;
 return (tk) => {
 const key = `${tk.source === 'oms' ? 'oms' : 'local'}:${tk.id}`;
 if (searchStrictKeys && !searchStrictKeys.has(key)) return true;
 if (tk.stage_key === 'done') return activeStage !== 'done';
 if (lvOnlyTimer && !activeTimers[tk.id]) return true;
 if (lvUrgency === 'sla' && !urgencyMetrics.slaBreached.some(u => u.id === tk.id)) return true;
 if (lvUrgency === 'overdue24h' && !urgencyMetrics.overdue24h.some(u => u.id === tk.id)) return true;
 if (!lvUrgency && activeStage !== 'all' && tk.stage_key !== activeStage) return true;
 return false;
 };
 }, [searchActive, searchImmune, searchStrictKeys, activeStage, lvOnlyTimer, activeTimers, lvUrgency, urgencyMetrics]);

 // #1801 — Treffer außerhalb der aktiven Filter (2.2): Differenz voller Chip-Satz (searchPool)
 // minus sichtbare Zeilen — aus DERSELBEN Abfrage, keine zweite Vollsuche. Dedupe-Key ist
 // source-bewusst (#1499/#1507: lokale und OMS-IDs kollidieren numerisch).
 const outsideTasks = useMemo(() => {
 if (!searchActive || !searchPool || !searchPool.length) return [];
 const visKeys = new Set(visibleTasks.map(tk => `${tk.source === 'oms' ? 'oms' : 'local'}:${tk.id}`));
 return searchPool.filter(tk => !visKeys.has(`${tk.source === 'oms' ? 'oms' : 'local'}:${tk.id}`));
 }, [searchActive, searchPool, visibleTasks]);
 const outsideVisible = outsideTasks.length > 0 && (outsideMode === 'auto' || showOutside);

 // Clear selection when filters change
 useEffect(() => { setSelected([]); }, [activeStage, lvUrgency, lvOnlyTimer]);

 // Escape to clear selection
 useEffect(() => {
 const h = e => { if (e.key === 'Escape' && selected.length > 0) setSelected([]); };
 window.addEventListener('keydown', h);
 return () => window.removeEventListener('keydown', h);
 }, [selected.length]);

 const sorted = useMemo(() => {
 const arr = [...visibleTasks];
 arr.sort((a, b) => {
 let va, vb;
 switch (sortCol) {
 case 'priority': va = Number(a.priority||0); vb = Number(b.priority||0); break;
 case 'name': va = (a.name||'').toLowerCase(); vb = (b.name||'').toLowerCase(); return sortDir==='asc' ? (va<vb?-1:va>vb?1:0) : (vb<va?-1:vb>va?1:0);
 case 'partner': va = (a.company_name||a.partner_name||'').toLowerCase(); vb = (b.company_name||b.partner_name||'').toLowerCase(); return sortDir==='asc' ? (va<vb?-1:va>vb?1:0) : (vb<va?-1:vb>va?1:0);
 case 'create_date': va = new Date(a.create_date||0).getTime(); vb = new Date(b.create_date||0).getTime(); break;
 case 'write_date': va = new Date(a.write_date||0).getTime(); vb = new Date(b.write_date||0).getTime(); break;
 case 'messages': va = a.message_count||0; vb = b.message_count||0; break;
 case 'stage': va = (a.stage_key||''); vb = (b.stage_key||''); return sortDir==='asc' ? (va<vb?-1:va>vb?1:0) : (vb<va?-1:vb>va?1:0);
 case 'sla': va = a.sla_prozent||0; vb = b.sla_prozent||0; break;
 default: va = 0; vb = 0;
 }
 return sortDir === 'asc' ? va - vb : vb - va;
 });
 return arr;
 }, [visibleTasks, sortCol, sortDir]);

 // ── Paginierung ──
 const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
 // Bei Wechsel von Filter/Sortierung/Suche zurück auf Seite 1 (NICHT bei Auto-Refresh
 // von `tasks`, damit die aktuelle Seite während des 30s-Reloads erhalten bleibt).
 useEffect(() => { setPage(1); }, [activeStage, lvUrgency, lvOnlyTimer, sortCol, sortDir, searchActive]);
 // Clamp, falls der gefilterte Satz schrumpft und die aktuelle Seite leer würde.
 useEffect(() => { if (page > pageCount) setPage(pageCount); }, [pageCount, page]);
 const paged = useMemo(() => {
 const start = (page - 1) * pageSize;
 return sorted.slice(start, start + pageSize);
 }, [sorted, page, pageSize]);
 const pageItems = useMemo(() => {
 const total = pageCount, span = 1, items = [];
 if (total <= 7) { for (let p = 1; p <= total; p++) items.push(p); return items; }
 items.push(1);
 if (page - span > 2) items.push('…');
 for (let p = Math.max(2, page - span); p <= Math.min(total - 1, page + span); p++) items.push(p);
 if (page + span < total - 1) items.push('…');
 items.push(total);
 return items;
 }, [pageCount, page]);

 const arrow = (col) => sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
 // #1248: doneCount kommt jetzt als Prop vom Parent (voller ~868-Zähler, da `tasks` done ausblendet).

 const visCols = useMemo(() => columns.filter(c => c.visible), [columns]);
 const selectedSet = useMemo(() => new Set(selected), [selected]);

 // ─── Selection ───
 const toggleSelect = useCallback((id) => {
 setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
 }, []);
 const toggleSelectAll = useCallback(() => {
 setSelected(prev => prev.length === sorted.length ? [] : sorted.map(t => t.id));
 }, [sorted]);

 // ─── Bulk Action ───
 const handleBulk = useCallback(async (action, value) => {
 if (selected.length === 0) return;
 try {
 const body = { case_ids: selected, action, value };
 if (action === 'stage') {
 const st = stages.find(s => s.key === value);
 if (st) { body.value = st.id; body.stage_name = st.name; }
 }
 const res = await api('/api/cases/bulk', { method: 'POST', body });
 setToast?.({ message: t('cases.bulk.success', { count: res.updated || selected.length }), type: 'success' });
 setSelected([]);
 onRefresh?.();
 } catch (err) {
 setToast?.({ message: t('cases.bulk.failed'), type: 'error' });
 }
 }, [selected, stages, onRefresh, setToast, t]);

 const allSelectedEmail = useMemo(() => {
 if (selected.length === 0) return false;
 return selected.every(id => { const tk = tasks.find(t => t.id === id); return tk?.source === 'email'; });
 }, [selected, tasks]);

 const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

 const handleBulkDelete = useCallback(() => {
 if (selected.length === 0 || !allSelectedEmail) return;
 setConfirmBulkDelete(true);
 }, [selected, allSelectedEmail]);

 const executeBulkDelete = useCallback(async () => {
 setConfirmBulkDelete(false);
 try {
 const res = await api('/api/cases/bulk-delete', { method: 'POST', body: { case_ids: selected } });
 setToast?.({ message: t('cases.bulk.deleteSuccess', { count: res.deleted || selected.length }), type: 'success' });
 setSelected([]);
 onRefresh?.();
 } catch (err) {
 setToast?.({ message: t('cases.bulk.failed'), type: 'error' });
 }
 }, [selected, onRefresh, setToast, t]);

 // Persist column config
 const updateColumns = useCallback((updater) => {
 setColumns(prev => {
 const next = typeof updater === 'function' ? updater(prev) : updater;
 saveColumnConfig(next);
 return next;
 });
 }, []);

 const handleResize = useCallback((colId, e) => {
 e.preventDefault();
 const startX = e.clientX;
 const col = columns.find(c => c.id === colId);
 const startW = col?.width || 150;
 const minW = col?.minWidth || 60;

 const onMove = (ev) => {
 const delta = ev.clientX - startX;
 const newW = Math.max(minW, startW + delta);
 setColumns(prev => prev.map(c => c.id === colId ? { ...c, width: newW } : c));
 };
 const onUp = () => {
 document.removeEventListener('mousemove', onMove);
 document.removeEventListener('mouseup', onUp);
 document.body.style.cursor = '';
 document.body.style.userSelect = '';
 setColumns(prev => { saveColumnConfig(prev); return prev; });
 };
 document.body.style.cursor = 'col-resize';
 document.body.style.userSelect = 'none';
 document.addEventListener('mousemove', onMove);
 document.addEventListener('mouseup', onUp);
 }, [columns]);

 const resetColumns = useCallback(() => {
 const defaults = DEFAULT_COLUMNS.map(c => ({ ...c, width: c.defaultWidth, visible: true }));
 setColumns(defaults);
 localStorage.removeItem(COL_STORAGE_KEY);
 }, []);

 const toggleColumn = useCallback((colId) => {
 updateColumns(prev => prev.map(c => c.id === colId ? { ...c, visible: !c.visible } : c));
 }, [updateColumns]);

 const reorderColumns = useCallback((from, to) => {
 updateColumns(prev => {
 const arr = [...prev];
 const [moved] = arr.splice(from, 1);
 arr.splice(to, 0, moved);
 return arr;
 });
 }, [updateColumns]);

 const CheckBox = ({ checked, onClick, label, count }) => (
 <button onClick={onClick} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-line text-xs font-medium transition-colors hover:border-line-hover">
 <span className={`w-4 h-4 rounded flex items-center justify-center border transition-colors ${checked ? 'bg-[#338edc] border-[#338edc]' : 'border-line-input bg-surface-input'}`}>
 {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
 </span>
 <span className="text-[var(--text-secondary)]">{label}</span>
 {checked && count > 0 && <span className="text-[10px] text-content-tertiary">({count})</span>}
 </button>
 );

 // ─── Cell Renderer ───
 function renderCell(colId, tk) {
 switch (colId) {
 case 'ticket_id': {
 const tInfo = activeTimers[tk.id];
 const tPaused = tInfo?.timer_state === 'paused';
 // #1248 — Linked-Claims Pill (Link2-Icon + Counter + Indigo-Activity-Dot)
 const linkedCount = tk.linked_claims_count || 0;
 const linkedNums = Array.isArray(tk.linked_claim_numbers) ? tk.linked_claim_numbers.filter(Boolean) : [];
 const linkedActive = !!tk.has_active_linked_claims;
 const tooltipText = linkedNums.length
 ? t('cases.links.tooltip', 'Verknüpfte Klärung(en): {nums}').replace('{nums}', linkedNums.join(', '))
 : t('cases.links.tooltipPlural', '{n} verknüpfte Klärung(en)').replace('{n}', String(linkedCount));
 return (
 <span className="flex items-center gap-1.5">
 <Eye className={`w-3.5 h-3.5 cursor-pointer transition-colors flex-shrink-0 ${
 watchedIds.has(tk.id) ? 'text-[#338edc]' : 'text-transparent group-hover:text-[var(--text-tertiary)]'
 }`} onClick={e => { e.stopPropagation(); toggleWatch(tk.id); }} />
 {tInfo && (
 <span
 className="relative flex h-2 w-2 flex-shrink-0"
 title={`${tPaused ? t('zeiterfassung.cases.tooltipPaused', 'Timer pausiert') : t('zeiterfassung.cases.tooltipRunning', 'Timer läuft')} — ${tInfo.ma_name || ''}${tInfo.kategorie_name ? ' · ' + tInfo.kategorie_name : ''}${tInfo.n > 1 ? ` (+${tInfo.n - 1})` : ''}`}
 >
 {!tPaused && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />}
 <span className={`relative inline-flex rounded-full h-2 w-2 ${tPaused ? 'bg-amber-500' : 'bg-blue-500'}`} />
 </span>
 )}
 <span className="text-sm font-medium text-content">{formatCaseId(tk.id, tk.display_source || tk.source)}</span>
 {linkedCount > 0 && (
 <button
 type="button"
 onClick={(e) => { e.stopPropagation(); listNavigate(`/cases/${tk.id}?tab=claims`); }}
 title={tooltipText}
 className="inline-flex items-center gap-0.5 text-[var(--text-tertiary,#6B7280)] hover:text-indigo-500 transition-colors"
 >
 <Link2 className="w-3.5 h-3.5" />
 {linkedCount > 1 && <span className="text-[11px] font-medium">{linkedCount}</span>}
 {linkedActive && <span className="ml-0.5 w-2 h-2 rounded-full bg-indigo-500" title={t('cases.links.activity', 'Aktivität seit letztem Besuch')} />}
 </button>
 )}
 </span>
 );
 }
 case 'kunde': {
 // #1350 Teil B — bei fehlender Kundenzuordnung Absender-Name → E-Mail → Kein Unternehmen
 // #1482 — Hotline-Cases: Anrufername (Fallback Rückrufnummer) vor Absender, statt "Kein Unternehmen"
 const name = tk.company_name || tk.partner_name || tk.caller_name || tk.absender_name || tk.caller_number || tk.kunde_email || t('cases.noCompany');
 return (
 <div className="flex items-center gap-2.5 min-w-0">
 <KundenAvatar name={name} />
 {tk.partner_id
 ? <Link to={`/kunden/${tk.partner_id}`} className="text-sm font-medium text-content hover:text-[#338edc] transition-colors truncate" onClick={e => e.stopPropagation()}>{name}</Link>
 : <span className="text-sm font-medium text-content truncate">{name}</span>
 }
 </div>
 );
 }
 case 'titel': {
 const hasFlowAi = (tk.tags || []).some(tg => (tg.name || '').toLowerCase().includes('missing_nr'));
 return (
 <div className="relative min-w-0" title={tk.name} style={hasFlowAi ? { paddingLeft: 28 } : undefined}>
 {hasFlowAi && (
 <span style={{
 position: 'absolute', top: '50%', left: 0,
 transform: 'translateY(-50%)',
 background: 'linear-gradient(135deg, #338edc, #6366F1)',
 color: '#FFF', fontSize: 10, fontWeight: 700,
 width: 20, height: 20, borderRadius: '50%',
 display: 'flex', alignItems: 'center', justifyContent: 'center',
 pointerEvents: 'none', flexShrink: 0,
 boxShadow: '0 1px 4px rgba(51,142,220,0.4)', zIndex: 10,
 }}>✦</span>
 )}
 <div className="flex items-center gap-1.5 font-medium text-content">
 {tk.source === 'email' && (
 <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400" title="Email">
 <Mail className="w-3 h-3" />
 </span>
 )}
 <span className="truncate min-w-0 flex-1">{tk.name}</span>
 {tk.has_own_draft && (
 <span
 className="flex-shrink-0 inline-flex items-center"
 title={t('cases.ownDraftAria', 'Eigener Entwurf vorhanden')}
 aria-label={t('cases.ownDraftAria', 'Eigener Entwurf vorhanden')}
 >
 <PencilLine className="w-3.5 h-3.5 text-[var(--text-tertiary,#6B7280)]" aria-hidden="true" />
 </span>
 )}
 </div>
 {tk.ai_summary && <div className="text-[10px] text-content-tertiary truncate mt-0.5">{tk.ai_summary}</div>}
 </div>
 );
 }
 case 'status':
 return (
 <select
 className="text-xs bg-transparent border-0 cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5 text-content focus:outline-none"
 value={tk.stage_key || ''}
 onClick={e => e.stopPropagation()}
 onChange={async e => {
 e.stopPropagation();
 const newKey = e.target.value;
 const st = stages.find(s => s.key === newKey);
 if (!st) return;
 try { await api(`/api/cases/${tk.id}/stage${tk.source === 'oms' ? '?src=oms' : ''}`, { method: 'PUT', body: { stage_id: st.id, stage_name: st.name } }); onRefresh?.(); } catch {}
 }}
 >
 {stages.map(s => <option key={s.key} value={s.key}>{getStageLabel(s.name)}</option>)}
 </select>
 );
 case 'prio':
 return <Stars value={tk.priority} onChange={async p => { try { await api(`/api/cases/${tk.id}/priority${tk.source === 'oms' ? '?src=oms' : ''}`,{method:'PUT',body:{priority:p}}); onRefresh?.(); } catch {} }} />;
 case 'sla': {
 // #1062 SSoT via getEffectiveSla() — Manuel-Priorität First-Response > Resolution
 const eff = getEffectiveSla(tk, t);
 if (!eff) return <span className="text-xs text-content-tertiary">–</span>;
 if (eff.kind === 'stopped') {
 return (
 <div className={`text-[12px] font-semibold ${eff.colorCls}`}>
 {eff.isOverdue ? '❌' : '✅'} {eff.label}
 </div>
 );
 }
 if (eff.kind === 'paused') {
 const isOnTime = tk.sla_first_response_met === true || (tk.sla_first_response_met == null && !tk.sla_overdue);
 return (
 <div className="leading-tight">
 <div className="text-[12px] text-[#9CA3AF]">⏸ {eff.label}</div>
 <div className={`text-[11px] ${isOnTime ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
 {isOnTime ? t('sla.responseOnTime') : t('sla.responseLate')}
 </div>
 </div>
 );
 }
 return (
 <div className="flex items-center gap-1">
 <span className="relative flex h-2 w-2 shrink-0">
 {eff.pulse && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />}
 <span className={`relative inline-flex rounded-full h-2 w-2 ${eff.dotCls}`} />
 </span>
 <span className={`text-[10px] ${eff.colorCls}`}>{eff.label}</span>
 </div>
 );
 }
 case 'zugewiesen':
 return (
 <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
 {tk.assigned_users?.[0]?.name && (
 <div className="relative w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0 overflow-hidden" title={tk.assigned_users[0].name}>
 {tk.assigned_users[0].avatar_url ? (
 <img src={tk.assigned_users[0].avatar_url} alt={tk.assigned_users[0].name}
 className="w-full h-full object-cover"
 onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = 'flex'); }} />
 ) : null}
 <span style={{ display: tk.assigned_users[0].avatar_url ? 'none' : 'flex' }}
 className="absolute inset-0 items-center justify-center text-[9px] font-bold text-primary-600 dark:text-primary-400">
 {initials(tk.assigned_users[0].name)}
 </span>
 </div>
 )}
 <select
 className="text-xs bg-transparent border-0 cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5 max-w-[110px] truncate text-content focus:outline-none"
 value={tk.assigned_users?.[0]?.id || ''}
 onChange={async e => {
 const uid = e.target.value;
 try { await api(`/api/cases/${tk.id}/assign${tk.source === 'oms' ? '?src=oms' : ''}`, { method: 'PUT', body: { user_ids: uid ? [Number(uid)] : [] } }); onRefresh?.(); } catch {}
 }}
 >
 <option value="">–</option>
 {omsUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
 </select>
 </div>
 );
 case 'aktiv': {
 // #1081 Live-Presence: andere User aktuell im Case (self-filter)
 const uids = (listPresence[tk.id] || []).filter(uid => Number(uid) !== Number(currentUserId));
 if (!uids.length) return null;
 const users = uids.map(uid => {
 const u = omsUsers.find(o => Number(o.id) === Number(uid));
 return {
 user_id: uid,
 name: u?.name || `User ${uid}`,
 avatar_url: u?.avatar_url || `/api/users/${uid}/avatar`,
 last_active: new Date().toISOString(),
 };
 });
 return <div onClick={e => e.stopPropagation()}><AvatarStack users={users} t={t} currentUserId={currentUserId} max={3} compact /></div>;
 }
 case 'category':
 return tk.category_name
 ? <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${CASE_TAG_COLORS[tk.category_color] || CASE_TAG_COLORS.gray}`}>{tk.category_name}</span>
 : <span className="text-xs text-content-tertiary">–</span>;
 case 'tags':
 return (tk.local_tags || []).length > 0
 ? <div className="flex flex-wrap gap-1">{tk.local_tags.map(tg => <TagPill key={tg.id} tag={tg} />)}</div>
 : <span className="text-xs text-content-tertiary">–</span>;
 case 'ai':
 return <AiSuggestionBadge suggestion={tk.ai_suggestion} t={t} />;
 case 'erstellt': {
 const showLast = tk.last_message_at && tk.create_date &&
   (new Date(tk.last_message_at).getTime() - new Date(tk.create_date).getTime() > 60_000);
 return (
 <div className="flex flex-col gap-0.5 leading-tight">
 <span className="text-xs text-[var(--text-secondary)]" title={fmtDateTime(tk.create_date)}>{relTime(tk.create_date, t)}</span>
 {showLast && (
 <span className="text-[10px]" style={{ color: 'var(--text-muted)' }} title={fmtDateTime(tk.last_message_at)}>
 {t('cases.columns.lastMessageAgo', { ago: relTime(tk.last_message_at, t) })}
 </span>
 )}
 </div>
 );
 }
 default: return null;
 }
 }

 // Grid: checkbox col (36px) + configurable columns + optionale Vertretungs-Spalte (#1267)
 const gridCols = `36px ${visCols.map(c => c.id === 'titel' ? `minmax(${c.minWidth}px, 1fr)` : `${c.width}px`).join(' ')}${showVertretungCol ? ' 170px' : ''}`;

 const isAllSelected = sorted.length > 0 && selected.length === sorted.length;
 const isIndeterminate = selected.length > 0 && selected.length < sorted.length;

 return (
 <div className="flex-1 overflow-auto px-6 lg:px-8 pb-6">
 {/* #1289: Status-Filter ist in die sticky Filterleiste (Parent CasesPage) hochgezogen.
 Hier bleibt nur das Listen-Einstellungen-Zahnrad (rechtsbündig, weiterhin erreichbar). */}
 <div className="flex items-center justify-end gap-3 mb-3 flex-wrap">
 <div className="flex items-center gap-2 flex-shrink-0">
 <ColumnSettings columns={columns} onToggle={toggleColumn} onReorder={reorderColumns} onReset={resetColumns} t={t} />
 </div>
 </div>
 {/* Bulk Action Bar */}
 {selected.length > 0 && (
 <BulkActionBar count={selected.length} stages={stages} omsUsers={omsUsers} onBulk={handleBulk} onDelete={handleBulkDelete} allEmail={allSelectedEmail} onClear={() => setSelected([])} t={t} />
 )}
 <div className="overflow-x-auto">
 {/* Header */}
 <div className="grid border-b border-line text-left items-center" style={{ gridTemplateColumns: gridCols }}>
 {/* Select-all checkbox */}
 <div className="px-2 py-2 flex items-center justify-center">
 <input type="checkbox" checked={isAllSelected}
 ref={el => { if (el) el.indeterminate = isIndeterminate; }}
 onChange={toggleSelectAll}
 className="w-4 h-4 rounded border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)] text-[#338edc] focus:ring-[#338edc] cursor-pointer" />
 </div>
 {visCols.map(col => {
 const sortable = !!col.sortKey;
 return (
 <div key={col.id} className={`relative group py-2 px-2 text-[10px] uppercase tracking-wider font-semibold text-content-tertiary ${sortable ? 'cursor-pointer hover:text-content select-none' : ''}`}
 onClick={sortable ? () => toggle(col.sortKey) : undefined}>
 <span>{t(col.label)}{col.sortKey ? arrow(col.sortKey) : ''}</span>
 {/* Resize handle */}
 <div className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize opacity-0 group-hover:opacity-100 hover:!opacity-100 transition-opacity"
 onMouseDown={e => handleResize(col.id, e)} onClick={e => e.stopPropagation()}>
 <div className="w-px h-full mx-auto bg-[var(--text-muted,#9CA3AF)] dark:bg-[var(--text-secondary,#4B5563)] group-hover:bg-[#338edc]" />
 </div>
 </div>
 );
 })}
 {showVertretungCol && (
 <div className="py-2 px-2 text-[10px] uppercase tracking-wider font-semibold text-content-tertiary">
 {t('cases.vertretungFuer')}
 </div>
 )}
 </div>
 {/* Rows — #1801: renderRow wird für reguläre UND Outside-Zeilen genutzt (kein JSX-Duplikat) */}
 {(() => {
 const renderRow = (tk) => {
 // #1071-C — Row-Coloring an SLA-SSoT (#1062) gekoppelt statt Stage-Heuristik.
 // ROT bei effektivem Breach (First-Response- ODER Resolution-Overdue),
 // AMBER bei First-Response-Running mit <4h verbleibend (Frühwarnung).
 const eff = getEffectiveSla(tk, t);
 const isRedRow = !!(eff && eff.isOverdue);
 const isAmberRow = !isRedRow && !!(eff && eff.kind === 'first_response_running'
   && typeof eff.verblMin === 'number' && eff.verblMin <= 240);
 const isSel = selectedSet.has(tk.id);
 // #1058 Punkt 9: persist current sort/filter order so DetailModal can render Prev/Next arrows
 const handleRowOpen = () => {
 // #1499 — nav-list mit source speichern, damit Prev/Next die richtige Quelle treffen
 try { sessionStorage.setItem('weship_cases_nav_list', JSON.stringify(sorted.map(s => ({ id: s.id, source: s.source })))); } catch {}
 onOpen(tk.id, tk.source);
 };
 return (
 <div key={`${tk.source}-${tk.id}`} onClick={handleRowOpen}
 className={`group grid border-b border-line/50 cursor-pointer transition-colors hover:bg-surface-hover items-center ${isSel ? 'bg-primary-50 dark:bg-primary-900/10' : isRedRow ? 'bg-red-500/[0.03]' : isAmberRow ? 'bg-amber-500/[0.03]' : ''}`}
 style={{ gridTemplateColumns: gridCols }}>
 {/* Row checkbox */}
 <div className="px-2 py-2.5 flex items-center justify-center" onClick={e => e.stopPropagation()}>
 <input type="checkbox" checked={isSel} onChange={() => toggleSelect(tk.id)}
 className="w-4 h-4 rounded border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)] text-[#338edc] focus:ring-[#338edc] cursor-pointer" />
 </div>
 {visCols.map(col => (
 <div key={col.id} className="py-2.5 px-2 overflow-hidden">
 {renderCell(col.id, tk)}
 {/* #1801-Fix Regel 2.1 — Hinweis-Badge am filter-immunen Ticketnummern-Treffer */}
 {col.id === 'name' && isImmuneOutside(tk) && (
 <span className="inline-flex items-center mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/10 text-amber-600 border border-amber-500/30 whitespace-nowrap">
 {t('cases.csearch.outsideBadge', { defaultValue: 'außerhalb Filter: {{status}}', status: getStageLabel(tk.stage_name) || tk.stage_name })}
 </span>
 )}
 </div>
 ))}
 {showVertretungCol && (() => {
 const vName = (tk.assigned_users || []).map(au => vertretungFuerMap.get(Number(au.id))).find(Boolean);
 return (
 <div className="py-2.5 px-2 overflow-hidden">
 {vName ? (
 <div className="flex items-center gap-1.5 text-xs text-content-secondary">
 <User className="w-3 h-3 text-primary-400 flex-shrink-0" />
 <span className="truncate">{vName}</span>
 </div>
 ) : <span className="text-xs text-content-tertiary">–</span>}
 </div>
 );
 })()}
 </div>
 );
 };
 return (
 <>
 {paged.map(renderRow)}
 {/* #1801 Filter-Respekt 2.2 — Treffer außerhalb der aktiven Filter:
 on_click (Default) = dezente Klick-Zeile, auto = Sektion automatisch sichtbar */}
 {searchActive && outsideTasks.length > 0 && (
 outsideMode === 'on_click' && !showOutside ? (
 <button type="button" onClick={() => onShowOutside && onShowOutside(true)}
 className="w-full text-left px-3 py-2 text-xs font-semibold text-primary-600 border-t border-line hover:bg-surface-inset transition-colors">
 {t('cases.csearch.outsideRow', { defaultValue: '+ {{n}} weitere Treffer außerhalb deiner Filter', n: outsideTasks.length })} — {t('cases.csearch.outsideShow', 'anzeigen')}
 </button>
 ) : (
 <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-content-tertiary bg-surface-inset border-t border-line">
 {t('cases.csearch.outsideTitle', 'Außerhalb deiner Filter')} ({outsideTasks.length})
 </div>
 )
 )}
 {searchActive && outsideVisible && outsideTasks.map(renderRow)}
 </>
 );
 })()}
 </div>
 {sorted.length === 0 && !(searchActive && outsideTasks.length > 0) && <div className="text-center text-content-tertiary py-12 text-sm">{t('cases.noCasesFound')}</div>}

 {/* Paginierung — Seitenzahlen zum Weiterschalten + Seitengröße */}
 {sorted.length > 0 && (
 <div className="flex items-center justify-between gap-4 mt-4 flex-wrap text-xs">
 <div className="text-content-tertiary">
 {t('cases.pagination.range', { from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, sorted.length), total: sorted.length })}
 </div>
 {pageCount > 1 && (
 <div className="flex items-center gap-1">
 <button type="button" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
 className="px-2.5 py-1 rounded-md border border-line text-content-secondary hover:text-content disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
 {t('cases.pagination.prev')}
 </button>
 {pageItems.map((it, i) => it === '…'
 ? <span key={`e${i}`} className="px-1.5 text-content-tertiary select-none">…</span>
 : <button key={it} type="button" onClick={() => setPage(it)}
 className={`min-w-[30px] px-2 py-1 rounded-md border transition-colors ${it === page ? 'border-[#338edc] text-[#338edc] font-semibold' : 'border-line text-content-secondary hover:text-content'}`}>
 {it}
 </button>
 )}
 <button type="button" disabled={page >= pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))}
 className="px-2.5 py-1 rounded-md border border-line text-content-secondary hover:text-content disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
 {t('cases.pagination.next')}
 </button>
 </div>
 )}
 <div className="flex items-center gap-2">
 <select value={pageSize}
 onChange={e => { const v = Number(e.target.value); setPageSize(v); setPage(1); localStorage.setItem('weship_cases_page_size', String(v)); }}
 className="px-2 py-1 rounded-md border border-line bg-surface text-content-secondary cursor-pointer focus:outline-none focus:border-[#338edc]">
 {[25, 50, 100, 200].map(s => <option key={s} value={s}>{s}</option>)}
 </select>
 <span className="text-content-tertiary">{t('cases.pagination.perPage')}</span>
 </div>
 </div>
 )}

 <ConfirmDialog
 open={confirmBulkDelete}
 onCancel={() => setConfirmBulkDelete(false)}
 onConfirm={executeBulkDelete}
 title={t('cases.bulk.delete')}
 message={t('cases.bulk.deleteConfirm', { count: selected.length })}
 confirmLabel={t('common.delete')}
 cancelLabel={t('common.cancel')}
 variant="danger"
 />
 </div>
 );
}


// ════════════════════════════════════════════════════════════════
// URGENCY PANEL — SLA breached / Overdue / Unassigned counts
// ════════════════════════════════════════════════════════════════
function UrgencyPanel({ metrics, urgencyFilter, setUrgencyFilter, setPreviewId, t }) {
 const items = [
 { key: 'sla', count: metrics.slaBreached.length, label: t('cases.slaBreached'), color: 'red' },
 { key: 'overdue24h', count: metrics.overdue24h.length, label: t('cases.overdue24h'), color: 'amber' },
 { key: 'unassigned', count: metrics.unassigned.length, label: t('cases.unassignedCount'), color: 'gray' },
 ];

 return (
 <div className="p-3 border-b border-line">
 <h3 className="text-[10px] font-bold text-content-tertiary uppercase tracking-widest mb-2">{t('cases.urgency')}</h3>
 <div className="space-y-1.5">
 {items.map(it => {
 const active = urgencyFilter === it.key;
 const colorCls = it.color === 'red'
 ? (active ? 'border-red-500/40 bg-red-500/8' : 'border-line hover:border-red-500/30')
 : it.color === 'amber'
 ? (active ? 'border-amber-500/40 bg-amber-500/8' : 'border-line hover:border-amber-500/30')
 : (active ? 'border-line-hover bg-surface-hover' : 'border-line hover:border-line-hover');
 const countCls = it.color === 'red' ? 'text-red-500' : it.color === 'amber' ? 'text-amber-500' : 'text-content-secondary';
 return (
 <button key={it.key} onClick={() => setUrgencyFilter(urgencyFilter === it.key ? null : it.key)}
 className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-colors ${colorCls}`}>
 <span className="text-xs font-medium text-content-secondary">{it.label}</span>
 <span className={`text-sm font-bold ${countCls}`}>{it.count}</span>
 </button>
 );
 })}
 </div>

 {/* Top 3 urgent */}
 {metrics.topUrgent.length > 0 && (
 <div className="mt-3">
 <h4 className="text-[9px] font-bold text-content-tertiary uppercase tracking-widest mb-1.5">{t('cases.mostUrgent')}</h4>
 <div className="space-y-1">
 {metrics.topUrgent.map(tk => (
 <button key={`${tk.source}-${tk.id}`} onClick={() => setPreviewId({ id: tk.id, source: tk.source })}
 className="w-full text-left px-2.5 py-1.5 rounded-lg border border-line hover:border-red-500/30 hover:bg-red-500/5 transition-colors">
 <div className="text-[11px] font-medium text-content truncate">{tk.name}</div>
 <div className="flex items-center gap-1.5 mt-0.5">
 <span className="text-[9px] text-content-tertiary truncate">{tk.company_name || tk.partner_name || ''}</span>
 <span className="text-[9px] px-1 py-0.5 rounded bg-red-500/15 text-red-400 font-bold ml-auto flex-shrink-0">
 {Math.round(tk._overdueHrs)}h
 </span>
 </div>
 </button>
 ))}
 </div>
 </div>
 )}
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// CASE PREVIEW — Shows on single-click in sidebar
// ════════════════════════════════════════════════════════════════
function CasePreview({ previewData, previewLoading, onOpenFull, t }) {
 const { getStageLabel } = useLanguage();
 if (previewLoading) {
 return (
 <div className="flex-1 flex items-center justify-center p-6">
 <div className="animate-spin h-5 w-5 border-2 border-t-transparent rounded-full" style={{ borderColor: MODULE_COLOR, borderTopColor: 'transparent' }} />
 </div>
 );
 }
 if (!previewData) {
 return (
 <div className="flex-1 flex flex-col items-center justify-center p-6 text-content-tertiary opacity-50">
 <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59" />
 </svg>
 <span className="text-xs">{t('cases.clickForPreview')}</span>
 </div>
 );
 }

 const task = previewData.task;
 const messages = previewData.messages || [];
 const lastMsg = messages.find(m => {
 const b = m.body?.trim();
 return b && b !== '<p><br></p>' && b !== '<br>' && b !== '<p></p>';
 });

 return (
 <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
 <div className="p-3 space-y-3">
 {/* Title + Priority */}
 <div>
 <div className="flex items-center gap-1.5 mb-1">
 <Stars value={task.priority} onChange={() => {}} />
 <h4 className="text-sm font-bold text-content line-clamp-2 leading-snug">{task.name}</h4>
 </div>
 </div>

 {/* Company */}
 {(task.company_name || task.partner_name) && (
 <div className="flex items-center gap-1.5 text-xs text-content-secondary">
 <Building className="w-3.5 h-3.5 flex-shrink-0" />
 <Link to={`/kunden/${task.partner_id || ''}`} className="font-medium truncate hover:text-[#338edc] hover:underline transition-colors">{task.company_name || task.partner_name}</Link>
 </div>
 )}

 {/* Stage + SLA */}
 <div className="flex items-center gap-2 flex-wrap">
 <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold text-white" style={{ backgroundColor: MODULE_COLOR }}>
 {getStageLabel(task.stage_name)}
 </span>
 {task.sla_klasse && <SlaKlasseBadge task={task} />}
 {task.sla_overdue && !task.sla_klasse && !task.sla_pausiert && (
 <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500/15 text-red-400">{t('cases.slaOverdue')}</span>
 )}
 </div>

 {/* SLA Timer */}
 {task.sla_stunden && (
 <SlaTimerLine task={task} t={t} />
 )}

 {/* Assigned */}
 {task.assigned_users?.length > 0 && (
 <div className="flex items-center gap-2">
 <div className="relative w-5 h-5 rounded-full bg-gradient-to-br from-primary-500 to-purple-500 flex items-center justify-center overflow-hidden">
 {task.assigned_users[0].avatar_url ? (
 <img src={task.assigned_users[0].avatar_url} alt={task.assigned_users[0].name}
 className="w-full h-full object-cover"
 onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = 'flex'); }} />
 ) : null}
 <span style={{ display: task.assigned_users[0].avatar_url ? 'none' : 'flex' }}
 className="absolute inset-0 items-center justify-center text-[7px] text-white font-bold">
 {initials(task.assigned_users[0].name)}
 </span>
 </div>
 <span className="text-xs text-content-secondary">{task.assigned_users[0].name}</span>
 </div>
 )}

 {/* AI Summary */}
 {task.ai_summary && (
 <div className="px-2.5 py-2 rounded-lg bg-primary-500/8 border border-primary-500/15 text-[11px] text-primary-400">
 <span className="mr-1">🤖</span>
 <span className="font-medium">{task.ai_summary}</span>
 </div>
 )}

 {/* Last message */}
 {lastMsg && (
 <div>
 <h5 className="text-[9px] font-bold text-content-tertiary uppercase tracking-widest mb-1">{t('cases.lastMessage')}</h5>
 <div className="text-[11px] text-content-secondary leading-relaxed line-clamp-3 bg-surface-inset rounded-lg px-2.5 py-2"
 dangerouslySetInnerHTML={{ __html: lastMsg.body }} />
 <div className="text-[9px] text-content-tertiary mt-0.5">{relTime(lastMsg.date, t)}</div>
 </div>
 )}
 </div>

 {/* Open full button */}
 <div className="p-3 mt-auto border-t border-line">
 <button onClick={() => onOpenFull(task.id, task.source)}
 className="w-full py-2 rounded-lg text-xs font-semibold text-white transition-colors hover:brightness-110"
 style={{ backgroundColor: MODULE_COLOR }}>
 {t('cases.openFull')}
 </button>
 </div>
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// RIGHT SIDEBAR — UrgencyPanel + CasePreview
// ════════════════════════════════════════════════════════════════
function RightSidebar({ metrics, urgencyFilter, setUrgencyFilter, previewData, previewLoading, setPreviewId, onOpenFull, t }) {
 return (
 <aside className="hidden xl:flex w-[340px] flex-shrink-0 border-l border-line bg-surface-card flex-col overflow-hidden">
 <UrgencyPanel metrics={metrics} urgencyFilter={urgencyFilter} setUrgencyFilter={setUrgencyFilter} setPreviewId={setPreviewId} t={t} />
 <CasePreview previewData={previewData} previewLoading={previewLoading} onOpenFull={onOpenFull} t={t} />
 </aside>
 );
}

// ════════════════════════════════════════════════════════════════
// MAIN CASES PAGE
// ════════════════════════════════════════════════════════════════

// Backend now guarantees all 5 stages are always returned

export default function Cases() {
 const { user } = useAuth();
 const { t, getStageLabelShort, getStageLabel } = useLanguage();
 const location = useLocation();
 const navigate = useNavigate();
 const params = useParams();
 const [tasks, setTasks] = useState([]);
 const [stages, setStages] = useState([]);
 const [omsUsers, setOmsUsers] = useState([]);
 // #1250 — interne MA für „Zugewiesen"-Filter (derselbe bereinigte Resolver wie #1249 D)
 const [internalUsers, setInternalUsers] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 // #1153 — search initial aus sessionStorage (Filter-Persistenz, siehe weiter unten)
 const [search, setSearch] = useState(() => { try { const v = sessionStorage.getItem('weship_cases_filters'); return (v ? (JSON.parse(v).search || '') : ''); } catch { return ''; } });
 // #1247 — Refresh-Intervall aus User-Preference (localStorage). Settings-Page /cases/einstellungen.
 const [refreshSeconds] = useCasesRefreshInterval();
 const refreshInterval = refreshSeconds * 1000;

 // Detail ID derived from URL: /cases/:id → detailId = id (number or string)
 const detailId = params.id ? (isNaN(Number(params.id)) ? params.id : Number(params.id)) : null;
 // #1499 — source-Hint aus URL (?src=oms), damit das Detail bei ID-Kollision die OMS-Quelle lädt
 const detailSrc = new URLSearchParams(location.search).get('src');
 // #1499 — source mit-tragen: OMS-IDs kollidieren numerisch mit lokalen cases.id,
 // daher beim Öffnen ?src=oms anhängen, damit das Detail die richtige Quelle lädt.
 const openCase = useCallback((id, source) => navigate(`/cases/${id}${source === 'oms' ? '?src=oms' : ''}`), [navigate]);
 const closeDetail = useCallback(() => {
 // #1153 — IMMER zur Liste navigieren; Filter werden aus sessionStorage rehydriert
 // (vorher: navigate(-1) → bei externen Direkt-Links zu /cases/:id ist die History
 //  nicht von /cases/liste, dann landete der Zurück-Click ungewollt auf /home etc.)
 navigate('/cases/liste');
 }, [navigate]);
 const [toast, setToast] = useState(null);
 // #1153 — Filter-State persistieren in sessionStorage, damit „< Cases" zurück zur gefilterten Liste führt
 const __SS_KEY = 'weship_cases_filters';
 const __readSS = () => { try { const v = sessionStorage.getItem(__SS_KEY); return v ? JSON.parse(v) : {}; } catch { return {}; } };
 const __ssInit = (() => __readSS())();
 // #1801 — Deep-Link (AK7): Chips + Filter aus der URL-Query rekonstruieren.
 // URL gewinnt über sessionStorage (geteilter Link stellt exakt die geteilte Sicht her).
 const __urlInit = (() => {
 try {
 const sp = new URLSearchParams(window.location.search);
 const chips = sp.get('cs') ? JSON.parse(sp.get('cs')) : null;
 const f = sp.get('cf') ? JSON.parse(sp.get('cf')) : {};
 return { chips: Array.isArray(chips) ? chips.filter(c => c && c.cat && String(c.val ?? '').length).slice(0, 6) : null, f };
 } catch { return { chips: null, f: {} }; }
 })();
 const [assignFilter, setAssignFilter] = useState(__urlInit.f.assignFilter || __ssInit.assignFilter || 'all'); // 'all' | 'me' | 'unassigned' | 'vertretungen' | 'watchlist'
 const [sourceFilter, setSourceFilter] = useState(__urlInit.f.sourceFilter || __ssInit.sourceFilter || 'all'); // 'all' | 'email' | 'oms'
 const [kundeFilter, setKundeFilter] = useState(__urlInit.f.kundeFilter ?? __ssInit.kundeFilter ?? 'all'); // 'all' | 'none' | kunde_id number
 // #1250 — „Zugewiesen"-Filter: 'all' (kein Filter) | 'none' (Nicht zugewiesen) | users.id (interne MA)
 const [assigneeFilter, setAssigneeFilter] = useState(__urlInit.f.assigneeFilter ?? __ssInit.assigneeFilter ?? 'all');
 const [kundenListMain, setKundenListMain] = useState([]);
 const [summaryQueue, setSummaryQueue] = useState([]); // #1507 — {id, source} der Cases ohne Summary (source-bewusst gegen ID-Kollision)
 const [vertretungen, setVertretungen] = useState([]); // active vertretungen
 const [previewId, setPreviewId] = useState(null);
 const [previewData, setPreviewData] = useState(null);
 const [previewLoading, setPreviewLoading] = useState(false);
 const [urgencyFilter, setUrgencyFilter] = useState(__ssInit.urgencyFilter || null); // 'sla' | 'overdue24h' | 'unassigned' | null
 // #1289 — Status-Filter aus der ListView in die sticky Filterleiste hochgezogen.
 // Steuerstate lebt jetzt im Parent; ListView liest activeStage/lvUrgency/lvOnlyTimer
 // als Props und filtert in visibleTasks unverändert weiter (keine Logik-Änderung).
 const [lvUrgency, setLvUrgency] = useState(null); // 'sla' | 'overdue24h' | null (Warn-Items des Status-Dropdowns)
 const [lvOnlyTimer, setLvOnlyTimer] = useState(false); // #994 „mit Timer"
 const [allTags, setAllTags] = useState([]);
 const [allCategories, setAllCategories] = useState([]);
 const [categoryFilter, setCategoryFilter] = useState(__urlInit.f.categoryFilter || __ssInit.categoryFilter || 'all');
 const [tagFilter, setTagFilter] = useState(Array.isArray(__urlInit.f.tagFilter) ? __urlInit.f.tagFilter : (Array.isArray(__ssInit.tagFilter) ? __ssInit.tagFilter : []));
 // #1505 — pro-User Custom-Filter (kombinierbare Regeln, Sofort-Toggle)
 const [customFilters, setCustomFilters] = useState([]);
 const [cfConfigOpen, setCfConfigOpen] = useState(false);
 const [cfEditing, setCfEditing] = useState(null); // null = neu, sonst zu bearbeitender Filter
 const [watchedIds, setWatchedIds] = useState(new Set());
 // #1359 QW2 — serverseitiger erledigt-Count. Da erledigte Cases per Default NICHT geladen werden,
 // liefert das Backend (closed_count) die korrekte Zahl für den „Erledigt"-Tab-Badge/done-Count,
 // damit dieser nicht auf 0 fällt, wenn die done-Cases gar nicht im geladenen Satz sind.
 const [closedCount, setClosedCount] = useState(0);
 // #994: Map case_id → { mitarbeiter_id, ma_name, start_at, paused_at, timer_state, kategorie_name }
 const [activeTimers, setActiveTimers] = useState({});
 // #1081 Cases-Liste Live-Presence: Map<caseId, uid[]>
 const [listPresence, setListPresence] = useState({});
 // #1059 Punkt 1+4: + Neuer Case Modal
 const [createOpen, setCreateOpen] = useState(false);
 // #1232 — AI-Search Modal
 const [aiSearchOpen, setAiSearchOpen] = useState(false);

 // Collapsed stages (Kanban) + hide done (List) — persisted in localStorage
 const [collapsedStages, setCollapsedStages] = useState(() => {
 try { const v = localStorage.getItem('weship_cases_collapsed'); return v ? new Set(JSON.parse(v)) : null; }
 catch { return null; }
 });
 const [collapsedInited, setCollapsedInited] = useState(false);
 // #1248: "Abgeschlossen" als reguläre Stage-Option im Status-Dropdown (statt Checkbox).
 // activeStage zur Parent-Ebene gehoben, damit Liste, Such-Zähler und Empty-State kohärent sind.
 // Default 'all' = offenes Universum OHNE Abgeschlossen; erst Auswahl 'done' zeigt erledigte Cases.
 const [activeStage, setActiveStage] = useState(() => {
 try {
 if (__urlInit.f.activeStage) return __urlInit.f.activeStage; // #1801 Deep-Link
 const v = localStorage.getItem('weship_cases_active_stage');
 if (!v) return 'all';
 const parsed = JSON.parse(v);
 if (typeof parsed === 'number') return 'all'; // Migrate alte numerische IDs
 return parsed || 'all';
 } catch { return 'all'; }
 });
 useEffect(() => { try { localStorage.setItem('weship_cases_active_stage', JSON.stringify(activeStage)); } catch {} }, [activeStage]);
 // #1359 QW2 — erledigte Cases werden per Default NICHT geladen; nur der „Erledigt"-Tab lädt
 // mit include_closed=1 nach. #1378 — die Suche braucht den Voll-Load nicht mehr: abgeschlossene
 // Treffer kommen serverseitig vom Fulltext-Endpoint (#1376), OMS-Treffer aus dem OMS-Pool.
 // #1365-Semantik (Suche überstimmt ALLE Filter) bleibt — jetzt serverseitig statt per Voll-Load.
 // #1801 — Kategorisierte Suche: `search` ist NUR noch der getippte Text fürs Vorschlags-
 // Dropdown (kein Live-Filtern der Liste mehr — AK1, kein Ergebnis-Dump). Die Liste ändert
 // sich erst, wenn ein Filter-Chip gesetzt wird (Kategorie-Auswahl oder Enter=Standardsuche).
 const [chips, setChips] = useState(() => __urlInit.chips || (Array.isArray(__ssInit.chips) ? __ssInit.chips : []));
 const chipsActive = chips.length > 0;
 const searchActive = chipsActive; // Semantik-Anker für abhängige Stellen (Counter, Empty-State)
 const needClosed = activeStage === 'done';
 // Vorschläge (Server /suggest, Debounce 250ms, min. 2 Zeichen)
 const [suggest, setSuggest] = useState(null);
 const [suggestLoading, setSuggestLoading] = useState(false);
 const [suggestOpen, setSuggestOpen] = useState(false);
 const [sgExpanded, setSgExpanded] = useState(() => new Set()); // aufgeklappte Kategorien (▸)
 const [sgHighlight, setSgHighlight] = useState(0); // Index in der flachen Navigationsliste
 const suggestSeqRef = useRef(0);
 // Chip-Ergebnisse (Server /categorized) + OMS-Pool (Client-Matching pro Chip)
 const [catResults, setCatResults] = useState(null);
 const [catLoading, setCatLoading] = useState(false);
 const [catTruncated, setCatTruncated] = useState(false);
 // Filter-Respekt (2.2): Modus aus system_config (on_click | auto), Ausbruch-Zustand
 const [outsideMode, setOutsideMode] = useState('on_click');
 const [showOutside, setShowOutside] = useState(false);
 const catSeqRef = useRef(0);
 const [omsPool, setOmsPool] = useState(null); // null = Pool nicht geladen → Fallback geladene Tasks
 const [omsLoading, setOmsLoading] = useState(false);
 // #1381 R1 — OMS-Fetch graceful: Timeout/Fehler → klarer Hinweis statt ewigem „OMS wird mitgesucht…"
 const [omsFailed, setOmsFailed] = useState(false);
 const [searchArchive, setSearchArchive] = useState(false);
 const omsPoolReqRef = useRef(false);
 // #1381 R2 — Race-Guard auch für den OMS-Pool: späte Antworten einer überholten Such-Session
 // (Suche geleert + neu getippt, alter Fetch läuft noch) dürfen weder Pool noch Ladezustand überschreiben.
 const omsSeqRef = useRef(0);
 const [hideWaiting, setHideWaiting] = useState(() => {
 try { return localStorage.getItem('weship_cases_hide_waiting') === 'true'; }
 catch { return false; }
 });
 const searchRef = useRef(null);
 const refreshTimerRef = useRef(null);
 const summaryActiveRef = useRef(0); // concurrent summary requests counter

 // #1058: Kanban-View deprecated. Cases ist jetzt immer Listenansicht (kein Flash mehr beim Initial-Load).
 const view = 'list';

 // Redirect /cases & /cases/board → /cases/liste (only when not on /cases/:id)
 useEffect(() => {
 if (location.pathname === '/cases' || location.pathname === '/cases/board') {
 navigate('/cases/liste', { replace: true });
 }
 }, [location.pathname, navigate]);

 // #1153 — Filter-State in sessionStorage spiegeln (damit „< Cases"-Zurück zur gefilterten Liste führt)
 useEffect(() => {
 try {
 sessionStorage.setItem('weship_cases_filters', JSON.stringify({
 search, chips, assignFilter, sourceFilter, kundeFilter, assigneeFilter, categoryFilter, tagFilter, urgencyFilter,
 }));
 } catch { /* sessionStorage voll/disabled → ignorieren */ }
 }, [search, chips, assignFilter, sourceFilter, kundeFilter, assigneeFilter, categoryFilter, tagFilter, urgencyFilter]);

 const loadMeta = useCallback(() => {
 api('/api/cases/tags').then(d => setAllTags(d.tags || [])).catch(() => {});
 api('/api/cases/categories').then(d => setAllCategories(d.categories || [])).catch(() => {});
 }, []);

 // #1505 — eigene Custom-Filter laden (pro User, fail-closed im BE)
 const loadCustomFilters = useCallback(() => {
 api('/api/cases/custom-filters').then(d => setCustomFilters(d.filters || [])).catch(() => {});
 }, []);
 useEffect(() => { loadCustomFilters(); }, [loadCustomFilters]);

 // #1505 — Wert-Optionen je Custom-Filter-Feld (aus den bereits geladenen Dimensionen, F2).
 const cfOptionsFor = useCallback((feld) => {
 switch (feld) {
 case 'tag': return allTags.map(x => ({ value: String(x.id), label: x.name }));
 case 'kunde': return kundenListMain.map(x => ({ value: String(x.id), label: x.name }));
 case 'status': return stages.map(x => ({ value: x.key, label: getStageLabel(x.name) || x.name }));
 case 'kategorie': return allCategories.filter(c => c.is_active).map(x => ({ value: String(x.id), label: x.name }));
 case 'quelle': return [
 { value: 'oms', label: t('cases.sourceOms', 'OMS') },
 { value: 'email', label: t('cases.sourceEmail', 'E-Mail') },
 { value: 'voice_phone', label: t('cases.cf.quelleTelefon', 'Telefon') },
 ];
 case 'zugewiesen': return internalUsers.map(x => ({ value: String(x.id), label: `${x.vorname || ''} ${x.nachname || ''}`.trim() || x.email }));
 default: return [];
 }
 }, [allTags, kundenListMain, stages, allCategories, internalUsers, getStageLabel, t]);

 const toggleWatch = useCallback(async (id) => {
 const isWatched = watchedIds.has(id);
 try {
 await api(`/api/cases/${id}/watch`, { method: isWatched ? 'DELETE' : 'POST' });
 setWatchedIds(prev => { const s = new Set(prev); isWatched ? s.delete(id) : s.add(id); return s; });
 setToast({ message: t(isWatched ? 'cases.watchRemoved' : 'cases.watchAdded'), type: 'success' });
 } catch { /* ignore */ }
 }, [watchedIds, t]);

 useEffect(() => {
 Promise.all([
 api('/api/cases/stages').then(d => setStages(d.stages || [])),
 api('/api/cases/users').then(d => setOmsUsers(d.users || [])),
 api('/api/cases/vertretungen/users').then(d => setInternalUsers(d.users || [])).catch(() => {}),
 api('/api/cases/vertretungen/aktiv').then(d => {
 setVertretungen(d.vertretungen || []);
 }).catch(() => {}),
 api('/api/kunden').then(rows => setKundenListMain(Array.isArray(rows) ? rows : [])).catch(() => {}),
 ]).catch(e => setError(e.message));
 loadMeta();
 api('/api/cases/my-watchlist').then(d => {
 setWatchedIds(new Set((d.cases || []).map(c => c.case_id)));
 }).catch(() => {});
 }, [loadMeta]);

 const loadTasks = useCallback(async () => {
 try {
 const p = new URLSearchParams();
 // #1267 — 'vertretungen' lädt wie 'all'/'watchlist' den vollen Cases-Satz (kein assigned_to-Param);
 // das Narrowing auf die vertretenen Kolleg:innen passiert client-side (Multi-ID-Vorfilter, Dual-Namespace).
 // #1378 — der #1365-Voll-Load bei aktiver Suche ist abgelöst: die Suche läuft jetzt serverseitig
 // (Fulltext-Endpoint + OMS-Pool, eigene Effects unten) → loadTasks bleibt IMMER der gefilterte
 // Default-Load und ist von der Suche entkoppelt (Schicht 0 filtert diesen Satz instant).
 if (assignFilter !== 'all' && assignFilter !== 'watchlist' && assignFilter !== 'vertretungen') p.set('assigned_to', assignFilter);
 if (sourceFilter !== 'all') p.set('source', sourceFilter);
 if (categoryFilter !== 'all') p.set('category', categoryFilter);
 if (tagFilter.length === 1) p.set('tag', tagFilter[0]);
 // #1359 QW2 — erledigte Cases nur nachladen, wenn der Done-Tab gewählt ist (#1378: Suche braucht das nicht mehr).
 if (needClosed) p.set('include_closed', '1');
 const d = await api(`/api/cases?${p}`);
 let taskList = d.tasks || [];
 if (assignFilter === 'watchlist') taskList = taskList.filter(tk => watchedIds.has(tk.id));
 setTasks(taskList);
 // #1359 QW2 — serverseitigen erledigt-Count übernehmen (für korrekten Badge ohne geladene done-Cases).
 setClosedCount(d.closed_count || 0);
 setError(null);

 // Queue tasks without AI summary for lazy generation (max 5 at a time)
 // #1069 H4 — auch lokale Cases (source='email') werden jetzt summarized
 // (POST /:id/summarize branched intern auf local- vs OMS-Pfad).
 // #1507 — Queue trägt {id, source}: lokale cases.id und OMS-project.task-IDs
		// kollidieren numerisch; ohne source würde der Summarize-POST (local-first) den
		// falschen Case treffen und der Merge die Zusammenfassung auf beide Zeilen klatschen.
		const needSummary = taskList.filter(t => !t.ai_summary).map(t => ({ id: t.id, source: t.source }));
 if (needSummary.length) setSummaryQueue(needSummary);
 } catch (e) { setError(e.message); }
 finally { setLoading(false); }
 }, [assignFilter, sourceFilter, categoryFilter, tagFilter, watchedIds, needClosed]);

 // #1801 — Kategorisierte Vorschläge: Debounce 250ms, min. 2 Zeichen, Race-Guard per
 // Sequenznummer (Muster #1378 E6). Nachrichteninhalt wird hier NIE abgefragt (AK6) —
 // die Kategorie erscheint im Dropdown nur als explizit wählbare Option.
 useEffect(() => {
 const q = search.trim();
 if (q.length < 2) {
 suggestSeqRef.current++;
 setSuggest(null); setSuggestLoading(false); setSuggestOpen(false);
 return;
 }
 const seq = ++suggestSeqRef.current;
 setSuggestLoading(true);
 setSuggestOpen(true);
 const timer = setTimeout(async () => {
 try {
 const d = await api(`/api/cases/search/suggest?q=${encodeURIComponent(q)}`);
 if (suggestSeqRef.current !== seq) return;
 setSuggest(d.categories || null);
 setSgHighlight(0);
 } catch {
 if (suggestSeqRef.current === seq) setSuggest(null);
 } finally {
 if (suggestSeqRef.current === seq) setSuggestLoading(false);
 }
 }, 250);
 return () => clearTimeout(timer);
 }, [search]);

 // #1801 — Chip-Ergebnisse: bei jeder Chip-Änderung die lokalen Treffer serverseitig holen
 // (UND-Logik im Endpoint); OMS-Treffer matcht der Client gegen den Pool (siehe Merge unten).
 // Antwort trägt outside_filter_mode mit (kein separater Config-Fetch).
 useEffect(() => {
 if (!chipsActive) {
 catSeqRef.current++;
 setCatResults(null); setCatLoading(false); setCatTruncated(false); setShowOutside(false);
 return;
 }
 const seq = ++catSeqRef.current;
 setCatLoading(true);
 (async () => {
 try {
 const p = new URLSearchParams({ chips: JSON.stringify(chips.map(c => ({ cat: c.cat, val: String(c.val), ...(c.vtype ? { vtype: c.vtype } : {}) }))), limit: '200' });
 if (searchArchive) p.set('include_archive', '1');
 const d = await api(`/api/cases/search/categorized?${p}`);
 if (catSeqRef.current !== seq) return;
 setCatResults(d.tasks || []);
 setCatTruncated(!!d.truncated);
 if (d.outside_filter_mode) setOutsideMode(d.outside_filter_mode);
 } catch {
 if (catSeqRef.current === seq) { setCatResults([]); setCatTruncated(false); }
 } finally {
 if (catSeqRef.current === seq) setCatLoading(false);
 }
 })();
 }, [chips, chipsActive, searchArchive]);

 // #1378 — OMS-Pool: bei Suchstart 1× ALLE OMS-Cases (inkl. done) über den bestehenden
 // ?source-Weg laden (schnell, ~0,6s; OMS-Kommentartiefe bewusst wie heute). Das Matching
 // läuft client-seitig pro Chip gegen den Pool — kein Reload pro Chip (#1112).
 // #1801 — Pool lädt bereits beim Öffnen des Vorschlags-Dropdowns, damit OMS-Zähler
 // in den Kategorien mitgezählt werden können. Suche leeren → kompletter Reset (AK7).
 useEffect(() => {
 const poolNeeded = chipsActive || suggestOpen;
 if (!poolNeeded) {
 omsPoolReqRef.current = false;
 omsSeqRef.current++; // #1381 R2 — laufende OMS-Fetches der alten Session entwerten
 setOmsPool(null); setOmsLoading(false); setOmsFailed(false);
 if (!chipsActive) { setSearchArchive(false); setShowOutside(false); }
 return;
 }
 if (omsPoolReqRef.current) return;
 omsPoolReqRef.current = true;
 const omsSeq = ++omsSeqRef.current;
 setOmsLoading(true);
 setOmsFailed(false);
 // #1381 R1 — Timeout 6s: hängender/lahmer OMS darf den Indikator nicht ewig halten;
 // Abbruch → fail-soft (lokale Treffer bleiben) + klarer Hinweis (omsFailed).
 const omsCtrl = new AbortController();
 const omsTimer = setTimeout(() => omsCtrl.abort(), 6000);
 api('/api/cases?source=oms&include_closed=1', { signal: omsCtrl.signal })
 .then(d => { if (omsSeqRef.current !== omsSeq) return; setOmsPool(d.tasks || []); })
 .catch(() => { // fail-soft: OMS-Treffer dann nur aus geladenem Default-Satz + Hinweis
 if (omsSeqRef.current !== omsSeq) return;
 setOmsPool([]); setOmsFailed(true);
 })
 .finally(() => {
 clearTimeout(omsTimer);
 if (omsSeqRef.current === omsSeq) setOmsLoading(false);
 });
 }, [chipsActive, suggestOpen]);

 // Lazy summary generation: process queue, max 5 concurrent
 useEffect(() => {
 if (summaryQueue.length === 0) return;
 const MAX_CONCURRENT = 5;

 // #1507 — source-bewusst: ?src=oms an den (local-first) Summarize-Endpoint geben,
		// damit bei id-Kollision der RICHTIGE Case summariert wird; Merge auf id UND source.
		async function processOne(item) {
			const { id: taskId, source } = item;
 summaryActiveRef.current++;
 try {
 const res = await api(`/api/cases/${taskId}/summarize${source === 'oms' ? '?src=oms' : ''}`, { method: 'POST' });
 if (res.summary) {
 setTasks(prev => prev.map(t => (t.id === taskId && t.source === source) ? { ...t, ai_summary: res.summary } : t));
 }
 } catch { /* silent – summary is optional */ }
 finally { summaryActiveRef.current--; }
 }

 const toProcess = summaryQueue.slice(0, MAX_CONCURRENT - summaryActiveRef.current);
 if (toProcess.length === 0) return;

 setSummaryQueue(prev => prev.filter(q => !toProcess.some(p => p.id === q.id && p.source === q.source)));
 toProcess.forEach(item => processOne(item));
 }, [summaryQueue]);

 useEffect(() => { loadTasks(); }, [loadTasks]);
 useEffect(() => {
 if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
 if (refreshInterval > 0) refreshTimerRef.current = setInterval(loadTasks, refreshInterval);
 return () => { if (refreshTimerRef.current) clearInterval(refreshTimerRef.current); };
 }, [refreshInterval, loadTasks]);

 // #994: Active-Timer-Map für Cases-List Pulse-Indikator + Filter
 useEffect(() => {
 let cancelled = false;
 const fetchActiveTimers = async () => {
 try {
 const d = await api('/api/zeiterfassung/timer/active-cases');
 if (!cancelled) setActiveTimers(d?.active_cases || {});
 } catch { if (!cancelled) setActiveTimers({}); }
 };
 fetchActiveTimers();
 const iv = setInterval(fetchActiveTimers, 30000);
 return () => { cancelled = true; clearInterval(iv); };
 }, []);

 useEffect(() => {
 const h = (e) => {
 if (e.target.tagName==='INPUT'||e.target.tagName==='TEXTAREA'||e.target.tagName==='SELECT'||e.target.isContentEditable) return;
 if (e.key==='s'||e.key==='S') { e.preventDefault(); searchRef.current?.focus(); }
 if (e.key==='r'||e.key==='R') { e.preventDefault(); loadTasks(); }
 };
 window.addEventListener('keydown', h);
 return () => window.removeEventListener('keydown', h);
 }, [loadTasks]);

 // #1081 Cases-Liste Live-Presence: WS-Subscribe für Live-Avatar-Stack
 useEffect(() => {
 let ws = null;
 let cancelled = false;
 let retryIdx = 0;
 // #1837 — wie bei /ws/cases: nie 0 ms, sonst wird aus jedem Server-Close eine Heissschleife.
 const backoff = [1000, 2000, 5000, 10000, 30000];
 let reconnectTimer = null;
 let heartbeatTimer = null;
 let openedAt = 0;
 const KEIN_NEUAUFBAU = new Set([4001, 4002]);

 const connect = () => {
 if (cancelled) return;
 if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) return;
 try {
 const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
 ws = new WebSocket(`${proto}//${window.location.host}/ws/cases-list`);
 } catch { scheduleReconnect(); return; }

 ws.onopen = () => {
 openedAt = Date.now();
 heartbeatTimer = setInterval(() => {
 if (ws && ws.readyState === WebSocket.OPEN) {
 try { ws.send(JSON.stringify({ type: 'heartbeat' })); } catch { /* ignore */ }
 }
 }, 30000);
 };
 ws.onmessage = (ev) => {
 try {
 const msg = JSON.parse(ev.data);
 if (msg.type === 'list-snapshot') {
 const next = {};
 for (const [cid, uids] of Object.entries(msg.cases || {})) {
 next[cid] = Array.isArray(uids) ? uids.map(Number) : [];
 }
 setListPresence(next);
 } else if (msg.type === 'presence-update') {
 const cid = String(msg.case_id);
 const uids = Array.isArray(msg.active_user_ids) ? msg.active_user_ids.map(Number) : [];
 setListPresence(prev => {
 if (!uids.length) {
 const { [cid]: _, ...rest } = prev;
 return rest;
 }
 return { ...prev, [cid]: uids };
 });
 }
 } catch { /* ignore parse errors */ }
 };
 ws.onclose = (ev) => {
 if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
 if (ev && KEIN_NEUAUFBAU.has(ev.code)) return;
 scheduleReconnect();
 };
 ws.onerror = () => { /* close-Handler greift */ };
 };

 const scheduleReconnect = () => {
 if (cancelled) return;
 // Zähler nur zurücksetzen, wenn die Verbindung wirklich gestanden hat (#1837).
 if (openedAt && Date.now() - openedAt >= 10000) retryIdx = 0;
 openedAt = 0;
 const delay = backoff[Math.min(retryIdx, backoff.length - 1)];
 retryIdx++;
 reconnectTimer = setTimeout(connect, delay + Math.floor(Math.random() * 500));
 };

 connect();
 return () => {
 cancelled = true;
 if (reconnectTimer) clearTimeout(reconnectTimer);
 if (heartbeatTimer) clearInterval(heartbeatTimer);
 if (ws) { try { ws.close(); } catch { /* ignore */ } }
 };
 }, []);

 const rrId = useMemo(() => stages.find(s => s.key === 'response_required')?.key || null, [stages]);
 const doneId = useMemo(() => stages.find(s => s.key === 'done')?.key || null, [stages]);
 const waitingId = useMemo(() => stages.find(s => s.key === 'waiting_for_support')?.key || null, [stages]);
 const inProgressId = useMemo(() => stages.find(s => s.key === 'in_progress')?.key || null, [stages]);

 // Initialize collapsed stages: default = done collapsed, unless user has saved prefs
 useEffect(() => {
 if (collapsedInited || !doneId) return;
 const saved = collapsedStages;
 if (saved === null) {
 setCollapsedStages(new Set([doneId]));
 } else {
 // Migrate: if saved set contains numeric IDs, reset to key-based
 const hasNumeric = [...saved].some(v => typeof v === 'number');
 if (hasNumeric) setCollapsedStages(new Set([doneId]));
 }
 setCollapsedInited(true);
 }, [doneId, collapsedInited, collapsedStages]);

 const toggleCollapse = useCallback((stageId) => {
 setCollapsedStages(prev => {
 const next = new Set(prev || []);
 if (next.has(stageId)) next.delete(stageId);
 else next.add(stageId);
 try { localStorage.setItem('weship_cases_collapsed', JSON.stringify([...next])); } catch {}
 return next;
 });
 }, []);

 const toggleHideWaiting = useCallback(() => {
 setHideWaiting(prev => {
 const next = !prev;
 try { localStorage.setItem('weship_cases_hide_waiting', String(next)); } catch {}
 return next;
 });
 }, []);

 // Preview: fetch case detail when previewId changes
 useEffect(() => {
 if (!previewId) { setPreviewData(null); return; }
 // #1499 — previewId ist jetzt { id, source }; OMS-Preview braucht ?src=oms (ID-Kollision)
 const pvId = typeof previewId === 'object' ? previewId.id : previewId;
 const pvSrc = typeof previewId === 'object' ? previewId.source : undefined;
 let cancelled = false;
 setPreviewLoading(true);
 api(`/api/cases/${pvId}${pvSrc === 'oms' ? '?src=oms' : ''}`).then(d => {
 if (!cancelled) setPreviewData(d);
 }).catch(() => {
 if (!cancelled) setPreviewData(null);
 }).finally(() => {
 if (!cancelled) setPreviewLoading(false);
 });
 return () => { cancelled = true; };
 }, [previewId]);

 // Urgency metrics computed from tasks — exclude done + paused cases from SLA breach
 // #1062: SSoT via getEffectiveSla — First-Response- ODER Resolution-Breach
 const urgencyMetrics = useMemo(() => {
 const slaBreached = tasks.filter(tk => {
 if (tk.stage_key === 'done' || tk.sla_pausiert) return false;
 const eff = getEffectiveSla(tk, t);
 return eff && eff.isOverdue && !eff.isPaused;
 });
 const overdue24h = tasks.filter(tk => tk.stage_key === 'waiting_for_support' && hrsSince(tk.date_last_stage_update) > 24);
 const unassigned = tasks.filter(tk => (!tk.assigned_users || tk.assigned_users.length === 0) && tk.stage_key !== 'done');
 const topUrgent = slaBreached
 .map(tk => {
 // #1062: Use first_response_deadline if FR breached, otherwise resolution deadline
 const eff = getEffectiveSla(tk, t);
 const deadline = eff?.kind === 'first_response_overdue' ? tk.sla_first_response_deadline
 : (tk.sla_next_response_deadline || tk.sla_deadline);
 const deadlineMs = deadline ? new Date(deadline).getTime() : 0;
 return { ...tk, _overdueHrs: deadlineMs ? (Date.now() - deadlineMs) / 3600000 : 0 };
 })
 .sort((a, b) => b._overdueHrs - a._overdueHrs)
 .slice(0, 3);
 return { slaBreached, overdue24h, unassigned, topUrgent };
 }, [tasks, t]);

 // #1058: Kanban deprecated (view='list'); displayStages bleibt nur für tote Kanban-Pfade.
 const displayStages = stages;

 // #1267 — Vertretungs-Vorfilter (Dual-Namespace #1259). Eine vertretene Person wird
 // konsistent über BEIDE Identitäten aufgelöst: users.id (lokale E-Mail-Cases) UND
 // oms_user_id (aus OMS gespiegelte Cases). Das Multi-ID-Set speist den Standard-Listen-
 // Vorfilter; die Map id→Name speist die optionale Spalte „Vertretung für".
 const vertretungMatchIds = useMemo(() => {
 const s = new Set();
 for (const v of vertretungen) {
 if (v.user?.id != null) s.add(Number(v.user.id));
 if (v.user?.oms_user_id != null) s.add(Number(v.user.oms_user_id));
 }
 return s;
 }, [vertretungen]);
 const vertretungFuerMap = useMemo(() => {
 const m = new Map();
 for (const v of vertretungen) {
 if (v.user?.id != null) m.set(Number(v.user.id), v.user.name);
 if (v.user?.oms_user_id != null) m.set(Number(v.user.oms_user_id), v.user.name);
 }
 return m;
 }, [vertretungen]);
 const inVertretungView = assignFilter === 'vertretungen';
 // Auf die vertretenen Personen eingegrenztes Task-Universum (inkl. done) — speist die
 // Stage-Badges/Zähler der Standard-Liste im Vertretungs-View, damit die Counts stimmen.
 const vertretungTasks = useMemo(() => (
 inVertretungView
 ? tasks.filter(t => (t.assigned_users || []).some(au => vertretungMatchIds.has(Number(au.id))))
 : tasks
 ), [inVertretungView, tasks, vertretungMatchIds]);

 // #1248: Voller Zähler aller abgeschlossenen Cases (~868) für das Status-Dropdown.
 // #1267: Im Vertretungs-View auf die vertretenen Personen eingegrenzt.
 const loadedDoneCount = useMemo(() => vertretungTasks.filter(t => t.stage_key === 'done').length, [vertretungTasks]);
 // #1359 QW2 — erledigte Cases sind per Default NICHT geladen → der geladene done-Count wäre 0.
 // Dann den serverseitigen closed_count nutzen (Badge bleibt korrekt). Bei aktiver Suche/Done-Tab
 // (needClosed) sind die done-Cases im Satz → geladener Count ist maßgeblich (Vertretungs-View
 // bleibt korrekt eingegrenzt, da dann clientseitig gefiltert wird).
 const doneCount = needClosed ? loadedDoneCount : closedCount;

 // #1801 — Chip-Matcher pro Chip (UND-Logik): lokale Treffer liefert der Server (/categorized),
 // OMS-Treffer matcht der Client gegen den Pool. bearbeiter-Chips lösen den Dual-Namespace
 // users.id/oms_user_id auf (#1250/#1780) — im OMS-Pool tragen assigned_users OMS-IDs.
 const chipMatchers = useMemo(() => {
 if (!chipsActive) return null;
 return chips.map(c => {
 let sets = {};
 if (c.cat === 'bearbeiter') {
 const sel = internalUsers.find(u => String(u.id) === String(c.val));
 const ids = new Set([Number(c.val)]);
 if (sel) { ids.add(Number(sel.id)); if (sel.oms_user_id != null) ids.add(Number(sel.oms_user_id)); }
 sets = { bearbeiter: ids };
 }
 return buildChipMatcher(c, sets);
 });
 }, [chips, chipsActive, internalUsers]);

 // #1801 — Merge der zwei Suchquellen (Dedupe über source:id — OMS- und lokale IDs sind
 // getrennte Namensräume, #1499/#1507):
 // 1. Server /categorized = lokale Treffer inkl. Body/Kommentare/Abgeschlossene (Hot-Fenster bzw. Archiv).
 // 2. OMS = Client-Chip-Matcher über den OMS-Pool (Fallback: geladene OMS-Tasks, solange Pool lädt).
 const mergedSearchResults = useMemo(() => {
 if (!chipsActive) return null;
 const localPart = catResults || [];
 const omsSource = omsPool !== null ? omsPool : tasks.filter(tk => tk.source === 'oms');
 const omsPart = chipMatchers ? omsSource.filter(tk => chipMatchers.every(m => m(tk))) : [];
 const seen = new Set();
 const out = [];
 for (const tk of [...localPart, ...omsPart]) {
 const key = `${tk.source === 'oms' ? 'oms' : 'local'}:${tk.id}`;
 if (!seen.has(key)) { seen.add(key); out.push(tk); }
 }
 return out;
 }, [chipsActive, catResults, omsPool, tasks, chipMatchers]);

 // #1378 AK3 — solange lokal (/categorized) ODER OMS noch lädt, gilt die Suche als „läuft":
 // Indikator sichtbar, NIE verfrühtes „0 Treffer".
 const searchLoading = chipsActive && (catLoading || omsLoading);

 // #1801 — Kategorie-Labels fürs Dropdown + Chips (i18n)
 const csearchCatLabel = useCallback((cat) => ({
 case_nr: t('cases.csearch.cat.case_nr', 'Case-Nr'),
 betreff: t('cases.csearch.cat.betreff', 'Betreff'),
 kunde: t('cases.csearch.cat.kunde', 'Kunde'),
 email: t('cases.csearch.cat.email', 'E-Mail-Adresse'),
 tracking: t('cases.csearch.cat.tracking', 'Sendungsnummer'),
 bearbeiter: t('cases.csearch.cat.bearbeiter', 'Bearbeiter'),
 tag: t('cases.csearch.cat.tag', 'Tag'),
 inhalt: t('cases.csearch.cat.inhalt', 'Nachrichteninhalt'),
 default: t('cases.csearch.cat.default', 'Suche'),
 }[cat] || cat), [t]);

 const resetSearchBox = useCallback(() => {
 setSearch('');
 setSuggestOpen(false); setSuggest(null); setSgExpanded(new Set()); setSgHighlight(0);
 }, []);

 const addChip = useCallback((chip) => {
 setChips(prev => {
 if (prev.length >= 6) return prev; // BE-Cap (max 6) spiegeln
 if (prev.some(c => c.cat === chip.cat && String(c.val) === String(chip.val))) return prev;
 return [...prev, chip];
 });
 resetSearchBox();
 }, [resetSearchBox]);

 const removeChip = useCallback((idx) => {
 setChips(prev => prev.filter((_, i) => i !== idx));
 }, []);

 // #1801 — OMS-Zähler fürs Dropdown: der Pool lädt beim Öffnen mit; Kategorien, die im
 // OMS-Listen-Shape client-seitig matchbar sind, zählen OMS-Treffer dazu (Rest local-only).
 const omsCounts = useMemo(() => {
 const q = search.trim();
 if (!omsPool || !suggestOpen || q.length < 2) return {};
 const out = {};
 for (const cat of ['case_nr', 'betreff', 'kunde', 'tracking', 'tag']) {
 try { out[cat] = omsPool.filter(buildChipMatcher({ cat, val: q })).length; } catch { out[cat] = 0; }
 }
 return out;
 }, [omsPool, suggestOpen, search]);

 // #1801 — flache Zeilenliste fürs Vorschlags-Dropdown (Basis der Tastatur-Navigation):
 // Zeile 0 = Standard-Suche (Enter ohne Auswahl → Case-Nr + Betreff + Kunde, AK6),
 // dann je Kategorie mit Treffern eine Zeile (▸ aufklappbar: bis 5 Direkttreffer + „mehr…"),
 // letzte Zeile = Nachrichteninhalt (IMMER nur explizit — die alte Noise-Quelle).
 const sgRows = useMemo(() => {
 const q = search.trim();
 if (!suggestOpen || q.length < 2) return [];
 const rows = [{ type: 'default', q }];
 const order = ['case_nr', 'betreff', 'kunde', 'email', 'tracking', 'bearbeiter', 'tag'];
 for (const cat of order) {
 const data = suggest?.[cat];
 const localCount = data?.count || 0;
 const omsCount = omsCounts[cat] || 0;
 if (localCount + omsCount === 0) continue;
 rows.push({ type: 'cat', cat, q, count: localCount + omsCount });
 if (sgExpanded.has(cat)) {
 for (const item of (data?.items || [])) rows.push({ type: 'item', cat, item });
 if (localCount > (data?.items?.length || 0) || omsCount > 0) rows.push({ type: 'more', cat, q });
 }
 }
 rows.push({ type: 'inhalt', q });
 return rows;
 }, [suggestOpen, search, suggest, omsCounts, sgExpanded]);

 // #1801 — Auswahl einer Dropdown-Zeile: Kategorie-Zeile/„mehr…" → Chip mit Suchtext,
 // Direkttreffer → Case öffnen (case_nr/betreff/tracking) bzw. ID-Chip (kunde/bearbeiter/tag/email).
 const selectSgRow = useCallback((row) => {
 if (!row) return;
 const q = String(row.q || search).trim();
 switch (row.type) {
 case 'default': addChip({ cat: 'default', val: q }); break;
 case 'inhalt': addChip({ cat: 'inhalt', val: q }); break;
 case 'cat':
 case 'more': {
 // #1801-Fix: Case-Nr-Chip behält den Rohtext (z. B. "Mail-#4") — Server & Matcher parsen
 // Präfix+Nummer selbst; nur Tracking wird auf Ziffern normalisiert.
 const digits = q.replace(/^#/, '').replace(/[ .-]/g, '');
 addChip({ cat: row.cat, val: row.cat === 'tracking' ? digits : q });
 break;
 }
 case 'item': {
 const it = row.item;
 if (row.cat === 'case_nr' || row.cat === 'betreff' || row.cat === 'tracking') {
 resetSearchBox(); openCase(it.id, 'email');
 } else if (row.cat === 'kunde') {
 // #1801-Fix: Anrufer-/Absender-Namen ohne kunden-Datensatz (id NULL) → Text-Chip
 if (it.id != null) addChip({ cat: 'kunde', val: it.id, vtype: 'id', label: it.name });
 else addChip({ cat: 'kunde', val: it.name });
 } else if (row.cat === 'bearbeiter') {
 addChip({ cat: 'bearbeiter', val: it.id, vtype: 'id', label: it.name });
 } else if (row.cat === 'tag') {
 addChip({ cat: 'tag', val: it.id, vtype: 'id', label: it.name });
 } else if (row.cat === 'email') {
 addChip({ cat: 'email', val: it.email });
 }
 break;
 }
 default: break;
 }
 }, [search, addChip, resetSearchBox, openCase]);

 // #1801 — Tastatur: ↑↓ navigieren, → Kategorie aufklappen, Enter auswählen, Esc schließen;
 // Backspace im leeren Feld entfernt den letzten Chip. AK3: numerische Query mit exaktem
 // Case-Nr-Treffer + Enter → direkt in den Case springen.
 const onSearchKeyDown = useCallback((e) => {
 if (e.key === 'Backspace' && search === '' && chips.length > 0) { removeChip(chips.length - 1); return; }
 if (e.key === 'Escape') { setSuggestOpen(false); return; }
 if (!suggestOpen || sgRows.length === 0) {
 if (e.key === 'Enter' && search.trim().length >= 2) { e.preventDefault(); addChip({ cat: 'default', val: search.trim() }); }
 return;
 }
 if (e.key === 'ArrowDown') { e.preventDefault(); setSgHighlight(h => Math.min(h + 1, sgRows.length - 1)); }
 else if (e.key === 'ArrowUp') { e.preventDefault(); setSgHighlight(h => Math.max(h - 1, 0)); }
 else if (e.key === 'ArrowRight') {
 const row = sgRows[sgHighlight];
 if (row?.type === 'cat') { e.preventDefault(); setSgExpanded(prev => { const s = new Set(prev); s.add(row.cat); return s; }); }
 } else if (e.key === 'Enter') {
 e.preventDefault();
 // #1801-Fix: auch Anzeigeformate wie "Mail-#4" liefern die Nummer für den Direktsprung
 const tkq = parseTicketNumberQuery(search.trim());
 const digits = tkq ? String(tkq.number) : search.trim().replace(/^#/, '').replace(/[ .-]/g, '');
 if (/^\d{1,7}$/.test(digits) && (suggest?.case_nr?.items || []).some(it => Number(it.id) === Number(digits))) {
 resetSearchBox(); openCase(Number(digits), 'email'); return; // AK3 — Direktsprung
 }
 selectSgRow(sgRows[sgHighlight] || sgRows[0]);
 }
 }, [search, chips.length, suggestOpen, sgRows, sgHighlight, suggest, removeChip, addChip, selectSgRow, resetSearchBox, openCase]);

 // #1801 AK7 — Deep-Link: Chips + gesetzte Filter in die URL spiegeln (nur auf der Liste,
 // replace-Navigation → keine History-Flut). Leerer Zustand räumt die Query-Params weg.
 useEffect(() => {
 if (location.pathname !== '/cases/liste') return;
 const p = new URLSearchParams();
 if (chips.length) p.set('cs', JSON.stringify(chips));
 const f = {};
 if (assignFilter !== 'all') f.assignFilter = assignFilter;
 if (sourceFilter !== 'all') f.sourceFilter = sourceFilter;
 if (kundeFilter !== 'all') f.kundeFilter = kundeFilter;
 if (assigneeFilter !== 'all') f.assigneeFilter = assigneeFilter;
 if (categoryFilter !== 'all') f.categoryFilter = categoryFilter;
 if (tagFilter.length) f.tagFilter = tagFilter;
 if (activeStage !== 'all') f.activeStage = activeStage;
 if (Object.keys(f).length) p.set('cf', JSON.stringify(f));
 const qs = p.toString();
 const target = `${location.pathname}${qs ? `?${qs}` : ''}`;
 if (target !== `${location.pathname}${location.search}`) navigate(target, { replace: true });
 }, [chips, assignFilter, sourceFilter, kundeFilter, assigneeFilter, categoryFilter, tagFilter, activeStage, location.pathname, location.search, navigate]);

 // #1362 QW4 — Suche + Client-Filter OHNE Done-Exklusion als gemeinsame Basis für Liste UND
 // Treffer-Counter, damit der Counter mit der tatsächlich angezeigten Trefferzahl übereinstimmt
 // (vorher zählte searchStats VOR den Client-Filtern).
 // #1505 — Prädikat aus den AKTIVEN Custom-Filtern. Logik:
 //   (Filter A UND Filter B …); je Filter: alle Bedingungen UND; je Bedingung: Werte ODER.
 //   Operator ist_nicht = KEIN Wert trifft zu (Exclude). null = kein aktiver Custom-Filter.
 const customFilterPredicate = useMemo(() => {
 const active = (customFilters || []).filter(f => f.aktiv && Array.isArray(f.bedingungen) && f.bedingungen.length);
 if (active.length === 0) return null;
 const valueMatches = (tk, feld, wert) => {
 switch (feld) {
 case 'tag': {
 const ids = [...(tk.local_tags || []), ...(tk.tags || [])].map(tg => String(tg.id));
 return ids.includes(String(wert));
 }
 case 'kunde': return String(tk.partner_id) === String(wert);
 case 'status': return String(tk.stage_key) === String(wert);
 case 'kategorie': return String(tk.category_id) === String(wert);
 case 'quelle': return String(tk.source) === String(wert);
 case 'zugewiesen': {
 // Dual-Namespace wie #1250: interner MA matched auf users.id ODER oms_user_id.
 const sel = internalUsers.find(u => String(u.id) === String(wert));
 const matchIds = new Set();
 if (sel) { matchIds.add(Number(sel.id)); if (sel.oms_user_id != null) matchIds.add(Number(sel.oms_user_id)); }
 else matchIds.add(Number(wert));
 return (tk.assigned_users || []).some(au => matchIds.has(Number(au.id)));
 }
 default: return false;
 }
 };
 const condPasses = (tk, cond) => {
 const werte = Array.isArray(cond.werte) ? cond.werte : [];
 if (werte.length === 0) return true;
 const anyMatch = werte.some(w => valueMatches(tk, cond.feld, w));
 return cond.operator === 'ist_nicht' ? !anyMatch : anyMatch;
 };
 return (tk) => active.every(f => f.bedingungen.every(c => condPasses(tk, c)));
 }, [customFilters, internalUsers]);

 // #1801-Fix (Regel 2.1) — Case-Nr-Chips (bzw. default-Chips mit expliziter Ticketnummer)
 // sind FILTER-IMMUN: die AND-Treffermenge ist ⊆ genau dieser Nummer → ALLE Treffer
 // umgehen Listen-Filter + Done-Exklusion; nicht-filterkonforme Zeilen bekommen in der
 // ListView ein „außerhalb Filter"-Badge (strictKeySet liefert die Vergleichsbasis).
 const searchImmune = useMemo(() => chipsActive && chips.some(isFilterImmuneChip), [chips, chipsActive]);

 const strictFiltered = useMemo(() => {
 let result = tasks;
 // #1801 — Filter-Respekt (kehrt die #1365-Semantik um): Chip-Treffer sind die BASIS und
 // laufen durch DIESELBE Filterkette wie die normale Liste (kein Early-Return/Override mehr).
 // Die serverseitigen loadTasks-Filter (Quelle/Kategorie/Zuweisung) werden hier client-seitig
 // gespiegelt, weil der Chip-Satz aus eigenen Endpoints kommt (ungefiltert). Treffer außerhalb
 // der Filter bleiben über mergedSearchResults erreichbar (Outside-Sektion in der ListView).
 if (chipsActive) {
 result = mergedSearchResults;
 if (sourceFilter !== 'all') result = result.filter(t => t.source === sourceFilter);
 if (categoryFilter !== 'all') result = result.filter(t => String(t.category_id) === String(categoryFilter));
 if (assignFilter === 'me') {
 const me = internalUsers.find(u => Number(u.id) === Number(user?.id));
 const meIds = new Set([Number(user?.id)]);
 if (me?.oms_user_id != null) meIds.add(Number(me.oms_user_id));
 result = result.filter(t => (t.assigned_users || []).some(au => meIds.has(Number(au.id))));
 } else if (assignFilter === 'unassigned') {
 result = result.filter(t => !(t.assigned_users && t.assigned_users.length));
 } else if (assignFilter === 'watchlist') {
 result = result.filter(t => watchedIds.has(t.id));
 }
 }
 if (kundeFilter !== 'all') {
 if (kundeFilter === 'none') {
 result = result.filter(t => !t.partner_id && !t.company_name);
 } else {
 const kid = Number(kundeFilter);
 result = result.filter(t => t.partner_id === kid);
 }
 }
 if (tagFilter.length > 0) {
 const tagIdSet = new Set(tagFilter.map(Number));
 result = result.filter(t => {
 const taskTagIds = [...(t.local_tags || []), ...(t.tags || [])].map(tg => tg.id);
 return taskTagIds.some(id => tagIdSet.has(id));
 });
 }
 // #1250 — „Zugewiesen"-Filter. assigned_users[].id ist dual-namespace:
 // OMS-Cases → Odoo res.users id (= users.oms_user_id), lokale Cases → users.id.
 // Der gewählte interne MA matched daher auf user.id ODER user.oms_user_id.
 if (assigneeFilter !== 'all') {
 if (assigneeFilter === 'none') {
 result = result.filter(t => !(t.assigned_users && t.assigned_users.length));
 } else {
 const sel = internalUsers.find(u => String(u.id) === String(assigneeFilter));
 const matchIds = new Set();
 if (sel) {
 matchIds.add(Number(sel.id));
 if (sel.oms_user_id != null) matchIds.add(Number(sel.oms_user_id));
 } else {
 matchIds.add(Number(assigneeFilter));
 }
 result = result.filter(t => (t.assigned_users || []).some(au => matchIds.has(Number(au.id))));
 }
 }
 // #1267 — Vertretungs-View: auf Cases der vertretenen Kolleg:innen vorfiltern (Multi-ID, Dual-Namespace).
 // Verallgemeinert den #1250-Einzelfilter auf ein Set; Standard-Liste rendert dieselben Cases.
 if (inVertretungView) {
 result = result.filter(t => (t.assigned_users || []).some(au => vertretungMatchIds.has(Number(au.id))));
 }
 if (urgencyFilter === 'sla') {
 const ids = new Set(urgencyMetrics.slaBreached.map(t => t.id));
 result = result.filter(t => ids.has(t.id));
 } else if (urgencyFilter === 'overdue24h') {
 const ids = new Set(urgencyMetrics.overdue24h.map(t => t.id));
 result = result.filter(t => ids.has(t.id));
 } else if (urgencyFilter === 'unassigned') {
 const ids = new Set(urgencyMetrics.unassigned.map(t => t.id));
 result = result.filter(t => ids.has(t.id));
 }
 // #1505 — aktive Custom-Filter UND-verknüpft als letzter Schritt anwenden.
 if (customFilterPredicate) result = result.filter(customFilterPredicate);
 return result;
 }, [tasks, chipsActive, mergedSearchResults, sourceFilter, categoryFilter, assignFilter, user, watchedIds, kundeFilter, assigneeFilter, internalUsers, tagFilter, urgencyFilter, urgencyMetrics, inVertretungView, vertretungMatchIds, customFilterPredicate]);

 // #1801-Fix — Immun-Modus: kompletter Chip-Satz statt strikter Kette; strictKeySet
 // markiert, welche Treffer die normale Filterkette bestanden hätten (Badge-Basis).
 const searchFiltered = useMemo(
 () => (searchImmune ? (mergedSearchResults || []) : strictFiltered),
 [searchImmune, mergedSearchResults, strictFiltered]
 );
 const strictKeySet = useMemo(() => {
 if (!searchImmune) return null;
 return new Set(strictFiltered.map(tk => `${tk.source === 'oms' ? 'oms' : 'local'}:${tk.id}`));
 }, [searchImmune, strictFiltered]);

 // #1248: Abgeschlossene Cases nur einblenden, wenn die Stage "done" aktiv gewählt ist.
 // #1801 — Filter-Respekt: die Done-Exklusion gilt AUCH bei aktiver Chip-Suche (kehrt #1335
 // Fix A um) — abgeschlossene Chip-Treffer bleiben über die Outside-Sektion erreichbar.
 const filtered = useMemo(() => {
 // #1801-Fix — filter-immune Ticketnummern-Treffer überleben auch die Done-Exklusion.
 if (activeStage !== 'done' && !searchImmune) return searchFiltered.filter(t => t.stage_key !== 'done');
 return searchFiltered;
 }, [searchFiltered, activeStage, searchImmune]);

 // #1289 — Daten für das in die Filterleiste hochgezogene Status-Dropdown.
 // Identische Eingaben wie zuvor in der ListView (tasks={filtered}, allTasks={vertretungTasks})
 // → Badge-Counts bleiben unverändert. Das eigentliche Zeilen-Filtern bleibt in ListView.visibleTasks.
 const lvActiveTimerCount = useMemo(() => filtered.filter(tk => activeTimers[tk.id]).length, [filtered, activeTimers]);
 const lvUrgencyMetrics = useMemo(() => {
 const slaBreached = filtered.filter(tk => {
 if (tk.stage_key === 'done' || tk.sla_pausiert) return false;
 const eff = getEffectiveSla(tk, t);
 return eff && eff.isOverdue && !eff.isPaused;
 });
 const overdue24h = filtered.filter(tk => tk.stage_key === 'waiting_for_support' && hrsSince(tk.date_last_stage_update) > 24);
 return { slaBreached, overdue24h };
 }, [filtered, t]);
 const lvStageCounts = useMemo(() => {
 const counts = {};
 let openTotal = 0;
 for (const tk of (vertretungTasks || filtered)) {
 const sk = tk.stage_key;
 counts[sk] = (counts[sk] || 0) + 1;
 if (sk !== 'done') openTotal++;
 }
 return { counts, openTotal };
 }, [vertretungTasks, filtered]);

 // #1112 — Treffer-Counter + Empty-State-Banner: matched_open / matched_resolved separat zählen.
 // #1362 QW4/QW5 — zählt jetzt auf searchFiltered (Suche + Client-Filter, ohne Done-Exklusion):
 // Counter ≡ tatsächlich angezeigte Treffer; Chip-Logik: Server /categorized + buildChipMatcher (OMS).
 const searchStats = useMemo(() => {
 if (!chipsActive) return { matchedOpen: 0, matchedResolved: 0, active: false };
 let matchedOpen = 0, matchedResolved = 0;
 for (const tk of searchFiltered) {
 if (tk.stage_key === 'done') matchedResolved++;
 else matchedOpen++;
 }
 return { matchedOpen, matchedResolved, active: true };
 }, [searchFiltered, chipsActive]);

 // #1801 — Filter-Respekt 2.2: die Outside-Menge (Treffer außerhalb der aktiven Filter)
 // berechnet die ListView als Differenz vollständiger Chip-Satz (mergedSearchResults) minus
 // sichtbare Zeilen (nach Stage-Filter) — KEINE zweite Vollsuche (AK-Vorgabe).

 const byStage = useMemo(() => {
 const m = {};
 for (const s of displayStages) m[s.key] = [];
 for (const t of filtered) {
 const sk = t.stage_key;
 if (m[sk]) m[sk].push(t);
 }
 return m;
 }, [filtered, displayStages]);

 const openCnt = useMemo(() => {
 const activeStages = new Set([waitingId, inProgressId].filter(Boolean));
 return tasks.filter(t => activeStages.has(t.stage_key)).length;
 }, [tasks, waitingId, inProgressId]);
 const rrCnt = useMemo(() => tasks.filter(t => t.stage_key === waitingId).length, [tasks, waitingId]);

 // ── Full-page detail view (no list/header behind it) ──
 if (detailId) {
 // #1750 — key erzwingt Remount pro Case (inkl. Quelle, #1499-ID-Kollision):
 // alle Closures/States (Upload, Compose, Drag&Drop) binden frisch an den
 // aktuellen Case — Stale-taskId-Uploads strukturell unmöglich.
 return (
 <DetailModal key={`${detailSrc || 'local'}-${detailId}`} open fullPage onClose={closeDetail} taskId={detailId} srcHint={detailSrc} stages={stages} omsUsers={omsUsers} onRefresh={loadTasks} onDraftChange={(id, val) => { const dSrc = detailSrc === 'oms' ? 'oms' : 'email'; setTasks(prev => prev.map(t => (t.id === id && t.source === dSrc) ? { ...t, has_own_draft: !!val } : t)); }} allTags={allTags} allCategories={allCategories} onTagsChange={loadMeta} watchedIds={watchedIds} toggleWatch={toggleWatch} t={t} />
 );
 }

 return (
 <div className="flex flex-col h-full">
 {/* ── Header ── */}
 <div className="px-6 lg:px-8 pt-6 pb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
 <div className="flex items-center gap-3">
 <h1 className="text-2xl font-extrabold text-content">{t('cases.title')}</h1>
 <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold text-white" style={{ backgroundColor: MODULE_COLOR }}>{t('cases.open', { n: openCnt })}</span>
 {rrCnt > 0 && <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-red-500/15 text-red-400">{t('cases.waitingForResponse', { n: rrCnt })}</span>}
 </div>

 <div className="flex items-center gap-2 flex-wrap">
 {/* #1058: Kanban-View entfernt, kein View-Toggle mehr */}

 {/* Search — #1801 kategorisierte Suche: Tippen zeigt NUR das Vorschlags-Dropdown (AK1);
 die Liste ändert sich erst über Filter-Chips (Kategorie-Auswahl bzw. Enter=Standardsuche). */}
 <div className="relative flex flex-col">
 <div className="flex items-center flex-wrap gap-1 bg-surface-inset border border-line rounded-card pl-8 pr-8 py-1 min-h-[38px] w-72 sm:w-96 focus-within:border-primary-500/50">
 {chips.map((c, i) => (
 <span key={`${c.cat}:${c.val}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-primary-500/10 text-primary-600 text-xs font-semibold max-w-[11rem]">
 <span className="truncate">{csearchCatLabel(c.cat)}: {c.label || String(c.val)}</span>
 <button onClick={() => removeChip(i)} className="p-0.5 rounded hover:bg-primary-500/15"
 title={t('cases.csearch.chipRemove', 'Filter entfernen')} aria-label={t('cases.csearch.chipRemove', 'Filter entfernen')}>
 <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
 </button>
 </span>
 ))}
 <input ref={searchRef} type="text" value={search} onChange={e => setSearch(e.target.value)}
 onKeyDown={onSearchKeyDown}
 onFocus={() => { if (search.trim().length >= 2) setSuggestOpen(true); }}
 onBlur={() => setSuggestOpen(false)}
 placeholder={chips.length ? '' : t('cases.searchCases')}
 className="flex-1 min-w-[6rem] bg-transparent py-1 text-sm text-content focus:outline-none placeholder:text-content-tertiary" />
 </div>
 <svg className="absolute left-2.5 top-3 w-3.5 h-3.5 text-content-tertiary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
 <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
 </svg>
 {/* Clear-× — leert Text UND Chips (kompletter Reset, AK7). #1802: bei genau EINEM
 Chip ohne Freitext ausgeblendet — dort wäre es redundant zum Chip-×. */}
 {(search !== '' || chips.length >= 2) && (
 <button onClick={() => { setChips([]); resetSearchBox(); searchRef.current?.focus(); }}
 className="absolute right-2 top-2.5 p-0.5 rounded text-content-tertiary hover:text-content transition-colors"
 title={t('cases.searchClear')} aria-label={t('cases.searchClear')}>
 <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
 </button>
 )}
 {/* #1801 — Vorschlags-Dropdown (Odoo-Stil): eine Zeile je Kategorie, ▸ klappt bis 5
 Direkttreffer + „mehr…" auf; Nachrichteninhalt IMMER nur als explizite letzte Zeile (AK6). */}
 {suggestOpen && sgRows.length > 0 && (
 <div className="absolute top-full left-0 mt-1 w-full sm:w-[26rem] max-h-[70vh] overflow-auto rounded-card border border-line bg-surface-card z-50 py-1"
 onMouseDown={e => e.preventDefault() /* Fokus im Input halten */}>
 {suggestLoading && (
 <div className="px-3 py-1.5 text-xs text-content-tertiary">{t('cases.csearch.loading', 'Suche Vorschläge…')}</div>
 )}
 {sgRows.map((row, idx) => {
 const hl = idx === sgHighlight;
 const base = `w-full text-left px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${hl ? 'bg-primary-500/10 text-primary-600' : 'text-content hover:bg-surface-inset'}`;
 if (row.type === 'default') {
 return (
 <div key="default" className={base} onClick={() => selectSgRow(row)} onMouseEnter={() => setSgHighlight(idx)}>
 <svg className="w-3.5 h-3.5 flex-shrink-0 text-content-tertiary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
 <span className="truncate">{t('cases.csearch.defaultRow', 'Suchen nach')} <strong>„{row.q}"</strong> <span className="text-content-tertiary text-xs">({t('cases.csearch.defaultScope', 'Case-Nr + Betreff + Kunde')})</span></span>
 </div>
 );
 }
 if (row.type === 'cat') {
 const expanded = sgExpanded.has(row.cat);
 return (
 <div key={`cat-${row.cat}`} className={base} onClick={() => selectSgRow(row)} onMouseEnter={() => setSgHighlight(idx)}>
 <button className="p-0.5 -ml-1 rounded text-content-tertiary hover:text-content flex-shrink-0"
 onClick={e => { e.stopPropagation(); setSgExpanded(prev => { const s = new Set(prev); expanded ? s.delete(row.cat) : s.add(row.cat); return s; }); }}
 aria-label={expanded ? t('cases.csearch.collapse', 'Zuklappen') : t('cases.csearch.expand', 'Aufklappen')}>
 <svg className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
 </button>
 <span className="truncate">{t('cases.csearch.catRow', 'Suchen')} <strong>{csearchCatLabel(row.cat)}</strong> {t('cases.csearch.catRowFor', 'nach:')} <em className="not-italic font-semibold">{row.q}</em></span>
 <span className="ml-auto flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-surface-inset text-[var(--text-secondary)]">{row.count}</span>
 </div>
 );
 }
 if (row.type === 'item') {
 const it = row.item;
 const label = row.cat === 'kunde' || row.cat === 'bearbeiter' || row.cat === 'tag' ? it.name
 : row.cat === 'email' ? it.email
 : `#${it.id} · ${it.title || ''}`;
 return (
 <div key={`it-${row.cat}-${it.id ?? it.email}`} className={`${base} pl-9`} onClick={() => selectSgRow(row)} onMouseEnter={() => setSgHighlight(idx)}>
 <span className="truncate">{label}</span>
 {it.case_count != null && <span className="ml-auto flex-shrink-0 text-[10px] text-content-tertiary">{it.case_count}</span>}
 </div>
 );
 }
 if (row.type === 'more') {
 return (
 <div key={`more-${row.cat}`} className={`${base} pl-9 text-primary-600 text-xs font-semibold`} onClick={() => selectSgRow(row)} onMouseEnter={() => setSgHighlight(idx)}>
 {t('cases.csearch.more', 'mehr…')}
 </div>
 );
 }
 return (
 <div key="inhalt" className={`${base} border-t border-line mt-1 pt-2`} onClick={() => selectSgRow(row)} onMouseEnter={() => setSgHighlight(idx)}>
 <svg className="w-3.5 h-3.5 flex-shrink-0 text-content-tertiary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
 <span className="truncate">{t('cases.csearch.inhaltRow', 'Im Nachrichteninhalt suchen:')} <strong>„{row.q}"</strong></span>
 </div>
 );
 })}
 </div>
 )}
 {searchStats.active && (
 <span className="absolute -bottom-4 left-1 text-[10px] text-[var(--text-tertiary)] whitespace-nowrap">
 {searchStats.matchedResolved > 0
 ? t('cases.searchCounter.withResolved', { open: searchStats.matchedOpen, resolved: searchStats.matchedResolved })
 : t('cases.searchCounter.openOnly', { n: searchStats.matchedOpen })}
 {searchLoading && (
 <span className="text-primary-600"> · {omsLoading && !catLoading ? t('cases.searchOmsRunning') : t('cases.searchRunning')}</span>
 )}
 {/* #1381 R1/AK2 — OMS-Timeout/-Fehler: klarer Zustand statt hängendem Indikator, lokale Treffer bleiben */}
 {!searchLoading && omsFailed && (
 <span className="text-amber-600"> · {t('cases.searchOmsFailed')}</span>
 )}
 </span>
 )}
 </div>

 {/* #1378 AK5 — „Archiv durchsuchen": erweitert die Server-Suche auf ALLE Abgeschlossenen (include_archive=1) */}
 {searchStats.active && (
 <button onClick={() => setSearchArchive(v => !v)}
 className={`px-3 py-2 rounded-card text-sm font-semibold border transition-colors ${searchArchive
 ? 'border-primary-500/50 bg-surface-card text-primary-600 shadow-sm'
 : 'border-line bg-surface-inset text-[var(--text-secondary)] hover:text-content hover:border-line-hover'}`}
 title={t('cases.searchArchiveHint')}>
 {searchArchive ? t('cases.searchArchiveActive') : t('cases.searchArchive')}
 </button>
 )}

 {/* #1247 — Refresh-Pills entfernt, Intervall jetzt in /cases/einstellungen */}
 <button onClick={loadTasks} className="p-2 rounded-card bg-surface-inset border border-line text-[var(--text-secondary)] hover:text-content hover:border-line-hover transition-colors" title={t('cases.refresh')}>
 <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
 </button>

 {/* #1247 — Settings-Icon ⚙ verlinkt zur Cases-User-Settings-Page */}
 <Link to="/cases/einstellungen" className="p-2 rounded-card bg-surface-inset border border-line text-[var(--text-secondary)] hover:text-content hover:border-line-hover transition-colors" title={t('cases.settings', 'Einstellungen')}>
 <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
 </Link>

 {/* #1232 — AI-Search */}
 <button
 onClick={() => setAiSearchOpen(true)}
 className="px-3 py-2 rounded-card text-sm font-semibold border transition-colors flex items-center gap-1.5 border-line bg-surface-card text-[var(--text-secondary)] hover:text-content hover:border-line-hover"
 title={t('cases.aiSearch.button')}
 >
 <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2z"/><path d="M19 14l.7 2.1L22 17l-2.3.9L19 20l-.7-2.1L16 17l2.3-.9L19 14z"/></svg>
 <span className="hidden sm:inline">{t('cases.aiSearch.button')}</span>
 </button>

 {/* #1059 Punkt 1+4: + Neuer Case */}
 <button
 onClick={() => setCreateOpen(true)}
 className="px-3 py-2 rounded-card text-sm font-semibold text-white hover:opacity-90 transition-opacity flex items-center gap-1.5"
 style={{ backgroundColor: MODULE_COLOR }}
 title={t('cases.newCase')}
 >
 <Plus className="w-4 h-4" /> {t('cases.newCase')}
 </button>
 </div>
 </div>

 {/* Quick filters — #1247: Pills → Dropdowns */}
 <div className="px-6 lg:px-8 pb-3 flex items-center gap-2 flex-wrap">
 {/* #1289 — Status-Filter: erster Dropdown in der sticky Leiste (vorher eigene, wegscrollende Zeile in der ListView) */}
 {(() => {
 const stageItems = [
 { key: 'all', label: t('cases.stages.all'), badge: lvStageCounts.openTotal },
 ...stages.map(s => ({
 key: s.key,
 label: getStageLabel(s.name) || s.name,
 badge: s.key === 'done' ? doneCount : (lvStageCounts.counts[s.key] || 0),
 icon: <span className={`w-2 h-2 rounded-full ${stageTabDotCls(s.name)}`} />,
 })),
 ];
 const warnItems = [];
 if (lvUrgencyMetrics.slaBreached.length > 0) {
 warnItems.push({ key: 'urgency:sla', label: t('cases.slaBreached'), badge: lvUrgencyMetrics.slaBreached.length, variant: 'warn', icon: <AlertTriangle className="w-3 h-3" /> });
 }
 if (lvUrgencyMetrics.overdue24h.length > 0) {
 warnItems.push({ key: 'urgency:overdue24h', label: t('cases.overdue24h'), badge: lvUrgencyMetrics.overdue24h.length, variant: 'warn', icon: <Clock className="w-3 h-3" /> });
 }
 if (lvActiveTimerCount > 0) {
 warnItems.push({ key: 'urgency:timer', label: t('zeiterfassung.cases.filterTimerActive', 'mit Timer'), badge: lvActiveTimerCount, variant: 'warn' });
 }
 const combined = warnItems.length > 0 ? [...stageItems, { variant: 'divider' }, ...warnItems] : stageItems;
 const value = lvUrgency === 'sla' ? 'urgency:sla'
 : lvUrgency === 'overdue24h' ? 'urgency:overdue24h'
 : lvOnlyTimer ? 'urgency:timer'
 : activeStage;
 const activeItem = combined.find(it => it && it.key === value);
 const triggerLabel = activeItem ? activeItem.label : t('cases.stages.all');
 const onChange = (key) => {
 if (key === 'urgency:sla') {
 setLvUrgency('sla'); setActiveStage('all'); setLvOnlyTimer(false);
 } else if (key === 'urgency:overdue24h') {
 setLvUrgency('overdue24h'); setActiveStage('all'); setLvOnlyTimer(false);
 } else if (key === 'urgency:timer') {
 setLvOnlyTimer(true); setLvUrgency(null); setActiveStage('all');
 } else {
 setActiveStage(key); setLvUrgency(null); setLvOnlyTimer(false);
 }
 };
 return (
 <SimpleFilterDropdown
 icon={activeItem?.icon}
 triggerLabel={triggerLabel}
 items={combined}
 value={value}
 onChange={onChange}
 t={t}
 ariaLabel={t('cases.filterStageAria', 'Status-Filter')}
 />
 );
 })()}
 <span className="w-px h-5 bg-line mx-1" />
 {(() => {
 const assignItems = [
 { key: 'all', label: t('cases.allCases') },
 { key: 'me', label: t('cases.myCases') },
 ...(vertretungen.length > 0 ? [{ key: 'vertretungen', label: t('cases.vertretungen'), badge: vertretungen.reduce((s, v) => s + (v.offene_cases || 0), 0) }] : []),
 ...(watchedIds.size > 0 ? [{ key: 'watchlist', label: t('cases.watchlist'), badge: watchedIds.size }] : []),
 { key: 'unassigned', label: t('cases.unassigned') },
 ];
 const assignActive = assignItems.find(it => it.key === assignFilter) || assignItems[0];
 return (
 <SimpleFilterDropdown
 icon={<Users className="w-3 h-3" />}
 triggerLabel={assignActive.label}
 items={assignItems}
 value={assignFilter}
 onChange={setAssignFilter}
 t={t}
 ariaLabel={t('cases.filterAssignmentAria', 'Zuweisungs-Filter')}
 />
 );
 })()}
 {(() => {
 const sourceItems = [
 { key: 'all', label: t('cases.sourceAll') },
 { key: 'oms', label: t('cases.sourceOms', 'OMS') },
 { key: 'email', label: t('cases.sourceEmail', 'E-Mail'), icon: <Mail className="w-3 h-3" /> },
 ];
 const sourceActive = sourceItems.find(it => it.key === sourceFilter) || sourceItems[0];
 return (
 <SimpleFilterDropdown
 icon={<Mail className="w-3 h-3" />}
 triggerLabel={sourceActive.label}
 items={sourceItems}
 value={sourceFilter}
 onChange={setSourceFilter}
 t={t}
 ariaLabel={t('cases.filterSourceAria', 'Quellen-Filter')}
 />
 );
 })()}
 {/* Kunde filter */}
 <span className="w-px h-5 bg-line mx-1" />
 <KundeFilterDropdown kundenList={kundenListMain} value={kundeFilter} onChange={setKundeFilter} t={t} />

 {/* #1250 — „Zugewiesen"-Filter: aktive interne MA (derselbe Resolver wie #1249 D) + Alle + Nicht zugewiesen */}
 {(() => {
 const assigneeItems = [
 { key: 'all', label: t('cases.assigneeAll', 'Alle Zugewiesenen') },
 { key: 'none', label: t('cases.assigneeNone', 'Nicht zugewiesen') },
 ...internalUsers.map(u => ({ key: String(u.id), label: `${u.vorname || ''} ${u.nachname || ''}`.trim() || u.email })),
 ];
 const assigneeActive = assigneeItems.find(it => it.key === assigneeFilter) || assigneeItems[0];
 return (
 <SimpleFilterDropdown
 icon={<Users className="w-3 h-3" />}
 triggerLabel={assigneeActive.label}
 items={assigneeItems}
 value={assigneeFilter}
 onChange={setAssigneeFilter}
 t={t}
 ariaLabel={t('cases.filterAssigneeAria', 'Zugewiesen-Filter')}
 />
 );
 })()}

 {/* Category filter */}
 {allCategories.length > 0 && (
 <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
 className="px-3 py-1.5 rounded-lg text-[11px] font-semibold border border-line text-[var(--text-secondary)] bg-surface-card focus:outline-none cursor-pointer">
 <option value="all">{t('cases.allCategories')}</option>
 {allCategories.filter(c => c.is_active).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
 </select>
 )}

 {/* Tag filter (multi-select with search) */}
 {allTags.length > 0 && (() => {
 const isActive = tagFilter.length > 0;
 const label = isActive ? t('cases.tagsSelected', { count: tagFilter.length }) : t('cases.allTags');
 return <TagFilterDropdown allTags={allTags} selected={tagFilter} onChange={setTagFilter} label={label} isActive={isActive} t={t} />;
 })()}

 {urgencyFilter && (
 <button onClick={() => setUrgencyFilter(null)}
 className="px-3 py-1.5 rounded-lg text-[11px] font-semibold border border-red-500/30 bg-red-500/10 text-red-400 flex items-center gap-1.5 hover:bg-red-500/15 transition-colors">
 {urgencyFilter === 'sla' ? t('cases.slaBreached') : urgencyFilter === 'overdue24h' ? t('cases.overdue24h') : t('cases.unassignedCount')}
 <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
 </button>
 )}
 {/* #1248: Checkbox "Erledigte ausblenden" entfernt — "Abgeschlossen" ist jetzt eine reguläre Stage im Status-Dropdown. */}
 {/* #1505 — pro-User Custom-Filter: "+"-Button + Toggle-Chips am Leisten-Ende */}
 <span className="w-px h-5 bg-line mx-1" />
 <CasesCustomFilters filters={customFilters} optionsFor={cfOptionsFor} onReload={loadCustomFilters} t={t} />
 </div>

 {/* Vertretungs-Banner */}
 {vertretungen.length > 0 && assignFilter !== 'vertretungen' && (
 <div className="mx-6 lg:mx-8 mb-3 px-4 py-3 rounded-card border-l-[3px] border-l-amber-500 bg-amber-500/5 border border-amber-500/10">
 {vertretungen.length === 1 ? (
 <div className="flex items-center justify-between gap-3">
 <div className="flex items-center gap-2 text-sm text-content">
 <Users className="w-4 h-4 text-amber-500 flex-shrink-0" />
 <span>{t('cases.vertretungBannerSingle', { name: vertretungen[0].user.name, grund: vertretungen[0].grund ? `(${vertretungen[0].grund})` : '' })}</span>
 </div>
 <div className="flex items-center gap-3 flex-shrink-0">
 <span className="text-xs text-content-secondary">{vertretungen[0].offene_cases} {t('cases.offeneCases')}</span>
 <button onClick={() => setAssignFilter('vertretungen')} className="text-xs font-semibold text-amber-600 hover:text-amber-500 transition-colors">{t('cases.showVertretungCases')}</button>
 </div>
 </div>
 ) : (
 <div>
 <div className="flex items-center gap-2 text-sm text-content mb-2">
 <Users className="w-4 h-4 text-amber-500 flex-shrink-0" />
 <span>{t('cases.vertretungBannerMulti', { count: vertretungen.length })}</span>
 </div>
 <div className="space-y-1 ml-6">
 {vertretungen.map(v => (
 <div key={v.id} className="flex items-center justify-between text-xs text-content-secondary">
 <span>{v.user.name}{v.grund ? ` (${v.grund})` : ''}</span>
 <span>{v.offene_cases} Cases</span>
 </div>
 ))}
 </div>
 <div className="mt-2 ml-6">
 <button onClick={() => setAssignFilter('vertretungen')} className="text-xs font-semibold text-amber-600 hover:text-amber-500 transition-colors">{t('cases.showVertretungCases')}</button>
 </div>
 </div>
 )}
 </div>
 )}

 {error && <div className="mx-6 lg:mx-8 mb-3 px-4 py-2.5 rounded-card bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}

 {/* ── Content ── */}
 {loading ? (
 <div className="flex-1 overflow-auto px-6 lg:px-8 pb-6"><ListSkeleton /></div>
 ) : view === 'kanban' ? (
 <div className="flex-1 flex min-h-0 overflow-hidden">
 <div className="flex-1 min-w-0 overflow-x-auto px-6 lg:px-8 pb-6">
 <div className="flex gap-3 min-h-full" style={{ minWidth: `${displayStages.reduce((w, s) => w + (collapsedStages?.has(s.key) ? 60 : 316), 0)}px` }}>
 {displayStages.map(s => <KCol key={s.key} stage={s} tasks={byStage[s.key]||[]} stages={stages} omsUsers={omsUsers} onOpen={openCase} onPreview={(id, source) => setPreviewId({ id, source })} onRefresh={loadTasks} rrId={rrId} doneId={doneId} waitingId={waitingId} collapsed={collapsedStages?.has(s.key)} onToggleCollapse={toggleCollapse} t={t} />)}
 </div>
 </div>
 <RightSidebar metrics={urgencyMetrics} urgencyFilter={urgencyFilter} setUrgencyFilter={setUrgencyFilter} previewData={previewData} previewLoading={previewLoading} setPreviewId={setPreviewId} onOpenFull={(id, source) => { openCase(id, source); setPreviewId(null); }} t={t} />
 </div>
 ) : (
 <>
 {/* #1380 K1 — Server-Cap-Hinweis: limit (200) erreicht → es fehlen lokale Treffer, Suche verfeinern */}
 {searchStats.active && catTruncated && (
 <div className="px-6 lg:px-8 pb-3">
 <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-card border border-amber-300 bg-surface-inset text-sm text-[var(--text-secondary)]">
 <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>
 <span>{t('cases.searchTruncatedNotice')}</span>
 </div>
 </div>
 )}
 {/* #1112 — Empty-State-Banner bei 0 Treffern */}
 {/* #1378 AK3 — NIE verfrühtes „Keine Treffer", solange lokal ODER OMS noch lädt (searchLoading) */}
 {searchStats.active && !searchLoading && filtered.length === 0 && (
 <div className="px-6 lg:px-8 pb-3">
 <div className="flex items-center gap-2.5 px-4 py-3 rounded-card border border-line bg-surface-inset text-sm text-[var(--text-secondary)]">
 <svg className="w-4 h-4 text-content-tertiary flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
 <span>{t('cases.emptyBanner.noResults')}</span>
 {/* #1378 AK5 — direkter Absprung: Suche aufs ganze Archiv erweitern */}
 {!searchArchive && (
 <button onClick={() => setSearchArchive(true)} className="ml-auto text-primary-600 font-semibold hover:underline whitespace-nowrap">
 {t('cases.searchArchive')}
 </button>
 )}
 </div>
 </div>
 )}
 <ListView tasks={filtered} allTasks={vertretungTasks} stages={stages} omsUsers={omsUsers} onOpen={openCase} onRefresh={loadTasks} rrId={rrId} doneId={doneId} waitingId={waitingId} activeStage={activeStage} onSetActiveStage={setActiveStage} doneCount={doneCount} setToast={setToast} watchedIds={watchedIds} toggleWatch={toggleWatch} activeTimers={activeTimers} listPresence={listPresence} currentUserId={user?.id} vertretungFuerMap={inVertretungView ? vertretungFuerMap : null} t={t} lvUrgency={lvUrgency} lvOnlyTimer={lvOnlyTimer} searchActive={chipsActive} searchPool={mergedSearchResults} outsideMode={outsideMode} showOutside={showOutside} onShowOutside={setShowOutside} searchImmune={searchImmune} searchStrictKeys={strictKeySet} />
 </>
 )}

 {/* #1059 Punkt 1+4: + Neuer Case Modal */}
 {createOpen && (
 <CreateCaseModal
 onClose={() => setCreateOpen(false)}
 onCreated={(caseId) => { setCreateOpen(false); loadTasks(); openCase(caseId); }}
 kunden={kundenListMain}
 stages={stages}
 t={t}
 getStageLabelShort={getStageLabelShort}
 />
 )}

 {/* #1232 — AI-Search Modal */}
 <AISearchModal
 open={aiSearchOpen}
 onClose={() => setAiSearchOpen(false)}
 onSelectCase={(r) => { openCase(r.case_id, r.case_source); }}
 />


 {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
 </div>
 );
}

// ─── + Neuer Case Modal (#1059 Punkt 1+4) ────────────────────────────────
function CreateCaseModal({ onClose, onCreated, kunden, stages, t, getStageLabelShort }) {
 const [title, setTitle] = useState('');
 const [source, setSource] = useState('phone');
 const [kundeId, setKundeId] = useState('');
 const [manualEmail, setManualEmail] = useState('');
 const [defaultStatus, setDefaultStatus] = useState('waiting_for_support');
 const [priority, setPriority] = useState('medium');
 const [sendMail, setSendMail] = useState(false);
 // #1160 — CC/BCC im Case-Anlegen-Dialog (greift nur bei sendMail)
 const [cc, setCc] = useState([]);
 const [bcc, setBcc] = useState([]);
 const [showCc, setShowCc] = useState(false);
 const [showBcc, setShowBcc] = useState(false);
 const [bodyHtml, setBodyHtml] = useState('');
 const [files, setFiles] = useState([]);
 const [dragOver, setDragOver] = useState(false);
 const [submitting, setSubmitting] = useState(false);
 const [err, setErr] = useState('');
 // #1685 BR-58 P-B — Signatur-Hinweis (konsistent zum Reply-Composer #1131)
 const [userSig, setUserSig] = useState(null);
 const [showSigPreview, setShowSigPreview] = useState(false);
 // #1685 — Inline-Bilder deferred: beim Pasten existiert der Case noch nicht →
 // Datei lokal halten (Blob-URL im Editor), Upload erst beim Senden (Multi-Step).
 const inlineMapRef = useRef(new Map()); // blobUrl → File

 useEffect(() => {
  fetch(`${API}/api/users/me/signatur`, { credentials: 'include' })
   .then(r => r.ok ? r.json() : null)
   .then(d => setUserSig(d || null))
   .catch(() => setUserSig(null));
 }, []);

 // #1685 — Blob-URLs beim Schließen freigeben
 useEffect(() => () => {
  for (const url of inlineMapRef.current.keys()) URL.revokeObjectURL(url);
 }, []);

 const handleInlineImageUpload = async (file) => {
  const url = URL.createObjectURL(file);
  inlineMapRef.current.set(url, file);
  return { src: url, alt: file.name || '' };
 };
 const handleInlineImageError = (reason, file) => {
  const name = file?.name || 'Bild';
  if (reason === 'type') setErr(t('cases.inlineImageBadType').replace('{name}', name));
  else if (reason === 'size') setErr(t('cases.inlineImageTooLarge').replace('{name}', name));
  else setErr(t('cases.create.failed'));
 };

 // #1247 — 25MB Total-Limit, max 10 Files (Server-Limit /api/cases/:id/attachments)
 const MAX_TOTAL_BYTES = 25 * 1024 * 1024;
 const MAX_FILES = 10;
 const totalBytes = files.reduce((s, f) => s + f.size, 0);
 const overLimit = totalBytes > MAX_TOTAL_BYTES;

 // #1685 — funktional (setFiles(prev)): der Editor hält die Drop-Callback-Closure
 // vom Mount → mit direktem `files`-Zugriff wäre der State dort stale.
 const addFiles = (newList) => {
 setFiles(prev => {
 const next = [...prev];
 for (const f of newList) {
 if (next.length >= MAX_FILES) break;
 // Dedupe by name+size
 if (next.find(x => x.name === f.name && x.size === f.size)) continue;
 next.push(f);
 }
 return next;
 });
 };
 const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));
 const fmtSize = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

 const sourceOptions = [
 { v: 'phone', label: t('cases.create.sourcePhone') },
 { v: 'whatsapp', label: t('cases.create.sourceWhatsapp') },
 { v: 'in_person', label: t('cases.create.sourceInPerson') },
 { v: 'email', label: t('cases.create.sourceEmail') },
 { v: 'manual', label: t('cases.create.sourceManual') },
 ];

 // Stage-Liste filter: nur lokale Status-Keys
 const localStages = (stages || []).filter(s => s.key && s.key !== 'oms_only');
 const stageList = localStages.length ? localStages : [
 { key: 'waiting_for_support', name: t('cases.stage.waiting_for_support') },
 { key: 'in_progress', name: t('cases.stage.in_progress') },
 { key: 'clarification_with_recipient', name: t('cases.stage.clarification_with_recipient') },
 { key: 'solution_proposed', name: t('cases.stage.solution_proposed') },
 ];

 const submit = async () => {
 setErr('');
 if (!title.trim()) { setErr(t('cases.create.titleRequired')); return; }
 if (sendMail && !kundeId && !manualEmail.trim()) {
 setErr(t('cases.create.recipientRequired'));
 return;
 }
 if (manualEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manualEmail.trim())) {
 setErr(t('cases.create.emailInvalid'));
 return;
 }
 if (overLimit) {
 setErr(t('cases.create.filesTotalTooLarge', { max: '25 MB' }));
 return;
 }
 setSubmitting(true);
 try {
 const hasFiles = files.length > 0;
 const hasBody = !!bodyHtml.trim();
 // #1686 — im Body referenzierte Inline-Bilder, unabhängig vom Mail-Toggle
 // (Kunden-Mail → CID-Kette via /message; interne Notiz → served URLs in der Bubble).
 // Gelöschte Bilder = Blob-URL nicht mehr im HTML → werden NICHT hochgeladen.
 const inlineBlobUrls = [...inlineMapRef.current.keys()].filter(u => bodyHtml.includes(u));
 const hasInline = inlineBlobUrls.length > 0;

 if (!hasFiles && !hasInline) {
 // #1247 — Single-Step: ohne Files läuft alles wie bisher.
 // Tiptap liefert bereits HTML — kein plaintext→HTML mehr nötig.
 const payload = {
 title: title.trim(),
 source,
 default_status: defaultStatus,
 priority,
 send_initial_mail: sendMail,
 };
 if (kundeId) payload.kunde_id = Number(kundeId);
 if (manualEmail.trim()) payload.manual_customer_email = manualEmail.trim();
 // #1160 — CC/BCC nur bei sendMail
 if (sendMail && cc.length) payload.cc = cc;
 if (sendMail && bcc.length) payload.bcc = bcc;
 if (hasBody) {
 payload.body_html = bodyHtml;
 if (!sendMail) payload.is_internal = true;
 }
 const res = await fetch(`${API}/api/cases/manual-create`, {
 method: 'POST',
 credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(payload),
 });
 const data = await res.json();
 if (!res.ok) { setErr(data.error || t('cases.create.failed')); setSubmitting(false); return; }
 onCreated(data.case_id);
 return;
 }

 // #1247 — Multi-Step bei Files (#1685: auch bei Inline-Bildern):
 // 1) manual-create (ohne body + ohne Mail) → Case anlegen
 // 2) /attachments → Datei-Upload, attachment_ids einsammeln
 //    (#1685: Inline-Bilder einzeln hochladen, Blob-URL im Body → served Inline-URL)
 // 3) /message → Comment mit body + attachment_ids; bei sendMail wird Mail inkl. Anhängen
 //    verschickt — der geteilte Send-Pfad macht CID-Umschreibung (#1676), Signatur (#1683)
 //    und OMS-img-Strip (#1678); Inline-IDs werden serverseitig nicht als Ghost-Anhang gelistet.
 const createPayload = {
 title: title.trim(),
 source,
 default_status: defaultStatus,
 priority,
 send_initial_mail: false,
 };
 if (kundeId) createPayload.kunde_id = Number(kundeId);
 if (manualEmail.trim()) createPayload.manual_customer_email = manualEmail.trim();
 const createRes = await fetch(`${API}/api/cases/manual-create`, {
 method: 'POST',
 credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(createPayload),
 });
 const createData = await createRes.json();
 if (!createRes.ok) { setErr(createData.error || t('cases.create.failed')); setSubmitting(false); return; }
 const caseId = createData.case_id;

 // 2a) #1685 — Inline-Bilder EINZELN hochladen (deterministisches id↔Blob-URL-Mapping),
 //     Blob-URLs im Body auf die served Inline-URL umschreiben.
 let finalBodyHtml = bodyHtml;
 const inlineAttIds = [];
 for (const blobUrl of inlineBlobUrls) {
 const file = inlineMapRef.current.get(blobUrl);
 if (!file) continue;
 const ifd = new FormData();
 ifd.append('files', file, file.name || 'inline-image.png');
 const ir = await fetch(`${API}/api/cases/${caseId}/attachments`, {
 method: 'POST', credentials: 'include', body: ifd,
 });
 const idata = await ir.json().catch(() => ({}));
 const attId = ir.ok ? (idata.attachments || [])[0]?.id : null;
 if (!attId) {
 setErr(idata.error || t('cases.create.attachmentUploadFailed', 'Datei-Upload fehlgeschlagen'));
 // Case existiert bereits → öffnen, User kann im Case weiterarbeiten
 onCreated(caseId);
 return;
 }
 finalBodyHtml = finalBodyHtml.split(blobUrl).join(`/api/cases/attachments/${attId}/inline`);
 inlineAttIds.push(attId);
 }

 // 2b) Upload files (normale Anhänge)
 let attachmentIds = [];
 if (hasFiles) {
 const fd = new FormData();
 files.forEach(f => fd.append('files', f, f.name));
 const upRes = await fetch(`${API}/api/cases/${caseId}/attachments`, {
 method: 'POST', credentials: 'include', body: fd,
 });
 const upData = await upRes.json().catch(() => ({}));
 if (!upRes.ok) {
 setErr(upData.error || t('cases.create.attachmentUploadFailed', 'Datei-Upload fehlgeschlagen'));
 // Case existiert bereits → trotzdem öffnen, damit der User die Anhänge manuell hochladen kann
 onCreated(caseId);
 return;
 }
 attachmentIds = (upData.attachments || []).map(a => a.id);
 }

 // 3) Comment mit body + attachment_ids (#1685: inkl. Inline-IDs → sendCaseReplyEmail
 //    findet sie via comment_id und schreibt sie auf cid: um)
 if (hasBody || attachmentIds.length > 0 || inlineAttIds.length > 0) {
 // Body Fallback: wenn nur Anhänge ohne Text, schreibe einen Platzhalter-Satz,
 // sonst lehnt /message wegen "body erforderlich" ab.
 const finalBody = hasBody
 ? finalBodyHtml
 : `<p>${attachmentIds.length === 1
 ? t('cases.create.attachmentBodyFallbackSingle', 'Anhang siehe unten.')
 : t('cases.create.attachmentBodyFallbackMulti', 'Anhänge siehe unten.')}</p>`;
 const messagePayload = {
 body: finalBody,
 is_internal: !sendMail,
 attachment_ids: [...attachmentIds, ...inlineAttIds],
 };
 // #1160 — CC/BCC bei Multi-Step (Files) ebenfalls durchreichen (nur bei sendMail)
 if (sendMail && cc.length) messagePayload.cc = cc;
 if (sendMail && bcc.length) messagePayload.bcc = bcc;
 await fetch(`${API}/api/cases/${caseId}/message`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(messagePayload),
 }).catch(() => { /* best-effort, Case ist da, Attachment auch */ });
 }
 onCreated(caseId);
 } catch (e) {
 setErr(e.message);
 setSubmitting(false);
 }
 };

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
 <div className="bg-surface-card rounded-card border border-line shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
 <div className="px-5 py-4 border-b border-line flex items-center justify-between">
 <h2 className="text-base font-bold text-content flex items-center gap-2">
 <Plus className="w-4 h-4" /> {t('cases.create.title')}
 </h2>
 <button onClick={onClose} className="text-content-tertiary hover:text-content"><X className="w-4 h-4" /></button>
 </div>

 <div className="overflow-y-auto px-5 py-4 space-y-4">
 {/* Titel */}
 <div>
 <label className="block text-[11px] font-semibold text-content-secondary mb-1.5 uppercase tracking-wide">
 {t('cases.create.titleLabel')} <span className="text-red-500">*</span>
 </label>
 <input type="text" value={title} onChange={e => setTitle(e.target.value)} maxLength={500}
 placeholder={t('cases.create.titlePlaceholder')}
 className="w-full bg-surface-inset border border-line rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:border-primary-500/50" />
 </div>

 <div className="grid grid-cols-2 gap-3">
 {/* Quelle */}
 <div>
 <label className="block text-[11px] font-semibold text-content-secondary mb-1.5 uppercase tracking-wide">{t('cases.create.sourceLabel')}</label>
 <select value={source} onChange={e => setSource(e.target.value)}
 className="w-full bg-surface-inset border border-line rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:border-primary-500/50">
 {sourceOptions.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
 </select>
 </div>

 {/* Priority */}
 <div>
 <label className="block text-[11px] font-semibold text-content-secondary mb-1.5 uppercase tracking-wide">{t('cases.create.priorityLabel')}</label>
 <select value={priority} onChange={e => setPriority(e.target.value)}
 className="w-full bg-surface-inset border border-line rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:border-primary-500/50">
 <option value="low">{t('cases.priority.low')}</option>
 <option value="medium">{t('cases.priority.medium')}</option>
 <option value="high">{t('cases.priority.high')}</option>
 <option value="urgent">{t('cases.priority.urgent')}</option>
 </select>
 </div>
 </div>

 {/* Default Status */}
 <div>
 <label className="block text-[11px] font-semibold text-content-secondary mb-1.5 uppercase tracking-wide">{t('cases.create.defaultStatusLabel')}</label>
 <select value={defaultStatus} onChange={e => setDefaultStatus(e.target.value)}
 className="w-full bg-surface-inset border border-line rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:border-primary-500/50">
 {stageList.map(s => {
   // #1071-A — i18n-Stage-Label statt EN-OMS-Name. getStageLabelShort mappt
   // EN-Stage-Name aus API auf lokalisierten i18n-Key (de.json/en.json).
   const label = getStageLabelShort ? getStageLabelShort(s.name) : (s.name || s.key);
   return <option key={s.key} value={s.key}>{label || s.key}</option>;
 })}
 </select>
 </div>

 {/* Kunde */}
 <div>
 <label className="block text-[11px] font-semibold text-content-secondary mb-1.5 uppercase tracking-wide">{t('cases.create.kundeLabel')}</label>
 <SearchableSelect
 value={kundeId}
 onChange={(v) => setKundeId(v)}
 placeholder={t('cases.create.kundeSearch')}
 emptyText={t('cases.create.kundeNoMatch')}
 clearable
 options={[
 { value: '', label: t('cases.create.kundeNone') },
 ...(kunden || []).map(k => ({
 value: String(k.id),
 label: k.name,
 })),
 ]}
 />
 </div>

 {/* Manual Email — #1160: Autocomplete mit Kontakte-Vorschlägen */}
 <div>
 <label className="block text-[11px] font-semibold text-content-secondary mb-1.5 uppercase tracking-wide">{t('cases.create.manualEmailLabel')}</label>
 <ContactAutocomplete
 value={manualEmail}
 onChange={(v) => setManualEmail(typeof v === 'string' ? v : '')}
 kundeId={kundeId || null}
 placeholder={t('cases.create.manualEmailPlaceholder')}
 multiple={false}
 />
 <p className="text-[10px] text-content-tertiary mt-1">{t('cases.create.manualEmailHint')}</p>
 </div>

 {/* Toggle: Initiale Mail senden */}
 <label className="flex items-start gap-2.5 p-3 rounded-lg bg-surface-inset border border-line cursor-pointer">
 <input type="checkbox" checked={sendMail} onChange={e => setSendMail(e.target.checked)}
 className="mt-0.5 w-4 h-4 rounded text-primary-500 focus:ring-primary-500/30" />
 <div className="flex-1">
 <div className="text-sm font-semibold text-content">{t('cases.create.sendMailLabel')}</div>
 <div className="text-[11px] text-content-tertiary mt-0.5">{t('cases.create.sendMailHint')}</div>
 </div>
 </label>

 {/* Mail-Banner — reactive to sendMail toggle (#1059 fix2)
     #1220 — Phantom-Input entfernt: bei sendMail=OFF gab es eine Box, die wie ein
     leeres Input-Feld aussah und nur den Hint-Text duplizierte. Hint steht jetzt
     bereits unter der Checkbox (cases.create.sendMailHint). Nur der gruene
     Bestaetigungs-Banner bleibt fuer sendMail=ON. */}
 {sendMail && (() => {
  const direct = (manualEmail || '').trim();
  const sel = (kunden || []).find(k => String(k.id) === String(kundeId));
  const recipient = direct || (sel?.name ? sel.name : t('cases.create.bannerRecipientPlaceholder'));
  return (
   <div className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-[12px] text-emerald-700">
    {t('cases.create.bannerMail').replace('{recipient}', recipient)}
   </div>
  );
 })()}

 {/* #1160 — CC/BCC nur bei sendMail */}
 {sendMail && (
  <div className="space-y-2">
   <div className="flex items-center gap-3">
    {!showCc && (
     <button type="button" onClick={() => setShowCc(true)}
      className="text-[11px] font-semibold text-[#338edc] hover:underline">
      + {t('cases.create.addCc')}
     </button>
    )}
    {!showBcc && (
     <button type="button" onClick={() => setShowBcc(true)}
      className="text-[11px] font-semibold text-[#338edc] hover:underline">
      + {t('cases.create.addBcc')}
     </button>
    )}
   </div>
   {showCc && (
    <div>
     <div className="flex items-center justify-between mb-1.5">
      <label className="block text-[11px] font-semibold text-content-secondary uppercase tracking-wide">{t('cases.cc')}</label>
      <button type="button" onClick={() => { setShowCc(false); setCc([]); }}
       className="text-[10px] text-content-tertiary hover:text-content-secondary">{t('cases.hideCc')}</button>
     </div>
     <ContactAutocomplete
      value={cc}
      onChange={setCc}
      kundeId={kundeId || null}
      placeholder={t('cases.create.ccPlaceholder')}
      multiple
     />
    </div>
   )}
   {showBcc && (
    <div>
     <div className="flex items-center justify-between mb-1.5">
      <label className="block text-[11px] font-semibold text-content-secondary uppercase tracking-wide">{t('cases.bcc')}</label>
      <button type="button" onClick={() => { setShowBcc(false); setBcc([]); }}
       className="text-[10px] text-content-tertiary hover:text-content-secondary">{t('cases.hideBcc')}</button>
     </div>
     <ContactAutocomplete
      value={bcc}
      onChange={setBcc}
      kundeId={kundeId || null}
      placeholder={t('cases.create.bccPlaceholder')}
      multiple
     />
    </div>
   )}
  </div>
 )}

 {/* Body — #1247: Tiptap-RichText statt plain textarea */}
 <div>
 <label className="block text-[11px] font-semibold text-content-secondary mb-1.5 uppercase tracking-wide">
 {sendMail ? t('cases.create.bodyMailLabel') : t('cases.create.bodyNoteLabel')}
 </label>
 {/* #1686 — Inline-Bilder IMMER an (Problemstellungs-Feld): Kunden-Mail → CID-Kette,
     interne Notiz → served URLs in der Bubble; OMS-Writes sind serverseitig img-gestrippt
     (#1678: manual-create description + /message omsBody). key-Remount nur noch für
     Placeholder/isInternal-Wechsel — img bleibt in beiden Modi im Schema erhalten. */}
 <RichTextEditor
 key={sendMail ? 'create-mail' : 'create-note'}
 value={bodyHtml}
 onChange={setBodyHtml}
 placeholder={sendMail ? t('cases.create.bodyMailPlaceholder') : t('cases.create.bodyNotePlaceholder')}
 isInternal={!sendMail}
 minHeight={260}
 maxHeight={560}
 resizable
 toolbarMode="always"
 enableInlineImages
 onInlineImageUpload={handleInlineImageUpload}
 onInlineImageError={handleInlineImageError}
 onNonImageFiles={addFiles}
 tooltips={{
 bold: t('cases.editor.bold', 'Fett'),
 italic: t('cases.editor.italic', 'Kursiv'),
 underline: t('cases.editor.underline', 'Unterstrichen'),
 bulletList: t('cases.editor.bulletList', 'Aufzählung'),
 orderedList: t('cases.editor.orderedList', 'Nummerierte Liste'),
 link: t('cases.editor.link', 'Link'),
 code: t('cases.editor.code', 'Inline-Code'),
 resize: t('cases.dragToResize'),
 }}
 />
 {/* #1685 — Signatur-Hinweis wie Reply-Composer (#1131 Idee 1): BE hängt die
     Signatur beim Senden an (manual-create #1058 P10 bzw. /message #1683) */}
 {sendMail && userSig?.signatur_use_cases && userSig?.signatur_html?.trim() && (
 <div className="mt-2 rounded-[10px] border border-dashed border-[var(--border-default,#E5E7EB)] dark:border-line bg-[var(--bg-inset,#F8F9FC)] dark:bg-surface-inset">
 <button type="button" onClick={() => setShowSigPreview(v => !v)}
   className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-[var(--bg-hover,#F3F4F6)] dark:hover:bg-surface-hover rounded-[10px] transition-colors">
 <span className="text-[11px] text-content-tertiary truncate">
 <span className="font-medium">{t('cases.signaturePreviewLabel')}:</span> {t('cases.signatureHintCollapsed')}
 </span>
 <span className="text-[10px] text-[#338edc] hover:underline whitespace-nowrap flex-shrink-0">
 {showSigPreview ? t('cases.signatureHidePreview') : t('cases.signatureShowPreview')}
 </span>
 </button>
 {showSigPreview && (
 <div className="px-3 pb-2 pt-1 border-t border-[var(--border-default,#E5E7EB)]/50 dark:border-line/50">
 <div className="text-[12px] text-content-secondary [&_p]:my-0 [&_a]:underline" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(userSig.signatur_html, PURIFY_CONFIG) }} />
 </div>
 )}
 </div>
 )}
 </div>

 {/* Anhänge — #1247: Drag&Drop Multi-Upload (max 25MB total, 10 Files) */}
 <div>
 <label className="block text-[11px] font-semibold text-content-secondary mb-1.5 uppercase tracking-wide">
 {t('cases.create.attachmentsLabel', 'Anhänge')}
 </label>
 <div
 onDragOver={e => { e.preventDefault(); setDragOver(true); }}
 onDragLeave={() => setDragOver(false)}
 onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(Array.from(e.dataTransfer.files || [])); }}
 className={`relative border-2 border-dashed rounded-lg px-4 py-5 text-center transition-colors cursor-pointer ${
 dragOver
 ? 'border-[#338edc] bg-[#338edc]/5'
 : 'border-line bg-surface-inset hover:border-line-hover'
 }`}
 onClick={() => document.getElementById('case-create-file-input')?.click()}
 >
 <input
 id="case-create-file-input"
 type="file"
 multiple
 className="hidden"
 onChange={e => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
 />
 <Paperclip className="w-5 h-5 mx-auto text-content-tertiary mb-1.5" />
 <div className="text-xs text-content-secondary">
 {t('cases.create.attachmentsDropHint', 'Dateien hierher ziehen oder klicken zum Auswählen')}
 </div>
 <div className="text-[10px] text-content-tertiary mt-0.5">
 {t('cases.create.attachmentsLimit', { count: MAX_FILES, max: '25 MB' })}
 </div>
 </div>
 {files.length > 0 && (
 <div className="mt-2 space-y-1.5">
 {files.map((f, i) => (
 <div key={`${f.name}-${f.size}-${i}`} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md bg-surface-inset border border-line">
 <div className="flex items-center gap-2 min-w-0">
 <FileText className="w-3.5 h-3.5 text-content-tertiary flex-shrink-0" />
 <span className="text-xs text-content truncate" title={f.name}>{f.name}</span>
 <span className="text-[10px] text-content-tertiary flex-shrink-0">{fmtSize(f.size)}</span>
 </div>
 <button
 type="button"
 onClick={() => removeFile(i)}
 className="text-content-tertiary hover:text-red-500 transition-colors flex-shrink-0"
 title={t('common.remove', 'Entfernen')}
 >
 <X className="w-3.5 h-3.5" />
 </button>
 </div>
 ))}
 <div className={`text-[10px] mt-1 ${overLimit ? 'text-red-500 font-semibold' : 'text-content-tertiary'}`}>
 {t('cases.create.attachmentsTotalSize', { size: fmtSize(totalBytes), max: '25 MB' })}
 {overLimit && <span className="ml-1">— {t('cases.create.attachmentsOverLimit', 'Limit überschritten')}</span>}
 </div>
 </div>
 )}
 </div>

 {err && (
 <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 text-xs">{err}</div>
 )}
 </div>

 <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
 <button onClick={onClose} disabled={submitting}
 className="px-4 py-2 rounded-lg border border-line text-sm font-semibold text-content-secondary hover:text-content hover:border-line-hover transition-colors disabled:opacity-50">
 {t('common.cancel')}
 </button>
 <button onClick={submit} disabled={submitting || !title.trim() || overLimit}
 className="px-4 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
 style={{ backgroundColor: MODULE_COLOR }}>
 {submitting && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
 {submitting ? t('cases.create.submitting') : (sendMail ? t('cases.create.submitWithMail') : t('cases.create.submit'))}
 </button>
 </div>
 </div>
 </div>
 );
}

// ─── Task Slide-Over (read-only task detail opened from linked tasks) ──────
function TaskSlideOver({ taskId, onClose, onRefresh, t }) {
 const [detail, setDetail] = useState(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);

 useEffect(() => {
 if (!taskId) return;
 setLoading(true);
 setError(null);
 fetch(`${API}/api/user-tasks/${taskId}`, { credentials: 'include' })
 .then(r => { if (!r.ok) throw new Error(r.status === 403 ? t('cases.taskNoAccess', 'Keine Berechtigung') : t('cases.taskNotFound', 'Task nicht gefunden')); return r.json(); })
 .then(d => { setDetail(d.task); setLoading(false); })
 .catch(e => { setError(e.message); setLoading(false); });
 }, [taskId]);

 const statusColors = {
 offen: 'bg-blue-100 text-blue-700',
 in_bearbeitung: 'bg-amber-100 text-amber-700',
 erledigt: 'bg-green-100 text-green-700',
 verschoben: 'bg-amber-100 text-amber-700',
 };
 const statusLabels = {
 offen: t('cases.ltStatusOpen', 'Offen'),
 in_bearbeitung: t('cases.ltStatusWip', 'In Arbeit'),
 erledigt: t('cases.ltStatusDone', 'Erledigt'),
 verschoben: t('cases.ltStatusDeferred', 'Verschoben'),
 };
 const prioLabels = {
 urgent: t('cases.taskPrioUrgent', 'Dringend'),
 high: t('cases.taskPrioHigh', 'Hoch'),
 normal: t('cases.taskPrioNormal', 'Normal'),
 low: t('cases.taskPrioLow', 'Niedrig'),
 };

 const isDone = detail?.status === 'erledigt';
 const dueDate = detail?.due_date ? new Date(detail.due_date + (String(detail.due_date).length === 10 ? 'T00:00:00' : '')) : null;
 const isOverdue = !isDone && dueDate && !isNaN(dueDate.getTime()) && dueDate < new Date(new Date().toDateString());

 const initials = (name) => {
 if (!name) return '?';
 const p = name.trim().split(/\s+/);
 return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
 };

 const handleStatusChange = async (newStatus) => {
 try {
 const r = await fetch(`${API}/api/user-tasks/${taskId}/status`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ status: newStatus }),
 });
 if (r.ok) {
 const d = await r.json();
 setDetail(d.task);
 onRefresh?.();
 }
 } catch { /* */ }
 };

 return (
 <>
 <div className="fixed inset-0 bg-black/40 z-[80]" onClick={onClose} />
 <div className="fixed inset-y-0 right-0 z-[85] w-full max-w-xl bg-[var(--bg-card,#fff)] border-l overflow-y-auto" style={{ borderColor: 'var(--border-card, #E5E7EB)', boxShadow: '-8px 0 30px rgba(0,0,0,0.12)' }}>
 {/* Header */}
 <div className="sticky top-0 z-10 bg-[var(--bg-card,#fff)] border-b px-5 py-4 flex items-center justify-between" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <div className="flex items-center gap-2 min-w-0 flex-1">
 <CheckSquare className="w-4 h-4 text-[#338edc] flex-shrink-0" />
 <h2 className="text-base font-bold truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
 {loading ? '...' : detail?.title || t('cases.taskDetail', 'Aufgabe')}
 </h2>
 </div>
 <button onClick={onClose} className="p-1.5 rounded-lg text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
 <X className="w-5 h-5" />
 </button>
 </div>

 {loading ? (
 <div className="flex items-center justify-center h-40">
 <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
 </div>
 ) : error ? (
 <div className="p-5">
 <div className="flex items-center gap-2 p-4 rounded-lg border border-red-200 bg-red-50">
 <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
 <span className="text-sm text-red-700">{error}</span>
 </div>
 </div>
 ) : detail ? (
 <div className="p-5 space-y-5">
 {/* Status + Priority Badges */}
 <div className="flex items-center gap-2 flex-wrap">
 <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${isOverdue ? 'bg-red-100 text-red-700' : (statusColors[detail.status] || statusColors.offen)}`}>
 {isOverdue ? t('cases.ltOverdue', 'Überfällig') : (statusLabels[detail.status] || detail.status)}
 </span>
 {detail.priority && detail.priority !== 'normal' && (
 <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${detail.priority === 'urgent' ? 'bg-red-100 text-red-700' : detail.priority === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)]'}`}>
 {prioLabels[detail.priority] || detail.priority}
 </span>
 )}
 {detail.source && detail.source !== 'manual' && (
 <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-[var(--bg-inset,#F3F4F6)] text-[var(--text-tertiary,#6B7280)]">
 {detail.source}
 </span>
 )}
 </div>

 {/* Description */}
 {detail.description && (
 <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {detail.description}
 </p>
 )}

 {/* Meta Grid */}
 <div className="grid grid-cols-2 gap-3">
 {dueDate && !isNaN(dueDate.getTime()) && (
 <div>
 <p className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{t('cases.taskDueDate', 'Fällig am')}</p>
 <p className={`text-sm font-medium ${isOverdue ? 'text-red-500' : ''}`} style={{ color: isOverdue ? undefined : 'var(--text-primary, #1A1A2E)' }}>
 {dueDate.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
 </p>
 </div>
 )}
 {detail.estimated_minutes && (
 <div>
 <p className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{t('cases.taskDuration', 'Dauer')}</p>
 <p className="text-sm font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{detail.estimated_minutes} min</p>
 </div>
 )}
 </div>

 {/* Creator + Assignee */}
 <div className="flex items-center gap-5">
 {detail.creator_name && (
 <div className="flex items-center gap-2">
 <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg, #338edc, #6366F1)' }}>
 {initials(detail.creator_name)}
 </div>
 <div>
 <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{t('cases.taskCreator', 'Ersteller')}</p>
 <p className="text-xs font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{detail.creator_name}</p>
 </div>
 </div>
 )}
 {detail.assigned_name && (
 <div className="flex items-center gap-2">
 <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg, #338edc, #6366F1)' }}>
 {initials(detail.assigned_name)}
 </div>
 <div>
 <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{t('cases.taskAssignee', 'Bearbeiter')}</p>
 <p className="text-xs font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{detail.assigned_name}</p>
 </div>
 </div>
 )}
 </div>

 {/* Entity Link */}
 {detail.entity_type && detail.entity_id && (
 <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-inset, #F8F9FC)' }}>
 <LinkIcon className="w-4 h-4 shrink-0" style={{ color: '#338edc' }} />
 <span className="text-sm flex-1 truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
 {detail.entity_label || `${detail.entity_type} #${detail.entity_id}`}
 </span>
 {detail.entity_type === 'case' && (
 <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0" style={{ background: '#FEE2E2', color: '#DC2626' }}>CASE</span>
 )}
 </div>
 )}

 {/* Action Button — mark done / reopen (only for own tasks or admin) */}
 <div className="flex items-center gap-2">
 {!isDone ? (
 <button onClick={() => handleStatusChange('erledigt')}
 className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ background: '#22C55E' }}>
 <CheckSquare className="w-3.5 h-3.5" />
 {t('cases.taskMarkDone', 'Erledigt')}
 </button>
 ) : (
 <button onClick={() => handleStatusChange('offen')}
 className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border"
 style={{ borderColor: 'var(--border-default, #D1D5DB)', color: 'var(--text-secondary, #4B5563)' }}>
 {t('cases.taskReopen', 'Wieder öffnen')}
 </button>
 )}
 </div>

 {/* Mini-Chatter */}
 <div className="border-t pt-4" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <UniversalChatter entityType="task" entityId={detail.id} compact />
 </div>
 </div>
 ) : null}
 </div>
 </>
 );
}

// Sidebar badge helper
export async function getCasesResponseRequiredCount() {
 try {
 const [sr, cr, mc] = await Promise.all([
 fetch(`${API}/api/cases/stages`, { credentials:'include' }).then(r=>r.json()),
 fetch(`${API}/api/cases`, { credentials:'include' }).then(r=>r.json()),
 fetch(`${API}/api/cases/my-count`, { credentials:'include' }).then(r=>r.json()).catch(() => ({ count: 0, vertretung_count: 0 })),
 ]);
 const rrCount = (cr.tasks||[]).filter(t => t.stage_key==='waiting_for_support').length;
 const vCount = mc.vertretung_count || 0;
 return rrCount + vCount;
 } catch { return 0; }
}
